import { CREATIVE_FORMATS } from '@digital-twins/shared-types';
import type {CreativeContextSelection,CreativeFormatId, QuickStudyCommand, QuickStudyFindingsMessage} from '@digital-twins/shared-types';
import { projectAssistantActivity } from '@digital-twins/shared-types/assistant-activity';
import { ASSISTANT_STREAM_EVENT_TYPES } from "@digital-twins/shared-types/assistant";
import type {
  AssistantRequestMode, AssistantIntakeRequest,
  AssistantDoneEvent, AssistantStreamEvent, AssistantPlanStep,
  AssistantPreparedStudy, AssistantStudySuggestionSpec,
  AssistantEvidenceDoneFields, AssistantEvidenceItem, AssistantToolCall,
  AssistantPlanStepKind, AssistantPlanStepFlag, AssistantPlanStepInputs,
  AssistantPlanTail as AssistantTurnPlanTail,
  AssistantImageProgressEvent,
  AssistantStreamEventType,
  AssistantReportTail,
  TurnSuggestions,
  AssistantTurnSnapshot,
  AssistantPlanCursorBinding,
  PreparedConversation,
} from "@digital-twins/shared-types/assistant";
import type { AssistantThreadCredits, SocialCitationPost } from "@digital-twins/shared-types";
import type {
  AssistantDoneContextUsage,
  AssistantDoneFrame,
  AssistantDoneReport,
} from "@digital-twins/shared-types/assistant-done.generated";
export type {
  AssistantPlanStep, AssistantPlanStepKind, AssistantPlanStepFlag,
  AssistantPlanStepInputs, AssistantTurnIntakeTail,
  AssistantPlanTail as AssistantTurnPlanTail,
  AssistantReportTail as AssistantTurnReportTail,
} from "@digital-twins/shared-types/assistant";

import axios from "axios";
import type { KnowledgeDatasetPreview, KnowledgeDatasetPreviewQuery } from '@digital-twins/shared-types/knowledge-dataset-preview';
import {
  CreateLibraryCollectionBody,
  LibraryCandidatesQuery,
  LibraryCollectionCardsPage,
  LibraryCollectionDetail,
  LibraryCollectionKpis,
  LibraryCollectionMemberBody,
  LibraryCollectionPreviewBody,
  LibraryCollectionPreviewResponse,
  LibraryCollectionResourcesPage,
  LibraryCollectionTopicsResponse,
  LibraryItemCollectionsResponse,
  LibraryItemKind,
  LibraryKpiWindow,
  LibraryResourceKind,
  LibrarySearchGroup,
  LibrarySearchGroupPage,
  LibrarySearchResponse,
  UpdateLibraryCollectionBody,
  SocialPostsByUrlResponse,
} from "@digital-twins/shared-types";
import i18n, { resolveUiLanguage } from "@/i18n";
import { nanoid } from "nanoid";
import type {
  ChatRoomSenderType,
  AnalystChartSpec,
  AnalystRunResponse,
  AnalystSessionResponse,
  AnalystTableSpec,
  AssistantCreativeDraft,
  AudienceTag,
  AudienceTagCondition,
  BrandRoleGraph,
  BrandSettings,
  BrandThemeConfig,
  ContextMaterialStage,
  KnowledgeAskCitation,
  KnowledgeAskResponse,
  KnowledgeBulkUploadResponse,
  KnowledgeDocumentComment,
  KnowledgeFacetsResponse,
  KnowledgeLinkIngestResponse,
  KnowledgeSearchResponse,
  KnowledgeDocumentListItem,
  SocialPostsResponse,
  SocialPostsSort,
  SocialSyncRunsResponse,
  SocialTrackerInput,
  SocialTrackerItem,
  SocialTrackerProposal,
  SocialTrackerSuggestionRequest,
  SocialTrackersResponse,
  SocialTrackerUpdate,
  SocialAdminWalletUsageSummary,
  SocialEstimateRequest,
  SocialEstimateResponse,
  SocialWalletUsageSummary,
  SocialTopicAuthorsQuery,
  SocialTopicAuthorsResponse,
  SocialTopicCountsQuery,
  SocialTopicCountsResponse,
  SocialTopicFilters,
  SocialTopicPostsQuery,
  SocialTopicSeriesQuery,
  SocialTopicSeriesResponse,
  SocialUniverseInput,
  SocialUniverseItem,
  SocialPostCommentsResponse,
  SocialUniversePostsResponse,
  SocialRunUnlockResponse,
  SocialUniverseRunsResponse,
  SocialUniverseSuggestion,
  SocialUniverseSuggestionRequest,
  SocialSourceCheckRequest,
  SocialSourceCheckResponse,
  SocialUniverseUpdate,
  SocialUniversesResponse,
  SocialWindow,
  InsightScope,
  Skill,
  SkillSummary,
  AssetMediaRef,
  ThreadArtifactDto,
  ThreadArtifactListResponse,
  SubmitAnalystMessageRequest,
  SubmitAnalystMessageResponse,
  ThemeFontId,
  ThemeHeadingFontId,
  ThemePresetId,
  KnowledgeAskSourceScope,
} from "@digital-twins/shared-types";
import type { SegmentationMapping } from "@/types/components/audiences/segmentation-mapping";
import type { StudiesLobbyResponse } from "@/types/routes/tests";
import type {
  AudienceExternalInvite,
  AudienceOwnershipUpdateParams,
} from "@/types/components/audiences/audience-sharing";
import type {
  AudienceImportContextMaterialsProjection,
  AudienceImportPreviewProjection,
  AudienceCreationFlowSnapshot,
  AudienceImportRouteAck,
  AudienceImportSession,
  AudienceImportStatusProjection,
  AudienceImportWorkbookProjection,
  ContextMaterialsFilesResponse,
  ContextMaterialsFilesListResponse,
  IdentityFieldPatchRequest,
  IdentityFieldPatchResponse,
  IdentitySourceOptions,
  IdentityStepShell,
  IdentityValueMapWorkspace,
  ImportSessionPatch,
  ImportSessionSheetAssignmentPatch,
  PopulationContextMaterialsFilesResponse,
} from "@/types/components/audiences/import-session";

import { getApiBaseUrl } from "@/lib/runtime-env";
import {
  BoundaryParseResult,
  createBoundaryParser,
} from "@/lib/validation/schema-loader";
import {
  DeckStatus,
  ReportStatus,
  ReportReadinessView,
  ReportSectionRetryRequest,
  ResolvedStudyReel,
} from "@digital-twins/shared-types/reporting";
import reportingContractsSchema from "../../../../../packages/shared-types/schemas/reporting/contracts.schema.json";
import { SOCIAL_TOPIC_HIDDEN_ONLY } from "@digital-twins/shared-types";
import { handleUnauthorized } from "./sessionExpiry";
import {
  notifyGuestSessionExpired,
  type GuestSessionTab,
} from "@/lib/utils/guest-session-events";

/** Backend returns 409 while identity value-map projection is still hydrating. */
function isIdentityWorkspaceHydrating409(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const ax = error as {
    response?: { status?: number; data?: { message?: unknown } };
  };
  if (ax.response?.status !== 409) return false;
  const msg = ax.response.data?.message;
  const text = Array.isArray(msg) ? msg.join(" ") : String(msg ?? "");
  const lower = text.toLowerCase();
  if (lower.includes("expired")) return false;
  // Matches: "Identity workspace is still hydrating. Try again in a moment."
  return (
    lower.includes("still hydrating") ||
    (lower.includes("hydrat") && lower.includes("identity"))
  );
}

async function retryIdentityWorkspaceHydrationRequest<T>(args: {
  request: () => Promise<T>;
  signal?: AbortSignal;
}): Promise<T> {
  const maxAttempts = 12;
  const baseDelayMs = 350;
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await args.request();
    } catch (error) {
      lastError = error;
      if (args.signal?.aborted) throw error;
      if (
        !isIdentityWorkspaceHydrating409(error) ||
        attempt === maxAttempts - 1
      ) {
        throw error;
      }
      const delay = Math.min(10_000, baseDelayMs * 1.45 ** attempt);
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(resolve, delay);
        args.signal?.addEventListener(
          "abort",
          () => {
            clearTimeout(timeoutId);
            reject(error);
          },
          { once: true },
        );
      });
    }
  }

  throw lastError;
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  config.headers["x-trace"] = nanoid();
  return config;
});

api.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  const method = (config.method ?? "get").toLowerCase();
  if (method === "get" || method === "head" || method === "options") {
    // Default axios Content-Type: application/json on GET forces a CORS
    // preflight. x-trace already triggers OPTIONS; don't add a second
    // non-simple header on reads.
    delete config.headers["Content-Type"];
    delete config.headers["content-type"];
  }

  // Prefer K8s runtime-env.js over Vite bake-in so one image works across envs.
  config.baseURL = getApiBaseUrl();

  if (
    config.url &&
    !config.url.startsWith("http") &&
    !config.url.startsWith("/")
  ) {
    config.url = "/" + config.url;
  }

  return config;
});

// A 401 is the one session-expiry rule (B8): this client's pages leave for
// sign-in and come back to where they were.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    handleUnauthorized(error, { redirect: true });
    return Promise.reject(error);
  },
);

export default api;

export { authApi } from "./auth.api";

// Users API
export interface CreditInfo {
  balance: number;
  creditLimit: number;
  overageCredits: number;
  overageUsed: number;
  overageLimitCents: number | null;
  overageRateCentsPerCredit: number;
  isInOverage: boolean;
  allowOverage: boolean;
  used: number;
  pending: number;
  available: number;
  isOrgOwner: boolean;
  /** True when the org uses a shared pool (Enterprise). Balance/creditLimit reflect the org-level pool. */
  isPoolBilling: boolean;
  /** Credit metering enrollment of the org. Off by default; set from the backoffice. */
  meteringMode?: 'off' | 'shadow' | 'on';
  /** Cents one credit is worth on this org's billing surfaces (backoffice-set, default 50). */
  creditDisplayValueCents: number;
}

export type UsageKind = "audience" | "study" | "chat" | "system" | "social";

export interface CreditTransaction {
  id: string;
  performedByUserId?: string | null;
  /** When set, the org member who triggered the charge (name or email). */
  performedByDisplayName?: string | null;
  amount: number;
  type: "debit" | "credit";
  description: string;
  /** Categorization for the "Type" column (audience / study / chat / system). */
  kind: UsageKind;
  objectType?: string | null;
  objectId?: string | null;
  /** Present only for bonus_credit_awarded events. */
  bonusSource?: string | null;
  balanceAfter: number;
  createdAt: string;
}

export interface CreditHistoryPage {
  items: CreditTransaction[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Daily usage log (`GET /users/me/credit-usage/daily`), mirrors
 * `apps/backend/src/billing/daily-usage.ts`. Money: `accruedMillicredits`
 * is exact usage (refunds netted into their source), `postedCredits` the
 * whole credits debited; the month waiver lives in `months`, never in a row.
 * Accruals use UTC months, so a local-day row can span two months.
 */
export type DailyUsageKind = "thread" | "room" | "topic" | "quick_study" | "study" | "workflow" | "other";
export type DailyUsageChildType =
  | "assistant"
  | "workflow"
  | "image"
  | "quick_study"
  | "study"
  | "room_message"
  | "social"
  | "audience"
  | "chat"
  | "other";
export type DailyUsageState =
  | "posted"
  | "unpaid"
  | "refunded"
  | "partially_refunded"
  | "refund";

export interface DailyUsageRefund {
  token: string;
  occurredAt: string;
  accruedMillicredits: number;
  postedCredits: number;
}

export interface DailyUsageChild {
  type: DailyUsageChildType;
  token: string;
  publicId: string | null;
  occurredAt: string;
  month: string;
  accruedMillicredits: number;
  postedCredits: number;
  state: DailyUsageState;
  policyVersion: string | null;
  videoMultiplier: number | null;
  newVideo: boolean;
  level: string | null;
  tokens: {
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
  } | null;
  creditRateCents: number;
  refunds: DailyUsageRefund[];
  /** A workflow run's link line under the reply that launched it: zero amounts, publicId = run token. */
  link?: boolean;
  /** Name of a charge on another object than its row's (a launched Quick Study, study, room, workflow). */
  title?: string | null;
}

export interface DailyUsageRow {
  date: string;
  userToken: string | null;
  kind: DailyUsageKind;
  publicId: string | null;
  objectType: string | null;
  title: string | null;
  /** A study row whose study no longer exists. */
  deleted?: boolean;
  accruedMillicredits: number;
  postedCredits: number;
  state: Exclude<DailyUsageState, "refund">;
  children: DailyUsageChild[];
}

export interface DailyUsageTotals {
  accruedMillicredits: number;
  postedCredits: number;
  unpaidMillicredits: number;
  refundedMillicredits: number;
  rows: number;
}

export interface DailyUsageMonth {
  month: string;
  wallet: "pool" | "seat";
  open: boolean;
  accruedMillicredits: number;
  postedCredits: number;
  unpaidMillicredits: number;
  waivedMillicredits: number;
}

export interface DailyUsageTeamMember {
  userToken: string;
  displayName: string | null;
  accruedMillicredits: number;
  postedCredits: number;
  rows: number;
}

export interface DailyUsagePage {
  from: string;
  to: string;
  timeZone: string;
  scope: "me" | "team";
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  rows: DailyUsageRow[];
  totals: DailyUsageTotals;
  months: DailyUsageMonth[];
  team: { totals: DailyUsageTotals; members: DailyUsageTeamMember[] } | null;
  creditRateCents: number;
}

export interface DailyUsageParams {
  /** Local days `YYYY-MM-DD`, inclusive. Default: the last 30 days. */
  from?: string;
  to?: string;
  /** IANA time zone; defaults to the browser's. */
  tz?: string;
  page?: number;
  pageSize?: number;
  /** `team` needs an org owner or admin. */
  scope?: "me" | "team";
}

/**
 * Canonical subscription tiers — must match the `subscription_tier` pgEnum in
 * `apps/backend/src/db/schema/users.ts` and the `SubscriptionTier` enum in
 * `apps/backend/src/billing/billing-config.service.ts`. Keep all three in sync.
 */
export type SubscriptionTier =
  | "trial"
  | "partner_trial"
  | "vip_trial"
  | "pro_trial"
  | "starter"
  | "pro"
  | "enterprise"
  | "fairgen_internal"
  | "locked_free";

export interface FeatureFlags {
  createStudy: boolean;
  createAudience: boolean;
  createKnowledge: boolean;
  /** Intelligent Segmentation Variable Mapping — gates wizard step + details editor. */
  intelligentMapping?: boolean;
  /** Enables publishing audiences to the marketplace. */
  marketplacePublish?: boolean;
  /** Enables access to marketplace audiences. Defaults on; organizations can opt out. */
  marketplaceAccess?: boolean;
  /** Route audience creation through Argo Workflows instead of BullMQ. */
  argoAudienceCreation?: boolean;
  /** White-label branding and theme customization. */
  whiteLabel?: boolean;
  /** Deck-scope branding (Pro): only deliverables adopt the brand. */
  brandedTheme?: boolean;
  /**
   * Org kill switch for twin chat. Missing means on.
   * Consumed via `useTwinChatEnabled`.
   */
  chatRoomsEnabled?: boolean;
  /**
   * Slack Twins org flag. Off by default; missing means off.
   * Consumed via `useSlackTwinsEnabled` (which also needs the Slack env configured).
   */
  slackTwins?: boolean;
  /** Paid-first onboarding gate (RND-2610). True while awaiting first payment; cleared by webhook. */
  paidFirstOnboarding?: boolean;
  /**
   * Assistant Platform v1 (docs/plans/assistant-platform-v1-execution-plan.md).
   * Server-enforced gate (`FeatureFlag.ASSISTANT_PLATFORM` +
   * `assertAssistantPlatformEnabled`) lands at Step 8.1; this is the frontend
   * read of the same key, consumed via `useAssistantPlatformEnabled`.
   */
  assistantPlatform?: boolean;
  /**
   * Skills customization (docs/plans/skills-org-personal-overrides.md).
   * Server-enforced with ASSISTANT_PLATFORM; this is the frontend read of
   * `FeatureFlag.SKILLS_CUSTOMIZATION`, consumed via
   * `useSkillsCustomizationEnabled`.
   */
  skillsCustomization?: boolean;
  /** Conversation model picker (Luna / Haiku); server-resolved, internal tier layered in. */
  conversationModel?: boolean;
  /**
   * Internal conversation options (`CONVERSATION_MODEL_INTERNAL_OPTIONS`) in the advanced
   * picker. Server-resolved: verified Fairgen staff only, never an org flag.
   */
  conversationModelInternal?: boolean;
  /** Social listening org flag. Off by default; missing means off. */
  socialListening?: boolean;
  /** Connected sources org flag. Off by default; missing means off. */
  agentHandler?: boolean;
  /** Gated web search in twin conversations, org flag. Off by default; missing means off. */
  conversationWebSearch?: boolean;
}

export interface UserLimits {
  maxAudiences: number;
  maxStorages: number;
  currentAudiences: number;
  currentStorages: number;
}

export type OrganizationType = "personal" | "agency" | "general" | "internal";
export type OrgRole = "owner" | "admin" | "member";

export interface Organization {
  id: string;
  name: string;
  type: OrganizationType;
  orgRole: OrgRole;
}

export interface MemberUsage {
  userId: string;
  name: string;
  email: string;
  orgRole: OrgRole;
  creditLimit: number;
  balance: number;
  used: number;
  overageCredits: number;
  overageUsed: number;
  overageLimitCents: number | null;
  /** True for Enterprise pool-billing orgs. creditLimit reflects shared org pool, not a per-user budget. */
  isPoolBilling?: boolean;
}

export interface OrgMember {
  id: string;
  email: string;
  name: string | null;
  orgRole: OrgRole;
  createdAt: string;
}

export type InviteDisplayStatus = "pending" | "expired" | "revoked";

export interface OrgInvite {
  id: number;
  email: string;
  role: string;
  status: string;
  displayStatus: InviteDisplayStatus;
  invitedBy: string | null;
  expiresAt: string;
  createdAt: string;
}

export const organizationsApi = {
  getCurrent: async (): Promise<Organization | null> => {
    const response = await api.get("/organizations/current");
    return response.data;
  },
  getMembers: async (orgId: string): Promise<OrgMember[]> => {
    const response = await api.get(`/organizations/${orgId}/members`);
    return response.data;
  },
  update: async (
    id: string,
    data: { name?: string; type?: OrganizationType },
  ): Promise<Organization> => {
    const response = await api.put(`/organizations/${id}`, data);
    return response.data;
  },
  listInvites: async (orgId: string): Promise<Array<OrgInvite>> => {
    const response = await api.get(`/organizations/${orgId}/invites`);
    return response.data;
  },
  createInvite: async (
    orgId: string,
    email: string,
    role: string = "member",
  ): Promise<OrgInvite & { invitationEmailSent: boolean }> => {
    const response = await api.post(`/organizations/${orgId}/invites`, {
      email,
      role,
    });
    return response.data;
  },
  resendInvite: async (orgId: string, inviteId: number): Promise<OrgInvite> => {
    const response = await api.post(
      `/organizations/${orgId}/invites/${inviteId}/resend`,
    );
    return response.data;
  },
  revokeInvite: async (
    orgId: string,
    inviteId: number,
  ): Promise<{ success: boolean }> => {
    const response = await api.post(
      `/organizations/${orgId}/invites/${inviteId}/revoke`,
    );
    return response.data;
  },
  removeMember: async (
    orgId: string,
    userId: string,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete(
      `/organizations/${orgId}/members/${userId}`,
    );
    return response.data;
  },
  getMemberUsage: async (id: string): Promise<Array<MemberUsage>> => {
    const response = await api.get(`/organizations/${id}/member-usage`);
    return response.data;
  },
  updateOverageSettings: async (
    orgId: string,
    data: { allowOverage?: boolean; defaultOverageLimitCents?: number | null },
  ): Promise<{
    allowOverage: boolean;
    defaultOverageLimitCents: number | null;
  }> => {
    const response = await api.patch(
      `/organizations/${orgId}/billing/overage`,
      data,
    );
    return response.data;
  },
  updateMemberOverageLimit: async (
    orgId: string,
    userId: string,
    overageLimitCents: number | null,
  ): Promise<{ userId: string; overageLimitCents: number | null }> => {
    const response = await api.patch(
      `/organizations/${orgId}/members/${userId}/overage-limit`,
      { overageLimitCents },
    );
    return response.data;
  },
};

export type BillingInterval = "monthly" | "annual";

export interface OrgUsageSummaryRow {
  userId: number;
  usageType: string;
  totalAmount: number;
  includedAmount: number;
  payAsYouGoAmount: number;
}

export interface PayAsYouGoEvent {
  id: number;
  organizationId: number;
  userId: number;
  billingAccountId: number;
  usageType: string;
  usageAmount: string | number;
  includedInPlan: boolean;
  isPayAsYouGo: boolean;
  createdAt: string;
}

/**
 * Lemon Squeezy discount-code identifiers, fetched from the backend so the
 * env-specific (test vs live) code strings are never baked into the frontend
 * bundle. `null` means the corresponding env var is not configured —
 * callers should hide / disable the related promo CTA.
 */
export interface PromoCodes {
  trial3mo: string | null;
  starter1mo: string | null;
  pro1mo: string | null;
}

export const billingApi = {
  createCheckout: async (data: {
    plan: "starter" | "pro" | "enterprise";
    interval: BillingInterval;
    discount?: string;
  }): Promise<{ checkoutUrl: string }> => {
    const response = await api.post("/billing/checkout", data);
    return response.data;
  },
  getPortalUrl: async (): Promise<{ portalUrl: string }> => {
    const response = await api.get("/billing/portal");
    return response.data;
  },
  switchPlan: async (data: {
    plan: "starter" | "pro" | "enterprise";
    interval: BillingInterval;
  }): Promise<{ success: boolean; checkoutUrl?: string }> => {
    const response = await api.post("/billing/switch-plan", data);
    return response.data;
  },
  /**
   * Fetch the LS discount-code map for the current environment. Cache
   * aggressively (codes only change on a backend redeploy).
   */
  getPromoCodes: async (): Promise<PromoCodes> => {
    const response = await api.get("/billing/promo-codes");
    return response.data;
  },
  /** Per-member, per-usage-type totals for current billing cycle. */
  getOrgUsageSummary: async (orgId: string): Promise<OrgUsageSummaryRow[]> => {
    const response = await api.get(`/usage/${orgId}/summary`);
    return response.data;
  },
  /** All pay-as-you-go usage events in the current billing cycle. */
  getPayAsYouGoEvents: async (orgId: string): Promise<PayAsYouGoEvent[]> => {
    const response = await api.get(`/usage/${orgId}/payg`);
    return response.data;
  },
};

export interface UserProfile {
  id: string;
  email: string;
  name?: string;
  credits: number;
  isActive: boolean;
  isAdmin: boolean;
  isPlatformAdmin?: boolean;
  /** Fairgen staff flag — grants access to deep audience-inspector tooling (RND-2638). */
  isFairgenInspector?: boolean;
  emailVerified?: boolean;
  organizationToken?: string;
  organizationName?: string | null;
  orgRole?: OrgRole;
  hasCompletedOnboarding?: boolean;
  onboardingStep?: number;
  companyName?: string;
  role?: string;
  brandPrimaryColor?: string | null;
  brandSecondaryColor?: string | null;
  brandThemeConfig?: BrandThemeConfig | null;
  brandLogoUrl?: string | null;
  brandLogoUrlOnDark?: string | null;
  /** Server-measured logo inversion flags; null means not measured (treat as invert). */
  brandLogoNeedsInversion?: boolean | null;
  brandLogoOnDarkNeedsInversion?: boolean | null;
  brandIconLogoUrl?: string | null;
  brandDomain?: string | null;
  avatarSeed?: string | null;
  brandRoleGraph?: BrandRoleGraph | null;
  brandRoleGraphVersion?: string | null;
  brandRoleGraphHash?: string | null;
  brandRevision?: number;
  themePresetId?: string | null;
  brandFontFamily?: string | null;
  brandHeadingFontFamily?: string | null;
  themeOverrides?: Record<string, string> | null;
  shareFooterText?: string | null;
  featuredTwins?: FeaturedTwin[] | null;
  /** ISO timestamp when trial expires; present for trial-tier accounts. */
  trialEndsAt?: string | null;
  /** Set to true when org exceeds plan limits (e.g. seats > maxSeats after downgrade). */
  planViolation?: boolean;
  /** Whether the org has an active Lemon Squeezy subscription (determines checkout vs. portal CTA). */
  hasActiveSubscription?: boolean;
  /** Current billing interval for subscribed orgs. */
  billingInterval?: BillingInterval | null;
  /** ISO timestamp when the current billing cycle renews. */
  renewsAt?: string | null;
  /** ISO timestamp when a cancelled subscription's access ends. */
  endsAt?: string | null;
  /** Lemon Squeezy subscription status (active, past_due, cancelled, etc.). */
  lsStatus?: string | null;
  createdAt: string;
  updatedAt: string;
  creditInfo: CreditInfo;
  featureFlags: FeatureFlags;
  /**
   * Goal ids this org cannot generate new studies for (global OR org close).
   * Existing studies stay openable. Empty when every type is open.
   */
  closedStudyTypes?: Array<string>;
  subscriptionTier: SubscriptionTier;
  pendingTier: SubscriptionTier | null;
  daysUntilReset: number;
  limits?: UserLimits;
}

// Brand types moved to @digital-twins/shared-types for shared usage across frontend and deck-ui.
// Re-exported here for back-compat with existing API client consumers.
export type { BrandSettings } from "@digital-twins/shared-types";
export type { BrandRoleGraph };

export interface BrandLookupResult {
  name: string;
  domain: string;
  primaryColor: string | null;
  secondaryColor?: string | null;
  logoUrl: string | null;
  logoUrlOnDark?: string | null;
  iconLogoUrl: string | null;
  qualityScore?: number | null;
  lowConfidence?: boolean;
  claimed?: boolean | null;
  derivedPalette?: {
    accent: string | null;
    card: string | null;
    deep: string | null;
    accentSource: string;
    cardSource: string;
    deepSource: string;
  };
  colors: { hex: string; type: string; brightness?: number | null }[];
  logos: {
    type: string;
    theme: string;
    formats: { format: string; src: string }[];
  }[];
}

export interface BrandDetectionDecision {
  key: string;
  label: string;
  value: string | null;
  source: string;
  confidence: number;
  reason: string;
}

export interface BrandDetectionDiagnostics {
  isMonochrome: boolean;
  brandfetchColorCount: number;
  crawlerCandidateCounts: {
    background: number;
    primaryAction: number;
    cardSurface: number;
    navBackground: number;
    metaThemeColors: number;
  };
  decisions: Array<BrandDetectionDecision>;
}

export interface BrandLookupEnrichedResult extends BrandLookupResult {
  website: {
    finalUrl: string;
    fontFamilies: string[];
    metaThemeColors: Array<{
      value: string;
      source: string;
      role: string;
      confidence: number;
    }>;
    candidates: Record<
      string,
      Array<{
        value: string;
        source: string;
        role: string;
        confidence: number;
      }>
    >;
  } | null;
  suggestedBrandThemeConfig?: BrandThemeConfig | null;
  brandDetectionDiagnostics?: BrandDetectionDiagnostics | null;
  suggestedTheme: {
    themePresetId: ThemePresetId | null;
    themeOverrides: Record<string, string>;
    brandFontFamily: ThemeFontId | null;
    brandHeadingFontFamily: ThemeHeadingFontId | null;
    detectedFontFamily: string | null;
    suggestedDarkMode: boolean;
    confidence: number;
  };
}

export interface OnboardingState {
  hasCompletedOnboarding: boolean;
  onboardingStep: number;
  companyName?: string;
  role?: string;
}

export interface TargetingPreferences {
  targetingType: "B2B" | "B2C" | "BOTH" | null;
  targetingPreferences: {
    roleFamilies?: string[];
    lifestyleSegments?: string[];
    race?: string[];
    ageRange?: { min: number; max: number };
    annualIncomeRange?: { min: number; max: number };
    focusMarkets?: string[];
    industryTokens?: string[];
    industryAncestorSourceIds?: string[];
    jtbd?: string[];
  } | null;
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export const usersApi = {
  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get("/users/me", {
      params: {
        _ts: Date.now(),
      },
    });
    return response.data;
  },
  updateProfile: async (data: {
    name?: string;
    avatarSeed?: string;
    companyName?: string | null;
  }): Promise<UserProfile> => {
    const response = await api.put("/users/me", data);
    return response.data;
  },
  getCreditInfo: async (): Promise<CreditInfo> => {
    const response = await api.get("/users/me/credits");
    return response.data;
  },
  getCreditHistory: async (
    params: { limit?: number; offset?: number } = {},
  ): Promise<CreditHistoryPage> => {
    const response = await api.get("/users/me/credits/history", {
      params: {
        limit: params.limit ?? 25,
        offset: params.offset ?? 0,
      },
    });
    return response.data;
  },
  getDailyCreditUsage: async (
    params: DailyUsageParams = {},
  ): Promise<DailyUsagePage> => {
    const response = await api.get("/users/me/credit-usage/daily", {
      params: { ...params, tz: params.tz ?? browserTimeZone() },
    });
    return response.data;
  },
  /** The whole range as CSV text (no pagination). */
  getDailyCreditUsageCsv: async (
    params: Omit<DailyUsageParams, "page" | "pageSize"> = {},
  ): Promise<string> => {
    const response = await api.get("/users/me/credit-usage/daily", {
      params: { ...params, tz: params.tz ?? browserTimeZone(), format: "csv" },
      responseType: "text",
    });
    return response.data;
  },
  getBonusCredits: async (): Promise<{ balance: number }> => {
    const response = await api.get("/users/me/credits/bonus");
    return response.data;
  },
  getReferralInfo: async (): Promise<{
    referralCode: string | null;
    referralLink: string | null;
  }> => {
    const response = await api.get("/users/me/referral");
    return response.data;
  },
  getOnboardingState: async (): Promise<OnboardingState> => {
    const response = await api.get("/users/me/onboarding");
    return response.data;
  },
  updateOnboardingState: async (
    data: Partial<OnboardingState>,
  ): Promise<OnboardingState> => {
    const response = await api.put("/users/me/onboarding", data);
    return response.data;
  },
  getBrandSettings: async (): Promise<BrandSettings> => {
    const response = await api.get("/users/me/brand");
    return response.data;
  },
  updateBrandSettings: async (
    data: Partial<BrandSettings>,
  ): Promise<BrandSettings> => {
    const response = await api.put("/users/me/brand", data);
    return response.data;
  },
  lookupBrand: async (domain: string): Promise<BrandLookupResult> => {
    const response = await api.post("/users/me/brand/lookup", { domain });
    return response.data;
  },
  lookupBrandEnriched: async (
    domain: string,
  ): Promise<BrandLookupEnrichedResult> => {
    const response = await api.post("/users/me/brand/lookup-enriched", {
      domain,
    });
    return response.data;
  },
  applyPromoCode: async (
    promoCode: string,
  ): Promise<{
    success: boolean;
    subscriptionTier: SubscriptionTier;
    pendingTier: SubscriptionTier | null;
    credits: number;
    featureFlags: FeatureFlags;
    daysUntilReset: number;
    isScheduled: boolean;
    message: string;
  }> => {
    const response = await api.post("/users/me/promo-code", { promoCode });
    return response.data;
  },
  getTargetingPreferences: async (): Promise<TargetingPreferences> => {
    const response = await api.get("/users/me/targeting");
    return response.data;
  },
  updateTargetingPreferences: async (
    data: Partial<TargetingPreferences>,
  ): Promise<TargetingPreferences> => {
    const response = await api.put("/users/me/targeting", data);
    return response.data;
  },
  updateFeaturedTwins: async (
    twins: FeaturedTwin[],
  ): Promise<FeaturedTwin[]> => {
    const response = await api.put("/users/me/featured-twins", {
      featuredTwins: twins,
    });
    return response.data;
  },
};

// Experiments API

/**
 * Submit persists the turn and returns pending/running. The python turn
 * continues in the background (up to 190s). This cap clears a hung POST so
 * the UI can retry or pick the run up from activeRuns.
 */
export const REPORT_ANALYST_SUBMIT_TIMEOUT_MS = 20_000;

export const reportAnalystApi = {
  loadOrCreateSession: async (
    experimentId: string,
    signal?: AbortSignal,
    timeoutMs?: number,
  ): Promise<AnalystSessionResponse> => {
    const response = await api.get(
      `/experiments/${experimentId}/report-analyst/session`,
      {
        signal,
        ...(timeoutMs === undefined ? {} : { timeout: timeoutMs }),
      },
    );
    return response.data;
  },
  submitMessage: async (
    experimentId: string,
    request: SubmitAnalystMessageRequest,
    signal?: AbortSignal,
  ): Promise<SubmitAnalystMessageResponse> => {
    const response = await api.post(
      `/experiments/${experimentId}/report-analyst/messages`,
      request,
      { signal, timeout: REPORT_ANALYST_SUBMIT_TIMEOUT_MS },
    );
    return response.data;
  },
  getRun: async (
    experimentId: string,
    runToken: string,
    signal?: AbortSignal,
  ): Promise<AnalystRunResponse> => {
    const response = await api.get(
      `/experiments/${experimentId}/report-analyst/runs/${runToken}`,
      { signal },
    );
    return response.data;
  },
  resetThread: async (
    experimentId: string,
    signal?: AbortSignal,
  ): Promise<AnalystSessionResponse> => {
    const response = await api.post(
      `/experiments/${experimentId}/report-analyst/reset`,
      undefined,
      { signal },
    );
    return response.data;
  },
};

export const experimentsApi = {
  list: async () => {
    const response = await api.get("/experiments");
    return response.data;
  },
  listAdmin: async () => {
    const response = await api.get("/experiments/admin/all");
    return response.data;
  },
  listSharedWithMe: async () => {
    const response = await api.get("/experiments/shared-with-me");
    return response.data;
  },
  listPaginated: async (page = 1, limit = 9) => {
    const response = await api.get("/experiments", { params: { page, limit } });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/experiments/${id}`);
    return response.data;
  },
  /**
   * CONSOLIDATED ENDPOINT: Get all data needed for results page in one request
   * Returns: experiment, results, population, storage - all in parallel on backend
   */
  getFull: async (
    id: string,
  ): Promise<{
    experiment: any;
    results: any;
    population: {
      id: string;
      name: string;
      analysisColumns: string[];
      /** Intelligent segmentation mappings when available for this audience. */
      segmentationMappings?: SegmentationMapping[] | null;
      /** Enriched recommended columns, either structured mappings or source-column ids. */
      recommendedAnalysisColumns?: SegmentationMapping[] | string[];
    } | null;
    storage: { id: string; name: string } | null;
    /**
     * The generated reader view. The route also sends private diagnostic
     * `requirements`, which no reader uses.
     */
    readiness: ReportReadinessView;
  }> => {
    const response = await api.get(`/experiments/${id}/full`);
    return response.data;
  },
  cancel: async (id: string) => {
    const response = await api.post(`/experiments/${id}/cancel`);
    return response.data;
  },
  rerun: async (id: string): Promise<{ id: string }> => {
    const response = await api.post(`/experiments/${id}/rerun`);
    return response.data;
  },
  duplicate: async (
    experimentId: string,
    payload: { title?: string } = {},
  ): Promise<{ draftId: string; status: string; sourcePopulationId?: string }> => {
    const response = await api.post(`/experiments/${experimentId}/duplicate`, payload);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/experiments/${id}`);
    return response.data;
  },
  getResults: async (id: string) => {
    const response = await api.get(`/experiments/${id}/results`);
    return response.data;
  },
  getSurveyJson: async (id: string) => {
    const response = await api.get(`/experiments/${id}/survey-json`);
    return response.data;
  },
  toggleGlobal: async (id: string) => {
    const response = await api.post(`/experiments/${id}/toggle-global`);
    return response.data;
  },
  /** No body: a full regeneration. A section retry request: only those unfinished sections. */
  regenerateReport: async (id: string, sectionRetry?: ReportSectionRetryRequest) => {
    const response = await api.post(
      `/experiments/${id}/generate-report`,
      sectionRetry,
    );
    return response.data;
  },
  clearResults: async (id: string): Promise<{ success: boolean }> => {
    const response = await api.post(`/experiments/${id}/clear-results`);
    return response.data;
  },
  getAudienceProfiles: async (
    id: string,
  ): Promise<{
    profiles: any;
    generatedAt?: string;
  }> => {
    const response = await api.get(`/experiments/${id}/audience-profiles`);
    return response.data;
  },
  saveAudienceProfiles: async (id: string, profiles: any): Promise<void> => {
    await api.post(`/experiments/${id}/audience-profiles`, { profiles });
  },
  findOneAdmin: async (id: string) => {
    const response = await api.get(`/experiments/admin/${id}`);
    return response.data;
  },
  patchAdmin: async (
    id: string,
    body: {
      op: "update_field" | "update_artifact" | "set_live" | "delete_artifact";
      field?: string;
      value?: unknown;
      artifactType?: string;
      versionNumber?: number;
    },
  ) => {
    const response = await api.patch(`/experiments/admin/${id}`, body);
    return response.data;
  },
  listChangesAdmin: async (id: string) => {
    const response = await api.get(`/experiments/admin/${id}/changes`);
    return response.data as {
      items: Array<{
        token: string;
        actorType: string;
        actorId: string;
        fieldPath: string;
        before: unknown;
        after: unknown;
        createdAt: string;
      }>;
    };
  },
  getArtifactAdmin: async (id: string, type: string) => {
    const response = await api.get(`/experiments/admin/${id}/artifacts/${type}`);
    return response.data as { artifactType: string; value: unknown };
  },
  listArtifactVersionsAdmin: async (id: string, type: string) => {
    const response = await api.get(
      `/experiments/admin/${id}/artifacts/${type}/versions`,
    );
    return response.data as {
      artifactType: string;
      liveGcsKey: string | null;
      versions: Array<{
        token: string;
        versionNumber: number;
        gcsKey: string;
        origin: string;
        createdAt: string;
        isLive: boolean;
      }>;
    };
  },
  setLiveArtifactAdmin: async (
    id: string,
    type: string,
    versionNumber: number,
  ) => {
    const response = await api.post(
      `/experiments/admin/${id}/artifacts/${type}/set-live`,
      { versionNumber },
    );
    return response.data;
  },
  revertArtifactAdmin: async (
    id: string,
    type: string,
    versionNumber: number,
  ) => {
    const response = await api.post(
      `/experiments/admin/${id}/artifacts/${type}/revert`,
      { versionNumber },
    );
    return response.data;
  },
  deleteArtifactAdmin: async (id: string, type: string) => {
    const response = await api.delete(
      `/experiments/admin/${id}/artifacts/${type}`,
    );
    return response.data;
  },
  deleteAdmin: async (id: string) => {
    const response = await api.delete(`/experiments/admin/${id}`);
    return response.data;
  },
  downloadSurveyAdmin: (id: string) => {
    window.open(
      `${getApiBaseUrl()}/experiments/admin/${id}/download-survey`,
      "_blank",
    );
  },
  downloadResultsAdmin: (id: string) => {
    window.open(
      `${getApiBaseUrl()}/experiments/admin/${id}/download-results`,
      "_blank",
    );
  },
  /** Fetch the full (unsanitized) results JSON for admin/backoffice inspection. */
  getResultsAdmin: async (id: string) => {
    const response = await api.get(`/experiments/admin/${id}/download-results`, {
      responseType: 'json',
    });
    return response.data;
  },
};

// Studies lobby API — unified launched studies + questionnaire drafts.
export const studiesLobbyApi = {
  listPaginated: async (
    offset = 0,
    limit = 9,
    search?: string,
  ): Promise<StudiesLobbyResponse> => {
    const response = await api.get("/studies/lobby", {
      params: { offset, limit, ...(search ? { search } : {}) },
    });
    return response.data;
  },
  /** The study's resolved reel; the backend answers 404 unless its reel is ready. */
  getReel: async (token: string, signal?: AbortSignal): Promise<ResolvedStudyReel> => {
    const response = await api.get(`/studies/${token}/reel`, { signal });
    return response.data;
  },
};

// Files API
export const filesApi = {
  upload: async (
    file: File,
    type: string,
    options?: string | { experimentId?: string; storageId?: string },
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    if (typeof options === "string") {
      formData.append("experimentId", options);
    } else if (options) {
      if (options.experimentId)
        formData.append("experimentId", options.experimentId);
      if (options.storageId) formData.append("storageId", options.storageId);
    }

    // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
    // The interceptor will add Authorization header and remove Content-Type
    const response = await api.post("/files/upload", formData);
    return response.data;
  },
  uploadBrandImage: async (
    file: File,
    variation: "light" | "dark" | "icon",
  ): Promise<{ token: string; publicUrl: string }> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("variation", variation);
    const response = await api.post("/files/upload/brand-image", formData);
    return response.data;
  },
  uploadQuestionnaireConceptImage: async (
    file: File,
  ): Promise<IssuedAssetFile> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      "/files/upload/questionnaire-concept-image",
      formData,
    );
    return response.data;
  },
  /**
   * Chat-only video hosting (rooms and 1:1): same storage as the study asset
   * route, and the backend asks for the video's seen-entity read. Studies keep
   * `uploadQuestionnaireConceptImage`.
   */
  uploadChatVideo: async (file: File): Promise<IssuedAssetFile> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/files/upload/chat-video", formData);
    return response.data;
  },
  getImageAsDataUrl: async (
    fileId: string,
  ): Promise<{
    dataUrl: string;
    mimeType: string;
    fileId: string;
    originalFilename: string;
  }> => {
    const response = await api.get(`/files/image/${fileId}`);
    return response.data;
  },
};

// Storages API
export const storagesApi = {
  create: async (name: string, description?: string) => {
    const response = await api.post("/storages", { name, description });
    return response.data;
  },
  list: async () => {
    const response = await api.get("/storages");
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/storages/${id}`);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/storages/${id}`);
    return response.data;
  },
  chat: async (
    id: string,
    message: string,
    conversationHistory?: Array<{ role: string; content: string }>,
  ) => {
    const response = await api.post(`/storages/${id}/chat`, {
      message,
      conversationHistory,
    });
    return response.data;
  },
};

export interface KnowledgeCollectionItem {
  token: string;
  name: string;
  description: string | null;
  documentTokens: Array<string>;
  createdAt: string;
  updatedAt: string;
}

/**
 * A knowledge document as served today, plus ingest flags that are not (yet)
 * part of the shared contract. `stalled` ships with the backend retry endpoint;
 * every consumer must treat it as optional so the UI degrades to "no failure"
 * against an older API.
 */
export interface KnowledgeDocumentWithStages extends KnowledgeDocumentListItem {
  /** The document never entered the ingest pipeline (nothing to retry from). */
  stalled?: boolean;
}

/**
 * Lobby example prompt (R3.6 / polish B1).
 * `promptId` is a publicId — never a DB row id.
 */
export interface KnowledgeExamplePrompt {
  promptId: string;
  text: string;
  audienceToken?: string;
}

/** Org-scoped generated lobby chips. */
export const knowledgeExamplePromptsApi = {
  list: async (language?: string): Promise<Array<KnowledgeExamplePrompt>> => {
    const trimmed = (language || '').trim();
    const response = await api.get('/knowledge/example-prompts', {
      params: trimmed ? { language: trimmed } : undefined,
    });
    return response.data;
  },
};

/** Knowledge Hub (S2) — org library documents, ask, collections, study activation. */
/** One Assistant profile the viewer may pick (`GET /knowledge/assistant/profiles`). */
export interface AssistantProfileOption {
  id: string;
  audience: "public" | "staff";
  isDefault: boolean;
  /**
   * Level rate and typical cost per message from the shared price list. Data only: the picker
   * shows the typical cost, never per-million wording. Dropped client-side for accounts whose
   * metering is not `on`.
   */
  pricing?: AssistantProfilePricing;
}

export interface AssistantProfilePricing {
  policyVersion: string;
  inputCreditsPerMillion: number;
  cachedInputCreditsPerMillion: number;
  outputCreditsPerMillion: number;
  typicalCreditsPerMessage: number;
}

/** The viewer's selectable profiles; staff entries are present only for verified staff. */
export interface AssistantProfileOptionsResponse {
  profiles: Array<AssistantProfileOption>;
  defaultProfileId: string | null;
}

export const knowledgeApi = {
  getDatasetPreview: async (
    documentToken: string,
    query: KnowledgeDatasetPreviewQuery,
    signal?: AbortSignal,
  ): Promise<KnowledgeDatasetPreview> => {
    const response = await api.get(`/knowledge/documents/${documentToken}/dataset/preview`, { params: query, signal });
    return response.data;
  },
  listAssistantProfiles: async (): Promise<AssistantProfileOptionsResponse> => {
    const response = await api.get("/knowledge/assistant/profiles");
    return response.data;
  },
  listDocuments: async (): Promise<Array<KnowledgeDocumentWithStages>> => {
    const response = await api.get("/knowledge/documents");
    return response.data;
  },
  listFacets: async (): Promise<KnowledgeFacetsResponse> => {
    const response = await api.get("/knowledge/facets");
    return response.data;
  },
  /** The org's stored social posts behind the post links an answer shows. */
  resolveSocialPostsByUrl: async (urls: Array<string>): Promise<SocialPostsByUrlResponse> => {
    const response = await api.post("/knowledge/social-posts/by-url", { urls });
    return response.data;
  },
  uploadDocument: async (file: File): Promise<KnowledgeDocumentListItem> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/knowledge/documents/upload", formData);
    return response.data;
  },
  replaceDocument: async (
    documentToken: string,
    file: File,
  ): Promise<KnowledgeDocumentListItem> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `/knowledge/documents/${encodeURIComponent(documentToken)}/replace`,
      formData,
    );
    return response.data;
  },
  bulkUpload: async (
    files: Array<File>,
  ): Promise<KnowledgeBulkUploadResponse> => {
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    const response = await api.post("/knowledge/documents/bulk", formData);
    return response.data;
  },
  undoBulk: async (batchId: string): Promise<{ deletedCount: number }> => {
    const response = await api.post(
      `/knowledge/documents/bulk/${batchId}/undo`,
    );
    return response.data;
  },
  ingestUrls: async (
    urls: Array<string>,
  ): Promise<KnowledgeLinkIngestResponse> => {
    const response = await api.post("/knowledge/documents/from-urls", { urls });
    return response.data;
  },
  getDocument: async (token: string): Promise<KnowledgeDocumentWithStages> => {
    const response = await api.get(`/knowledge/documents/${token}`);
    return response.data;
  },
  /**
   * Re-run one ingest stage. Stages fail (and therefore retry) independently:
   * a failed provider index leaves the document searchable and downloadable.
   * Returns the refreshed document.
   */
  retryDocumentStage: async (
    documentToken: string,
    stage: ContextMaterialStage,
  ): Promise<KnowledgeDocumentWithStages> => {
    const response = await api.post(
      `/knowledge/documents/${encodeURIComponent(documentToken)}/retry/${stage}`,
    );
    return response.data;
  },
  confirmFacet: async (
    documentToken: string,
    assignmentToken: string,
  ): Promise<KnowledgeDocumentListItem> => {
    const response = await api.post(
      `/knowledge/documents/${documentToken}/facets/${assignmentToken}/confirm`,
    );
    return response.data;
  },
  correctFacet: async (
    documentToken: string,
    assignmentToken: string,
    payload: { kind: string; value: string },
  ): Promise<KnowledgeDocumentListItem> => {
    const response = await api.put(
      `/knowledge/documents/${documentToken}/facets/${assignmentToken}`,
      payload,
    );
    return response.data;
  },
  rejectFacet: async (
    documentToken: string,
    assignmentToken: string,
  ): Promise<KnowledgeDocumentListItem> => {
    const response = await api.post(
      `/knowledge/documents/${documentToken}/facets/${assignmentToken}/reject`,
    );
    return response.data;
  },
  confirmAllFacets: async (
    documentToken: string,
  ): Promise<KnowledgeDocumentListItem> => {
    const response = await api.post(
      `/knowledge/documents/${documentToken}/facets/confirm-all`,
    );
    return response.data;
  },
  /**
   * Inline edits to the human-facing name and description. Returns the whole
   * refreshed document, same shape as `getDocument`.
   */
  updateDocument: async (
    documentToken: string,
    payload: { title?: string; summary?: string },
  ): Promise<KnowledgeDocumentWithStages> => {
    const response = await api.patch(
      `/knowledge/documents/${encodeURIComponent(documentToken)}`,
      payload,
    );
    return response.data;
  },
  updateInstructions: async (
    documentToken: string,
    instructions: string,
  ): Promise<KnowledgeDocumentListItem> => {
    const response = await api.put(
      `/knowledge/documents/${documentToken}/instructions`,
      { instructions },
    );
    return response.data;
  },
  deleteDocument: async (token: string): Promise<{ token: string }> => {
    const response = await api.delete(`/knowledge/documents/${token}`);
    return response.data;
  },
  listDocumentComments: async (
    documentToken: string,
    pageNumber?: number | null,
  ): Promise<Array<KnowledgeDocumentComment>> => {
    const response = await api.get(
      `/knowledge/documents/${encodeURIComponent(documentToken)}/comments`,
      { params: { pageNumber: pageNumber ?? undefined } },
    );
    return response.data;
  },
  createDocumentComment: async (
    documentToken: string,
    input: { body: string; pageNumber?: number | null },
  ): Promise<KnowledgeDocumentComment> => {
    const response = await api.post(
      `/knowledge/documents/${encodeURIComponent(documentToken)}/comments`,
      { body: input.body, pageNumber: input.pageNumber ?? undefined },
    );
    return response.data;
  },
  /**
   * Original markup of an uploaded HTML document, for the sandboxed web view.
   * Goes through the authenticated client rather than an iframe `src` so the
   * bearer token is never put in a URL the embedded page could read.
   */
  getDocumentHtml: async (token: string): Promise<string> => {
    const response = await api.get(
      `/knowledge/documents/${encodeURIComponent(token)}/download`,
      { responseType: "text" },
    );
    return typeof response.data === "string"
      ? response.data
      : String(response.data ?? "");
  },
  /**
   * Rendered HTML for one slide of a PPTX deck, for the sandboxed slide view.
   * One slide per call: the markup inlines its pictures, so fetching the whole
   * deck up front would download every image before the first slide is shown.
   */
  getDocumentSlideHtml: async (
    token: string,
    pageNumber: number,
  ): Promise<string> => {
    const response = await api.get(
      `/knowledge/documents/${encodeURIComponent(token)}/slides/${pageNumber}/html`,
    );
    return String((response.data as { html?: string })?.html ?? "");
  },
  downloadDocument: async (
    token: string,
  ): Promise<{ downloadUrl: string; filename?: string }> => {
    const response = await api.get(`/knowledge/documents/${token}/download`, {
      responseType: "blob",
    });
    const blob = response.data as Blob;
    const header = String(response.headers["content-disposition"] ?? "");
    const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
    const quoted = /filename="([^"]+)"/i.exec(header);
    const filename = star
      ? decodeURIComponent(star[1])
      : quoted?.[1] || undefined;
    return {
      downloadUrl: URL.createObjectURL(blob),
      filename,
    };
  },
  ask: async (
    message: string,
    scope?: {
      documentTokens?: Array<string>
      collectionTokens?: Array<string>
    } | null,
  ): Promise<KnowledgeAskResponse> => {
    const response = await api.post("/knowledge/ask", {
      message,
      documentTokens: scope?.documentTokens,
      collectionTokens: scope?.collectionTokens,
    });
    return response.data;
  },
  /**
   * Stream a grounded Hub answer. Yields `{ type: 'delta', text }` then a final
   * `{ type: 'done', ...KnowledgeAskResponse fields }`. Falls back to callers
   * catching errors and using `ask` if SSE is unavailable.
   */
  askStream: async function* (
    message: string,
    signal?: AbortSignal,
    scope?: {
      documentTokens?: Array<string>
      collectionTokens?: Array<string>
    } | null,
  ): AsyncGenerator<
    | { type: 'delta'; text: string }
    | { type: 'done'; askId: string; text: string; citations: KnowledgeAskResponse['citations']; refused?: boolean; gaps?: Array<string> }
    | { type: 'error'; message: string }
  > {
    const response = await fetch(`${getApiBaseUrl()}/knowledge/ask/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        message,
        documentTokens: scope?.documentTokens,
        collectionTokens: scope?.collectionTokens,
      }),
      signal,
    });
    if (!response.ok || !response.body) {
      const errText = await response.text().catch(() => '');
      throw new Error(errText || `Ask stream failed (${response.status})`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';
      for (const part of parts) {
        const line = part.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        let event: {
          type?: string;
          text?: string;
          askId?: string;
          citations?: KnowledgeAskResponse['citations'];
          refused?: boolean;
          gaps?: Array<string>;
          message?: string;
        };
        try {
          event = JSON.parse(line.slice(6));
        } catch {
          continue;
        }
        if (event.type === 'delta' && typeof event.text === 'string') {
          yield { type: 'delta', text: event.text };
        } else if (event.type === 'done') {
          yield {
            type: 'done',
            askId: event.askId || '',
            text: event.text || '',
            citations: event.citations || [],
            refused: event.refused,
            gaps: event.gaps,
          };
        } else if (event.type === 'error') {
          yield { type: 'error', message: event.message || 'Stream error' };
        }
      }
    }
  },
  search: async (query: string): Promise<KnowledgeSearchResponse> => {
    const response = await api.get("/knowledge/search", {
      params: { q: query },
    });
    return response.data;
  },
  reportWrongPage: async (
    citationToken: string,
  ): Promise<{ ok: boolean }> => {
    const response = await api.post(
      `/knowledge/citations/${citationToken}/wrong-page`,
    );
    return response.data;
  },
  getAllPagePreviews: async (
    documentToken: string,
    options?: { fromPage?: number; toPage?: number },
  ): Promise<{
    pageCount: number;
    pages: Array<{
      pageNumber: number;
      text: string;
      imageBase64?: string | null;
      imageUrl?: string | null;
    }>;
  }> => {
    const response = await api.get(
      `/knowledge/documents/${documentToken}/pages`,
      {
        params: {
          fromPage: options?.fromPage,
          toPage: options?.toPage,
        },
      },
    );
    return response.data;
  },
  getPagePreview: async (
    documentToken: string,
    pageNumber: number,
  ): Promise<{
    ok: boolean;
    pageNumber: number;
    text: string;
    imageBase64?: string | null;
    imageUrl?: string | null;
    previousPageTail?: string | null;
    nextPageHead?: string | null;
  }> => {
    const response = await api.get(
      `/knowledge/documents/${documentToken}/pages/${pageNumber}`,
    );
    return response.data;
  },
  locateSnippet: async (
    documentToken: string,
    snippet: string,
  ): Promise<{
    pageNumber: number | null;
    pageUnknown: boolean;
    matchedSnippet: string | null;
    text: string;
    anchorConfidence: number;
    timestampLabel?: string | null;
    /** The social post at the located span of a digest. */
    post?: SocialCitationPost | null;
    /** The posts a chunk crossing several posts overlaps; not all were cited. */
    posts?: Array<SocialCitationPost>;
  }> => {
    // May trigger on-demand PDF segmentation (up to ~3 min for large docs).
    const response = await api.post(
      `/knowledge/documents/${documentToken}/locate-snippet`,
      { snippet },
      { timeout: 180_000 },
    );
    return response.data;
  },
  listCollections: async (): Promise<Array<KnowledgeCollectionItem>> => {
    const response = await api.get("/knowledge/collections");
    return response.data;
  },
  createCollection: async (payload: {
    name: string;
    description?: string;
  }): Promise<KnowledgeCollectionItem> => {
    const response = await api.post("/knowledge/collections", payload);
    return response.data;
  },
  addCollectionDocument: async (
    collectionToken: string,
    documentToken: string,
  ): Promise<KnowledgeCollectionItem> => {
    const response = await api.post(
      `/knowledge/collections/${collectionToken}/documents`,
      { documentToken },
    );
    return response.data;
  },
  removeCollectionDocument: async (
    collectionToken: string,
    documentToken: string,
  ): Promise<KnowledgeCollectionItem> => {
    const response = await api.delete(
      `/knowledge/collections/${collectionToken}/documents/${documentToken}`,
    );
    return response.data;
  },
  setStudyActivation: async (payload: {
    populationId: string;
    studyId: string;
    documentTokens: Array<string>;
  }): Promise<{ studyId: string; documentTokens: Array<string> }> => {
    const response = await api.put("/knowledge/study-activation", payload);
    return response.data;
  },
  classifyStimulusReference: async (payload: {
    ask: string;
    hasAttachedStimulus: boolean;
  }): Promise<{ referencesStimulus: boolean; reason: string }> => {
    const response = await api.post("/knowledge/stimulus-reference", payload);
    return response.data;
  },
};

export interface KnowledgeThreadScope extends KnowledgeAskSourceScope {
  sourceMode?: "auto" | "manual";
  mode: 'all' | 'selected';
  documentTokens: Array<string>;
  collectionTokens: Array<string>;
  web?: boolean;
  library?: boolean;
}

export interface KnowledgeThreadListItem {
  token: string;
  title: string;
  visibility: string;
  /** Answer language for every turn in this thread; null when none was picked. */
  language?: string | null;
  scope: KnowledgeThreadScope;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  messageCount: number;
  citationCount: number;
  /**
   * Whether the CALLER owns this thread. A workflow-minted thread is readable
   * by the whole organization but writable only by its owner, so a row the
   * client received is not necessarily a row it may rename, delete, pin or
   * post into. Optional on the type so a cached page written before the field
   * existed still parses; treat absent as "assume not owned" only where that
   * is the safe default.
   */
  isOwner?: boolean;
  /**
   * A teammate's shared thread the caller has not opened since it was
   * shared: the sidebar shows a small dot until they open it. Optional so a
   * cached page written before the field existed reads as not new.
   */
  isNew?: boolean;
  /**
   * Whether the thread is open (view-only) to the organization. Server
   * derived; optional so a cached page written before the field existed
   * reads as private.
   */
  isShared?: boolean;
  /**
   * Owner pin. Pinned rows sort ahead of recency. Optional so a cache page
   * written before the field existed still parses; treat absent as unpinned.
   */
  pinned?: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Step 9 D1 collapse. Non-null only when this row is the collapsed
   * representative of a schedule's threads. Counts every non-deleted thread
   * for that schedule (including the surviving one).
   */
  scheduleRunCount?: number;
  /**
   * Human-readable day of the surviving thread ("3 Sep"). Populated together
   * with `scheduleRunCount`. Derived from the thread's UTC `created_at`.
   */
  dayLabel?: string;
}

export interface KnowledgeThreadListPage {
  threads: Array<KnowledgeThreadListItem>;
  nextCursor: string | null;
}

export interface KnowledgeThreadView extends KnowledgeThreadListItem {
  ownerDisplayName: string | null;
  /**
   * The thread this one was forked or duplicated from, while the caller can
   * still read it. Null (or absent on older responses) otherwise.
   */
  forkedFrom?: { token: string; title: string } | null;
  /**
   * Step 9 thread header. The newest 10 sibling threads of the same schedule,
   * ordered newest-first. Absent when this thread has no `source_schedule_id`.
   * Non-owners see the same list.
   */
  scheduleSiblings?: Array<{ token: string; title: string }>;
}

export interface KnowledgeThreadMessage extends Pick<AssistantEvidenceDoneFields,
  'evidenceVersion' | 'citations_resolved' | 'answer_spans' | 'calculations' | 'imageRequests'> {
  /** The image request this message's approval turn admitted (its user ask and its answer). */
  imageApprovalRequestId?: string;
  /** The format that approval turn rendered. */
  imageApprovalFormatId?: string;
  /** Offered library/social/web sources the answer read but did not cite. */
  further_reading?: Array<AssistantEvidenceItem> | null;
  /** Exact immutable Quick findings part; generic exhibits are legacy only. */
  quickStudyFindings?: QuickStudyFindingsMessage | null;
  /** P1: which assistant profile ran this turn (id, version, source). */
  profile?: { profileId: string | null; profileVersion: number | null; source: string } | null;
  token: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  askId: string | null;
  /**
   * Grounded asks persist `KnowledgeAskCitation`. Assistant turns persist the
   * `AssistantCitation` union into the SAME jsonb column, because a turn's
   * sources can be web pages with no local document row. The server's own view
   * type says so (`KnowledgeThreadMessageView.citations` in
   * `knowledge.service.ts` is `Array<KnowledgeAskCitation | AssistantCitation>`),
   * and `askAssistantTurnStream` writes `Array<AssistantCitation>` there on
   * every assistant turn, so this is the honest reload shape rather than a
   * defensive widening. A reader must partition on `kind` before rendering:
   * a web citation put through the document components has no document name,
   * no anchors and no page.
   */
  citations: Array<KnowledgeAskCitation | AssistantCitation> | null;
  refused: boolean;
  gaps: Array<string> | null;
  /** Agent C: validated plan on the proposing assistant message, if any. */
  plan?: AssistantTurnPlanTail | null;
  /**
   * Step 6 persistence: featured-plot specs on the assistant message.
   * Absent on legacy rows; the thread renderer treats missing as no exhibits.
   */
  charts?: Array<AnalystChartSpec> | null;
  tables?: Array<AnalystTableSpec> | null;
  /**
   * Unconfirmed creative set on the proposing assistant message.
   * Explicit null on ordinary rows, same as `plan`.
   */
  creativeDrafts?: Array<AssistantCreativeDraft> | null;
  /** Tracker the agent proposed on this message; null when none. */
  socialTrackerProposal?: SocialTrackerProposal | null;
  /** Launch card on this message. Owner only: a viewer and a fork get null. */
  preparedConversation?: PreparedConversation | null;
  /**
   * Last suggestion cards stored for this assistant turn, verbatim. Owner
   * only: a viewer (and every legacy or user row) gets null.
   */
  suggestions?: TurnSuggestions["suggestions"] | null;
  /**
   * The steps this assistant turn ran, as `done.tool_calls` persisted them
   * (`knowledge_thread_messages.tool_calls`). Snake case is the stored shape.
   * `item_count` is additive and absent on older rows. Rebuilds the folded
   * activity trail after a reload (docs/plans/thread-activity-steps.md, D6).
   */
  toolCalls?: Array<AssistantToolCall & { item_count?: number }> | null;
  /** A12: the saved report this assistant message announced (inline card). */
  report?: { publicId: string; title: string };
  /** A12: the agreed brief that report was written from. */
  reportBrief?: string;
  /** Saved canvas card pictures; absent when the reply has no canvas. */
  cardImages?: Array<import("@digital-twins/shared-types").SourceCardImage>;
  /** True while a canvas card may still get its picture. */
  cardImagesPending?: boolean;
  createdAt: string;
}

export interface KnowledgeThreadPlanCursorStep {
  slug: string;
  kind: AssistantPlanStepKind;
  displayName: string;
  title?: string;
  ask?: string;
  inputs?: { audience?: string };
  flags?: Array<string>;
  status:
    | "pending"
    | "current"
    | "running"
    | "done"
    | "failed"
    | "skipped";
}

/** One web source page of a message and its preview image (https), when the page has one. */
export interface KnowledgeThreadSourcePreview {
  url: string;
  imageUrl: string | null;
}

/** Web source thumbnails for one assistant message, loaded after the answer. */
export interface KnowledgeThreadSourcePreviewsView {
  previews: Array<KnowledgeThreadSourcePreview>;
  cardImages?: Array<import("@digital-twins/shared-types").SourceCardImage>;
  /** True while a canvas card may still get its picture; the page polls only then. */
  cardImagesPending?: boolean;
}

/**
 * Server cursor for a proposing message. `messageId` is the message token,
 * never a row id.
 */
export interface KnowledgeThreadPlanCursorView {
  publicId: string;
  threadToken: string;
  messageId: string;
  lifecycle:
    | "proposed"
    | "active"
    | "done"
    | "dismissed"
    | "superseded";
  steps: Array<KnowledgeThreadPlanCursorStep>;
  currentIndex: number | null;
  updatedAt: string;
}

export interface UpsertKnowledgeThreadPlanCursorBody {
  lifecycle: KnowledgeThreadPlanCursorView["lifecycle"];
  steps: Array<KnowledgeThreadPlanCursorStep>;
  currentIndex: number | null;
}

function isNotFoundStatus(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  return (
    (error as { response?: { status?: number } }).response?.status === 404
  );
}

function asCreativeDraftsConfirmResult(
  data: unknown,
): {
  artifacts: Array<ThreadArtifactDto>;
  creativeDrafts?: Array<AssistantCreativeDraft>;
  quickStudyContinuation?: AssistantTurnSnapshot;
} {
  if (Array.isArray(data)) {
    return { artifacts: data as Array<ThreadArtifactDto> };
  }
  if (data && typeof data === "object") {
    const record = data as {
      artifacts?: unknown;
      creativeDrafts?: unknown;
      quickStudyContinuation?: AssistantTurnSnapshot;
    };
    const artifacts = Array.isArray(record.artifacts)
      ? (record.artifacts as Array<ThreadArtifactDto>)
      : [];
    const creativeDrafts = normalizeAssistantCreativeDrafts(
      record.creativeDrafts,
    );
    if (creativeDrafts) {
      return { artifacts, creativeDrafts, quickStudyContinuation: record.quickStudyContinuation };
    }
    return { artifacts, quickStudyContinuation: record.quickStudyContinuation };
  }
  return { artifacts: [] };
}

const SOCIAL_IMAGE_PATH_PREFIX = "social-listening/";

/**
 * The URL of a stored post image under `basePath`. The API returns proxy paths
 * relative to the member base (`social-listening/items/<token>/images/<i>`); the
 * back office swaps that base for its admin one, so one path works for both.
 */
export function resolveSocialImageUrl(basePath: string, imagePath: string): string {
  const trimmedBase = basePath.replace(/\/+$/, "");
  const relative = imagePath.replace(/^\/+/, "");
  const rest = relative.startsWith(SOCIAL_IMAGE_PATH_PREFIX)
    ? relative.slice(SOCIAL_IMAGE_PATH_PREFIX.length)
    : relative;
  return `${trimmedBase}/${rest}`;
}

function intersectSocialWindows(
  first: SocialWindow | undefined,
  second: SocialWindow | undefined,
): SocialWindow | undefined {
  if (!first) {
    return second;
  }
  if (!second) {
    return first;
  }
  return {
    from: Date.parse(first.from) >= Date.parse(second.from) ? first.from : second.from,
    to: Date.parse(first.to) <= Date.parse(second.to) ? first.to : second.to,
  };
}

/** Query params of the Topic posts and counts endpoints; absent filters are omitted (Any). */
export function toSocialTopicFilterParams(
  filters: SocialTopicFilters | undefined,
  extraWindow?: SocialWindow,
): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters?.themeId) {
    params.themeId = filters.themeId;
  }
  if (filters?.brandId) {
    params.brandId = filters.brandId;
  }
  if (filters?.platform) {
    params.platform = filters.platform;
  }
  if (filters?.sentiment) {
    params.sentiment = filters.sentiment;
  }
  if (filters?.source) {
    params.source = filters.source;
  }
  const window = intersectSocialWindows(filters?.window, extraWindow);
  if (window) {
    params.from = window.from;
    params.to = window.to;
  }
  const q = filters?.q?.trim();
  if (q) {
    params.q = q;
  }
  // Both or neither: the server answers half a pair with 400.
  const authorHandle = filters?.author?.handle.trim();
  if (filters?.author && authorHandle) {
    params.authorPlatform = filters.author.platform;
    params.authorHandle = authorHandle;
  }
  if (filters?.market) {
    params.market = filters.market;
  }
  return params;
}

/** Posts list params: filters plus sort (omitted for the default `newest`), cursor and limit. */
export function toSocialTopicPostsParams(
  query: SocialTopicPostsQuery | undefined,
): Record<string, string | number> {
  const params: Record<string, string | number> = toSocialTopicFilterParams(query?.filters);
  if (query?.sort && query.sort !== "newest") {
    params.sort = query.sort;
  }
  if (query?.cursor) {
    params.cursor = query.cursor;
  }
  if (query?.limit) {
    params.limit = query.limit;
  }
  return params;
}

/** Counts params: the one HTTP window is the intersection of `query.window` and `filters.window`, as the server reads them. */
export function toSocialTopicCountsParams(
  query: SocialTopicCountsQuery,
): Record<string, string> {
  return {
    ...toSocialTopicFilterParams(query.filters, query.window),
    groupBy: query.groupBy,
    ...(query.includeMarket ? { includeMarket: 'true' } : {}),
  };
}

/** Series params: the counts filters, plus `bucket` and `brandShareOf` when asked. */
export function toSocialTopicSeriesParams(
  query: SocialTopicSeriesQuery,
): Record<string, string> {
  const params = toSocialTopicFilterParams(query.filters);
  if (query.bucket) {
    params.bucket = query.bucket;
  }
  if (query.brandShareOf) {
    params.brandShareOf = query.brandShareOf;
  }
  if (query.split) {
    params.split = query.split;
  }
  return params;
}

/** Authors params: the counts filters, plus sort (omitted for the default `views`) and limit. */
export function toSocialTopicAuthorsParams(
  query: SocialTopicAuthorsQuery,
): Record<string, string | number> {
  const params: Record<string, string | number> = toSocialTopicFilterParams(query.filters);
  if (query.sort && query.sort !== "views") {
    params.sort = query.sort;
  }
  if (query.limit) {
    params.limit = query.limit;
  }
  if (query.authorSearch) {
    params.authorSearch = query.authorSearch;
  }
  return params;
}

/**
 * Social listening trackers (RND-2900) under `basePath`. Contract:
 * `shared-types/social-listening.ts`. The back office mounts the same routes
 * under one org (`/admin/organizations/:orgToken/social-listening`).
 */
export function createSocialListeningApi(basePath = "/social-listening") {
  return {
    listTrackers: async (): Promise<SocialTrackersResponse> => {
      const response = await api.get(`${basePath}/trackers`);
      return response.data;
    },
    createTracker: async (
      input: SocialTrackerInput,
    ): Promise<SocialTrackerItem> => {
      const response = await api.post(`${basePath}/trackers`, input);
      return response.data;
    },
    suggestTracker: async (
      request: SocialTrackerSuggestionRequest,
    ): Promise<SocialTrackerProposal> => {
      const response = await api.post(`${basePath}/suggestions`, request);
      return response.data;
    },
    updateTracker: async (
      token: string,
      update: SocialTrackerUpdate,
    ): Promise<SocialTrackerItem> => {
      const response = await api.patch(
        `${basePath}/trackers/${encodeURIComponent(token)}`,
        update,
      );
      return response.data;
    },
    syncTracker: async (token: string): Promise<SocialTrackerItem> => {
      const response = await api.post(
        `${basePath}/trackers/${encodeURIComponent(token)}/sync`,
      );
      return response.data;
    },
    pauseTracker: async (token: string): Promise<SocialTrackerItem> => {
      const response = await api.post(
        `${basePath}/trackers/${encodeURIComponent(token)}/pause`,
      );
      return response.data;
    },
    resumeTracker: async (token: string): Promise<SocialTrackerItem> => {
      const response = await api.post(
        `${basePath}/trackers/${encodeURIComponent(token)}/resume`,
      );
      return response.data;
    },
    deleteTracker: async (token: string): Promise<void> => {
      await api.delete(`${basePath}/trackers/${encodeURIComponent(token)}`);
    },
    listPosts: async (
      token: string,
      params?: {
        cursor?: string | null;
        limit?: number;
        sort?: SocialPostsSort;
        topicId?: string | null;
      },
    ): Promise<SocialPostsResponse> => {
      const response = await api.get(
        `${basePath}/trackers/${encodeURIComponent(token)}/posts`,
        {
          params: {
            ...(params?.cursor ? { cursor: params.cursor } : {}),
            ...(params?.limit ? { limit: params.limit } : {}),
            ...(params?.sort && params.sort !== 'newest' ? { sort: params.sort } : {}),
            ...(params?.topicId ? { topic: params.topicId } : {}),
          },
        },
      );
      return response.data;
    },
    listRuns: async (token: string): Promise<SocialSyncRunsResponse> => {
      const response = await api.get(
        `${basePath}/trackers/${encodeURIComponent(token)}/runs`,
      );
      return response.data;
    },
    removePost: async (postToken: string): Promise<void> => {
      await api.delete(`${basePath}/posts/${encodeURIComponent(postToken)}`);
    },
    removeAuthor: async (trackerToken: string, handle: string): Promise<void> => {
      await api.delete(
        `${basePath}/trackers/${encodeURIComponent(trackerToken)}/authors/${encodeURIComponent(handle)}`,
      );
    },
    /** Removes one channel of a Topic; removing the last one deletes the Topic. */
    removeChannel: async (trackerToken: string): Promise<void> => {
      await api.delete(`${basePath}/trackers/${encodeURIComponent(trackerToken)}`);
    },
    listUniverses: async (): Promise<SocialUniversesResponse> => {
      const response = await api.get(`${basePath}/universes`);
      return response.data;
    },
    getUniverse: async (token: string): Promise<SocialUniverseItem> => {
      const response = await api.get(
        `${basePath}/universes/${encodeURIComponent(token)}`,
      );
      return response.data;
    },
    createUniverse: async (
      input: SocialUniverseInput,
    ): Promise<SocialUniverseItem> => {
      const response = await api.post(`${basePath}/universes`, input);
      return response.data;
    },
    suggestUniverse: async (
      request: SocialUniverseSuggestionRequest,
    ): Promise<SocialUniverseSuggestion> => {
      const response = await api.post(`${basePath}/universes/suggest`, request);
      return response.data;
    },
    /** Checks sources as one click: Suggest's proposals and accounts the client typed. */
    checkSocialSources: async (
      request: SocialSourceCheckRequest,
    ): Promise<SocialSourceCheckResponse> => {
      const response = await api.post(`${basePath}/universes/sources/check`, request);
      return response.data;
    },
    updateUniverse: async (
      token: string,
      update: SocialUniverseUpdate,
    ): Promise<SocialUniverseItem> => {
      const response = await api.patch(
        `${basePath}/universes/${encodeURIComponent(token)}`,
        update,
      );
      return response.data;
    },
    deleteUniverse: async (token: string): Promise<void> => {
      await api.delete(`${basePath}/universes/${encodeURIComponent(token)}`);
    },
    /** Updates every active channel of the Topic now. */
    syncUniverse: async (token: string): Promise<SocialUniverseItem> => {
      const response = await api.post(
        `${basePath}/universes/${encodeURIComponent(token)}/sync`,
      );
      return response.data;
    },
    listUniversePosts: async (
      token: string,
      query?: SocialTopicPostsQuery,
    ): Promise<SocialUniversePostsResponse> => {
      const response = await api.get(
        `${basePath}/universes/${encodeURIComponent(token)}/posts`,
        { params: toSocialTopicPostsParams(query) },
      );
      return response.data;
    },
    /**
     * Posts a manager hid from the Topic, for its Hidden section. Asks the
     * posts endpoint with `hidden=only` (SOCIAL_TOPIC_HIDDEN_ONLY); a
     * server before it refuses the param, and the caller falls back to the
     * posts hidden in this session.
     */
    listHiddenUniversePosts: async (
      token: string,
      query?: Pick<SocialTopicPostsQuery, "cursor" | "limit">,
    ): Promise<SocialUniversePostsResponse> => {
      const response = await api.get(
        `${basePath}/universes/${encodeURIComponent(token)}/posts`,
        {
          params: {
            ...toSocialTopicPostsParams(query),
            hidden: SOCIAL_TOPIC_HIDDEN_ONLY,
          },
        },
      );
      return response.data;
    },
    countUniversePosts: async (
      token: string,
      query: SocialTopicCountsQuery,
    ): Promise<SocialTopicCountsResponse> => {
      const response = await api.get(
        `${basePath}/universes/${encodeURIComponent(token)}/counts`,
        { params: toSocialTopicCountsParams(query) },
      );
      return response.data;
    },
    /** Posts per publish-date bucket and channel, for the Topic page's density and trend. */
    getUniverseSeries: async (
      token: string,
      query: SocialTopicSeriesQuery = {},
    ): Promise<SocialTopicSeriesResponse> => {
      const response = await api.get(
        `${basePath}/universes/${encodeURIComponent(token)}/series`,
        { params: toSocialTopicSeriesParams(query) },
      );
      return response.data;
    },
    /** The Topic's authors under the filters (top voices), grouped by platform and normalized handle. */
    getUniverseAuthors: async (
      token: string,
      query: SocialTopicAuthorsQuery = {},
    ): Promise<SocialTopicAuthorsResponse> => {
      const response = await api.get(
        `${basePath}/universes/${encodeURIComponent(token)}/authors`,
        { params: toSocialTopicAuthorsParams(query) },
      );
      return response.data;
    },
    listUniverseRuns: async (token: string): Promise<SocialUniverseRunsResponse> => {
      const response = await api.get(
        `${basePath}/universes/${encodeURIComponent(token)}/runs`,
      );
      return response.data;
    },
    /** Accepts a run's offer: adds its held posts and charges once; a retry charges nothing. */
    unlockRun: async (
      token: string,
      runToken: string,
    ): Promise<SocialRunUnlockResponse> => {
      const response = await api.post(
        `${basePath}/universes/${encodeURIComponent(token)}/runs/${encodeURIComponent(runToken)}/unlock`,
      );
      return response.data;
    },
    /** The kept comments under one post (D13); `commentsCollected` null when they were not read. */
    listPostComments: async (
      postToken: string,
    ): Promise<SocialPostCommentsResponse> => {
      const response = await api.get(
        `${basePath}/posts/${encodeURIComponent(postToken)}/comments`,
      );
      return response.data;
    },
    hidePost: async (postToken: string): Promise<void> => {
      await api.post(`${basePath}/posts/${encodeURIComponent(postToken)}/hide`);
    },
    unhidePost: async (postToken: string): Promise<void> => {
      await api.post(`${basePath}/posts/${encodeURIComponent(postToken)}/unhide`);
    },
    /** API path of a stored image under this base (member or back office). */
    imageUrl: (imagePath: string): string =>
      resolveSocialImageUrl(basePath, imagePath),
    /** Image bytes through the authenticated proxy (cookies), for an object URL. */
    fetchItemImage: async (imagePath: string): Promise<Blob> => {
      const response = await api.get(resolveSocialImageUrl(basePath, imagePath), {
        responseType: "blob",
      });
      return response.data as Blob;
    },
    /** The org's social wallet this UTC month (spent, held, posts added). */
    getPostsUsage: async (): Promise<SocialWalletUsageSummary> => {
      const response = await api.get(`${basePath}/usage`);
      return response.data;
    },
    /** Back office only: the same summary plus internal cost, margin, unpaid and cost-plus runs. */
    getAdminPostsUsage: async (): Promise<SocialAdminWalletUsageSummary> => {
      const response = await api.get(`${basePath}/usage`);
      return response.data;
    },
    /**
     * The Topic dialog's posts and "up to" ceilings: the server plans the
     * draft's channels as the run would and prices them with the claim's
     * hold function.
     */
    getSocialEstimate: async (
      request: SocialEstimateRequest,
    ): Promise<SocialEstimateResponse> => {
      const response = await api.post(`${basePath}/universes/estimate`, request);
      return response.data;
    },
  };
}

export type SocialListeningApi = ReturnType<typeof createSocialListeningApi>;

export const socialListeningApi = createSocialListeningApi();

/**
 * `GET /library/collections/:token/resources` query (library collections
 * plan, AF1). `market`, `year` and `brand` are facet value tokens, sent as
 * repeated params, one per token.
 */
export interface LibraryCollectionResourcesQuery {
  kind?: LibraryResourceKind;
  market?: Array<string>;
  year?: Array<string>;
  brand?: Array<string>;
  cursor?: string;
}

/** The resources query as repeated params (`market=a&market=b`); empty filters are left out. */
export function toLibraryResourcesParams(
  query: LibraryCollectionResourcesQuery,
): URLSearchParams {
  const params = new URLSearchParams();
  if (query.kind) {
    params.append("kind", query.kind);
  }
  for (const key of ["market", "year", "brand"] as const) {
    for (const token of query[key] ?? []) {
      params.append(key, token);
    }
  }
  if (query.cursor) {
    params.append("cursor", query.cursor);
  }
  return params;
}

/**
 * Library collections, Phase A (shared-types `library-collections.ts`): one
 * function per endpoint. Facet options come from `knowledgeApi.listFacets`
 * (AB6 tokens) and Topic brands from `socialListeningApi.listUniverses`.
 */
export const libraryCollectionsApi = {
  /** `GET /library/collections?cursor=`, 50 per page by `updated_at desc, token`. */
  listCollections: async (cursor?: string): Promise<LibraryCollectionCardsPage> => {
    const response = await api.get("/library/collections", {
      params: cursor ? { cursor } : {},
    });
    return response.data;
  },
  createCollection: async (
    body: CreateLibraryCollectionBody,
  ): Promise<LibraryCollectionDetail> => {
    const response = await api.post("/library/collections", body);
    return response.data;
  },
  getCollection: async (token: string): Promise<LibraryCollectionDetail> => {
    const response = await api.get(`/library/collections/${encodeURIComponent(token)}`);
    return response.data;
  },
  /** Only the changed fields. */
  updateCollection: async (
    token: string,
    body: UpdateLibraryCollectionBody,
  ): Promise<LibraryCollectionDetail> => {
    const response = await api.patch(
      `/library/collections/${encodeURIComponent(token)}`,
      body,
    );
    return response.data;
  },
  deleteCollection: async (token: string): Promise<void> => {
    await api.delete(`/library/collections/${encodeURIComponent(token)}`);
  },
  listResources: async (
    token: string,
    query: LibraryCollectionResourcesQuery,
  ): Promise<LibraryCollectionResourcesPage> => {
    const response = await api.get(
      `/library/collections/${encodeURIComponent(token)}/resources`,
      { params: toLibraryResourcesParams(query) },
    );
    return response.data;
  },
  listTopics: async (token: string): Promise<LibraryCollectionTopicsResponse> => {
    const response = await api.get(
      `/library/collections/${encodeURIComponent(token)}/topics`,
    );
    return response.data;
  },
  getKpis: async (
    token: string,
    window: LibraryKpiWindow,
  ): Promise<LibraryCollectionKpis> => {
    const response = await api.get(
      `/library/collections/${encodeURIComponent(token)}/kpis`,
      { params: { window } },
    );
    return response.data;
  },
  /** Include or exclude one item (an exclude also hides a rule match). */
  addMember: async (
    token: string,
    body: LibraryCollectionMemberBody,
  ): Promise<void> => {
    await api.post(`/library/collections/${encodeURIComponent(token)}/members`, body);
  },
  /** Drops the item's include or exclude row; a rule match comes back. */
  removeMember: async (
    token: string,
    kind: LibraryItemKind,
    itemToken: string,
  ): Promise<void> => {
    await api.delete(
      `/library/collections/${encodeURIComponent(token)}/members/${encodeURIComponent(kind)}/${encodeURIComponent(itemToken)}`,
    );
  },
  /** The collections already holding the item; 404 for an audience that is not the org's own. */
  listItemCollections: async (
    kind: LibraryItemKind,
    token: string,
  ): Promise<LibraryItemCollectionsResponse> => {
    const response = await api.get(
      `/library/items/${encodeURIComponent(kind)}/${encodeURIComponent(token)}/collections`,
    );
    return response.data;
  },
  listCandidates: async (
    query: LibraryCandidatesQuery,
  ): Promise<LibraryCollectionResourcesPage> => {
    const response = await api.get("/library/candidates", { params: query });
    return response.data;
  },
  previewCollection: async (
    body: LibraryCollectionPreviewBody,
  ): Promise<LibraryCollectionPreviewResponse> => {
    const response = await api.post("/library/collections/preview", body);
    return response.data;
  },
  search: async (q: string): Promise<LibrarySearchResponse> => {
    const response = await api.get("/library/search", { params: { q } });
    return response.data;
  },
  /** "See all" for one group, a page at a time. */
  searchGroup: async (
    q: string,
    group: LibrarySearchGroup,
    cursor?: string,
  ): Promise<LibrarySearchGroupPage<unknown>> => {
    const response = await api.get("/library/search", {
      params: cursor ? { q, group, cursor } : { q, group },
    });
    return response.data;
  },
};

export type LibraryCollectionsApi = typeof libraryCollectionsApi;

export const knowledgeThreadsApi = {
  /**
   * Fetch the full thread list. When `q` is present, returns uncollapsed
   * results matching the query (Step 9 search bypass). When absent, returns
   * the collapsed list (one row per schedule).
   */
  list: async (opts: { q?: string } = {}): Promise<Array<KnowledgeThreadListItem>> => {
    const response = await api.get('/knowledge/threads', {
      params: opts.q ? { q: opts.q } : undefined,
    });
    return response.data;
  },
  listPage: async (params: {
    limit: number;
    cursor?: string | null;
  }): Promise<KnowledgeThreadListPage> => {
    const response = await api.get('/knowledge/threads', {
      params: {
        limit: params.limit,
        ...(params.cursor ? { cursor: params.cursor } : {}),
      },
    });
    return response.data;
  },
  create: async (payload: {
    title?: string;
    scope?: Partial<KnowledgeThreadScope>;
    firstMessage?: string;
    /** Composer Language plan (D4): persists on the thread, not per ask. Omit for "unset". */
    language?: string;
    /**
     * Lobby example-prompt publicId that seeded this thread (R3.6 / B1).
     * Stamped at create so B4 conversion feedback needs no migration.
     */
    sourcePromptId?: string;
  }): Promise<KnowledgeThreadView> => {
    const response = await api.post('/knowledge/threads', payload);
    return response.data;
  },
  get: async (token: string): Promise<KnowledgeThreadView> => {
    const response = await api.get(`/knowledge/threads/${token}`);
    return response.data;
  },
  update: async (
    token: string,
    payload: {
      title?: string;
      pinned?: boolean;
      scope?: Partial<KnowledgeThreadScope>;
      /** Owner only: true shares the thread view-only with the organization. */
      shared?: boolean;
    },
  ): Promise<KnowledgeThreadView> => {
    const response = await api.patch(`/knowledge/threads/${token}`, payload);
    return response.data;
  },
  /**
   * Fork (a teammate) or Duplicate (the owner) a readable thread into a new
   * private thread owned by the caller. Returns the new thread.
   */
  fork: async (token: string): Promise<KnowledgeThreadView> => {
    const response = await api.post(`/knowledge/threads/${token}/fork`);
    return response.data;
  },
  /**
   * Prepare a full study from a suggestion. Returns the draft token and title.
   * The suggestion click opens the study pane on that draft.
   */
  prepareStudy: async (
    token: string,
    body: ({spec: AssistantStudySuggestionSpec; brief?: never; audience?: never} | {brief: string; audience: AssistantStudySuggestionSpec["audience"]; spec?: never}) & {title: string; presentation?: "full" | "quick"; clientActionId?: string},
  ): Promise<AssistantPreparedStudy> => {
    const response = await api.post<AssistantPreparedStudy>(
      `/knowledge/threads/${token}/studies/prepare`,
      body,
    );
    const prepared = normalizeAssistantPreparedStudy(response.data);
    if (!prepared) throw new Error("Invalid prepared study response");
    return prepared;
  },
  /** Soft delete (Step 5.2). The row survives; it just stops showing up anywhere. */
  delete: async (token: string): Promise<{ token: string }> => {
    const response = await api.delete(`/knowledge/threads/${token}`);
    return response.data;
  },
  listMessages: async (
    token: string,
  ): Promise<Array<KnowledgeThreadMessage>> => {
    const response = await api.get(`/knowledge/threads/${token}/messages`);
    return response.data;
  },
  /**
   * Living thread artifacts (briefs, reports, findings, stimuli, …).
   * `GET /knowledge/threads/:token/artifacts` — list may omit `body`.
   */
  listArtifacts: async (
    token: string,
  ): Promise<ThreadArtifactListResponse> => {
    const response = await api.get(
      `/knowledge/threads/${token}/artifacts`,
    );
    return response.data;
  },
  /**
   * Living markdown edit — `PATCH /knowledge/threads/:token/artifacts/:publicId`.
   * Optimistic `version` → 409 on stale (same as tip PATCH).
   */
  updateArtifact: async (
    token: string,
    publicId: string,
    payload: { body: string; version?: number },
  ): Promise<ThreadArtifactDto & { version: number; revision: number }> => {
    const response = await api.patch(
      `/knowledge/threads/${token}/artifacts/${publicId}`,
      payload,
    );
    return response.data;
  },
  getPlanCursor: async (
    threadToken: string,
    messageToken: string,
  ): Promise<KnowledgeThreadPlanCursorView | null> => {
    try {
      const response = await api.get(
        `/knowledge/threads/${threadToken}/messages/${messageToken}/plan-cursor`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isNotFoundStatus(error)) {
        return null;
      }
      throw error;
    }
  },
  getSourcePreviews: async (
    threadToken: string,
    messageToken: string,
  ): Promise<KnowledgeThreadSourcePreviewsView> => {
    const response = await api.get(
      `/knowledge/threads/${threadToken}/messages/${messageToken}/source-previews`,
    );
    return response.data;
  },
  putPlanCursor: async (
    threadToken: string,
    messageToken: string,
    body: UpsertKnowledgeThreadPlanCursorBody,
  ): Promise<KnowledgeThreadPlanCursorView> => {
    const response = await api.put(
      `/knowledge/threads/${threadToken}/messages/${messageToken}/plan-cursor`,
      body,
    );
    return response.data;
  },
  /**
   * Autosave one draft entry. Nest returns the full set:
   * `{ creativeDrafts: Array<AssistantCreativeDraft> }`.
   */
  patchCreativeDraft: async (
    threadToken: string,
    messageToken: string,
    key: string,
    body: { title: string; body: string },
  ): Promise<{ creativeDrafts: Array<AssistantCreativeDraft> }> => {
    const response = await api.patch(
      `/knowledge/threads/${threadToken}/messages/${messageToken}/creative-drafts/${key}`,
      body,
    );
    return response.data;
  },
  /**
   * Confirm the whole set. Request has no body. Nest returns
   * `{ artifacts, creativeDrafts? }`. An array envelope is accepted as a
   * defensive alias for artifacts only.
   */
  confirmCreativeDrafts: async (
    threadToken: string,
    messageToken: string,
  ): Promise<{
    artifacts: Array<ThreadArtifactDto>;
    creativeDrafts?: Array<AssistantCreativeDraft>;
    quickStudyContinuation?: AssistantTurnSnapshot;
  }> => {
    const response = await api.post(
      `/knowledge/threads/${threadToken}/messages/${messageToken}/creative-drafts/confirm`,
    );
    return asCreativeDraftsConfirmResult(response.data);
  },
  /** Stamps the tracker created from the agent's proposal card on its message. */
  markSocialTrackerProposalCreated: async (
    threadToken: string,
    messageToken: string,
    createdTrackerToken: string,
  ): Promise<SocialTrackerProposal> => {
    const response = await api.patch(
      `/knowledge/threads/${threadToken}/messages/${messageToken}/social-tracker-proposal`,
      { createdTrackerToken },
    );
    return response.data;
  },
  /**
   * Cancel the set. Nest returns `{ creativeDrafts: null }`.
   */
  clearCreativeDrafts: async (
    threadToken: string,
    messageToken: string,
  ): Promise<{ creativeDrafts: null }> => {
    const response = await api.delete(
      `/knowledge/threads/${threadToken}/messages/${messageToken}/creative-drafts`,
    );
    return response.data;
  },
};

export type { ThreadAssetView } from "@digital-twins/shared-types/assistant";
import type { ThreadAssetView } from "@digital-twins/shared-types/assistant";

export const threadAssetsApi = {
  list: async (threadToken: string): Promise<Array<ThreadAssetView>> => {
    const response = await api.get(`/knowledge/threads/${threadToken}/assets`);
    return response.data;
  },
  register: async (
    threadToken: string,
    payload: {
      fileToken: string;
      kind: "image" | "video";
      label?: string;
      mimeType?: string;
    },
  ): Promise<{ token: string; ref: AssetMediaRef }> => {
    const response = await api.post(
      `/knowledge/threads/${threadToken}/assets`,
      payload,
    );
    return response.data;
  },
  remove: async (threadToken: string, assetToken: string): Promise<void> => {
    await api.delete(
      `/knowledge/threads/${threadToken}/assets/${assetToken}`,
    );
  },
};

// Guest session token storage keys.
// Study and room share kinds are stored under different keys so a guest can
// hold a valid OTP session for both at the same time without one overwriting
// the other. The same constants are used by `lib/utils/share-guest-intent.ts`.
const GUEST_TOKEN_KEY = "guestSessionToken";
const GUEST_ROOM_TOKEN_KEY = "guestRoomSessionToken";

function getGuestToken(): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(GUEST_TOKEN_KEY);
}

function getGuestRoomToken(): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(GUEST_ROOM_TOKEN_KEY);
}

/**
 * Thrown when a public-report request is rejected for authentication reasons —
 * no guest token, expired token, or a token scoped to another share. Share
 * pages catch this to reopen the OTP gate and replay the action, instead of
 * surfacing a dead-end "failed to send" error.
 */
export class GuestSessionExpiredError extends Error {
  constructor(message = "Your session expired. Verify your email to continue.") {
    super(message);
    this.name = "GuestSessionExpiredError";
  }
}

export function isGuestSessionExpiredError(err: unknown): boolean {
  return err instanceof GuestSessionExpiredError;
}

/** Which report tab a guest request belongs to, so the gate can replay it. */
function guestTabForPath(path: string): GuestSessionTab {
  if (path.startsWith("/deck")) return "deck";
  if (path.startsWith("/export")) return "survey";
  return "chat";
}

/**
 * Single entry point for guest-authenticated `/public/reports/:shareToken/*`
 * calls. Attaches the study guest JWT, and normalizes authorization failures
 * (401, and 403s that are not a message-cap rejection) into
 * `GuestSessionExpiredError` so every caller recovers the same way.
 *
 * Uses raw `fetch` on purpose: the axios instance redirects to login on 401,
 * which is exactly wrong on a share link.
 */
export async function publicReportFetch<T>(
  shareToken: string,
  path: string,
  init: RequestInit & { fallbackError?: string } = {},
): Promise<T> {
  const response = await publicReportRequest(shareToken, path, init);

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** The share report fields checked at the trust boundary. */
export interface SharedReportEnvelope {
  experiment: { deckStatus?: DeckStatus };
  readiness: ReportReadinessView;
}

const reportingDefs = reportingContractsSchema.$defs;

/**
 * The share report parser, built from the generated reporting contract:
 * `readiness` is the generated `ReportReadinessView`, checked as strictly as
 * its contract, and the deck status the generated `DeckStatus`. The rest of
 * the payload is not checked here; the storyline is parsed where the report
 * page reads it (`parseStoryline`).
 */
const parseSharedReport = createBoundaryParser<SharedReportEnvelope>(
  "public-report/report",
  {
    $defs: reportingDefs,
    type: "object",
    required: ["experiment", "readiness"],
    properties: {
      experiment: {
        type: "object",
        properties: {
          deckStatus: { $ref: "#/$defs/DeckStatus" },
        },
      },
      readiness: { $ref: "#/$defs/ReportReadinessView" },
    },
  },
);

/** Binary sibling of `publicReportFetch`, for the guest .xlsx export. */
export async function publicReportBlob(
  shareToken: string,
  path: string,
  init: RequestInit & { fallbackError?: string } = {},
): Promise<Blob> {
  const response = await publicReportRequest(shareToken, path, init);
  return response.blob();
}

async function publicReportRequest(
  shareToken: string,
  path: string,
  init: RequestInit & { fallbackError?: string },
): Promise<Response> {
  const { fallbackError, headers: initHeaders, ...rest } = init;
  const headers: Record<string, string> = {
    ...((initHeaders as Record<string, string>) ?? {}),
  };
  const guestToken = getGuestToken();
  if (guestToken) {
    headers["Authorization"] = `Bearer ${guestToken}`;
  }

  const response = await fetch(
    `${getApiBaseUrl()}/public/reports/${shareToken}${path}`,
    { ...rest, headers, credentials: "include" },
  );

  if (response.ok) return response;

  const data = await response.json().catch(() => ({}) as Record<string, any>);
  if (data?.capReached) {
    const err = new Error(data.message || "Message limit reached") as Error & {
      capReached?: boolean;
    };
    err.capReached = true;
    throw err;
  }
  if (response.status === 401 || response.status === 403) {
    notifyGuestSessionExpired(guestTabForPath(path));
    throw new GuestSessionExpiredError(
      typeof data?.message === "string" && data.message
        ? data.message
        : undefined,
    );
  }
  // Keep the same typed status/data boundary used by authenticated requests.
  // In particular, a guest 409 must reach the chat queue as a busy rejection.
  const httpError = new axios.AxiosError(
    data?.message || fallbackError || "Request failed",
    undefined,
    undefined,
    undefined,
    {
      status: response.status,
      statusText: response.statusText,
      data,
      headers: {},
      // Error serialization must not retain the guest Authorization header.
      config: { headers: new axios.AxiosHeaders() },
    },
  );
  throw httpError;
}

export interface ShareOwnerBrandBundle {
  whiteLabel: boolean;
  /** Owner-org twin-chat kill switch. Missing means on. */
  chatRoomsEnabled?: boolean;
  organizationToken: string | null;
  orgName: string | null;
  brandPrimaryColor: string | null;
  brandSecondaryColor: string | null;
  brandThemeConfig: BrandThemeConfig | null;
  brandLogoUrl: string | null;
  brandLogoUrlOnDark: string | null;
  brandIconLogoUrl: string | null;
  brandRoleGraph: BrandRoleGraph | null;
  brandRoleGraphVersion: string | null;
  brandRoleGraphHash: string | null;
  themePresetId: string | null;
  brandFontFamily: string | null;
  brandHeadingFontFamily: string | null;
  themeOverrides: Record<string, string> | null;
  shareFooterText: string | null;
  organizationUpdatedAt: string | null;
}

export interface PublicAccessModeResponse {
  mode: "read_only" | "email_required" | "unavailable";
  experimentToken?: string;
  brandLogoUrl: string | null;
  brandLogoUrlOnDark: string | null;
  branding: ShareOwnerBrandBundle;
}

function getEmptyShareOwnerBrandBundle(): ShareOwnerBrandBundle {
  return {
    whiteLabel: false,
    organizationToken: null,
    orgName: null,
    brandPrimaryColor: null,
    brandSecondaryColor: null,
    brandThemeConfig: null,
    brandLogoUrl: null,
    brandLogoUrlOnDark: null,
    brandIconLogoUrl: null,
    brandRoleGraph: null,
    brandRoleGraphVersion: null,
    brandRoleGraphHash: null,
    themePresetId: null,
    brandFontFamily: null,
    brandHeadingFontFamily: null,
    themeOverrides: null,
    shareFooterText: null,
    organizationUpdatedAt: null,
  };
}

// Public API (no authentication required)
export const publicApi = {
  getAccessMode: async (
    shareToken: string,
  ): Promise<PublicAccessModeResponse> => {
    const response = await fetch(
      `${getApiBaseUrl()}/public/reports/${shareToken}/access-mode`,
    );
    if (!response.ok) {
      return {
        mode: "unavailable",
        brandLogoUrl: null,
        brandLogoUrlOnDark: null,
        branding: getEmptyShareOwnerBrandBundle(),
      };
    }
    return response.json();
  },

  getRandomPortraitUrls: async (count = 16): Promise<Array<string>> => {
    const response = await fetch(
      `${getApiBaseUrl()}/public/random-portrait-urls?count=${count}`,
    );
    if (!response.ok) {
      throw new Error("Failed to load portrait URLs");
    }
    const data = (await response.json()) as { urls?: Array<string> };
    return data.urls ?? [];
  },

  sendOtp: async (
    shareToken: string,
    email: string,
  ): Promise<{ sent: boolean; expiresInSeconds: number }> => {
    const response = await fetch(
      `${getApiBaseUrl()}/public/reports/${shareToken}/send-otp`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "Failed to send verification code");
    }
    return response.json();
  },

  verifyEmail: async (
    shareToken: string,
    email: string,
    otp: string,
  ): Promise<{
    token: string;
    expiresAt: string;
    organizationAccessGranted: boolean;
  }> => {
    const response = await fetch(
      `${getApiBaseUrl()}/public/reports/${shareToken}/verify-email`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "Verification failed");
    }
    const data = await response.json();
    if (typeof window !== "undefined") {
      sessionStorage.setItem(GUEST_TOKEN_KEY, data.token);
    }
    return data;
  },

  /**
   * The share report, parsed at the trust boundary (`SharedReportEnvelope`):
   * a payload of the wrong shape resolves `{ ok: false, reason }`, logged
   * once, for a typed unavailable state. Access and transport failures throw.
   */
  getReport: async <T extends SharedReportEnvelope = SharedReportEnvelope>(
    shareToken: string,
  ): Promise<BoundaryParseResult<T>> => {
    const headers: Record<string, string> = {};
    const guestToken = getGuestToken();
    if (guestToken) {
      headers["Authorization"] = `Bearer ${guestToken}`;
    }
    const response = await fetch(
      `${getApiBaseUrl()}/public/reports/${shareToken}`,
      {
        headers,
        credentials: "include",
      },
    );
    if (response.status === 401) {
      // Authenticated session expired — fall back to email gate, don't redirect to login
      throw new Error("Email verification required to view this report");
    }
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Email verification required to view this report");
      }
      if (response.status === 404) {
        throw new Error("Report not found or is no longer public");
      }
      throw new Error("Failed to load report");
    }
    return parseSharedReport(await response.json()) as BoundaryParseResult<T>;
  },

  validateToken: async (shareToken: string) => {
    const response = await fetch(
      `${getApiBaseUrl()}/public/reports/${shareToken}/validate`,
    );
    return response.json();
  },

  clearGuestToken: () => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(GUEST_TOKEN_KEY);
    }
  },

  initChat: async (
    shareToken: string,
    participantId?: string,
  ): Promise<{ roomToken: string }> =>
    publicReportFetch(shareToken, `/chat-init`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ participantId }),
      fallbackError: "Failed to initialize chat",
    }),

  sendChatMessage: async (
    shareToken: string,
    content: string,
    images?: Array<{ base64: string; mimeType: string }>,
    participantId?: string,
  ): Promise<{ success: boolean; roomToken: string }> =>
    publicReportFetch(shareToken, `/chat-message`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content, images, participantId }),
      fallbackError: "Failed to send message",
    }),

  getChatMessages: async (
    shareToken: string,
    lastSequenceIndex?: number,
    participantId?: string,
  ): Promise<{
    messages: Array<{
      sequenceIndex: number;
      senderType: ChatRoomSenderType;
      senderParticipantId: string | null;
      content: string;
      mode: "chat" | "sample" | null;
      metadataType: string | null;
      metadata: unknown;
      createdAt: string;
    }>;
  }> => {
    const params = new URLSearchParams();
    if (lastSequenceIndex !== undefined) {
      params.set("lastSequenceIndex", String(lastSequenceIndex));
    }
    if (participantId) {
      params.set("participantId", participantId);
    }
    const qs = params.toString();
    return publicReportFetch(
      shareToken,
      `/chat-messages${qs ? `?${qs}` : ""}`,
      { fallbackError: "Failed to load chat messages" },
    );
  },

  getChatStatus: async (
    shareToken: string,
    participantId?: string,
  ): Promise<{
    messageCount: number;
    cap: number;
    freeRemaining: number;
    roomToken: string | null;
  }> => {
    const pidParam = participantId
      ? `?participantId=${encodeURIComponent(participantId)}`
      : "";
    return publicReportFetch(shareToken, `/chat-status${pidParam}`, {
      fallbackError: "Failed to load chat status",
    });
  },

  // ── Guest rooms (focus groups) ─────────────────────────────────────
  // Guest-scoped mirror of `chatRoomsApi`. Same rooms and orchestration on the
  // backend, so `useChatRoom` can drive a guest focus group unchanged.

  createRoom: async (
    shareToken: string,
    payload: {
      participantIds: Array<string>;
      settings?: {
        language?: string;
      };
      participantTags?: Record<
        string,
        Array<{ chip: string; displayQuestion: string; displayAnswer: string }>
      >;
      participantImages?: Record<string, string>;
      participantBios?: Record<string, string>;
    },
  ): Promise<ChatRoomDto> =>
    publicReportFetch<ChatRoomDto>(shareToken, `/rooms`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      fallbackError: "Failed to start focus group",
    }),

  getRoom: async (
    shareToken: string,
    roomToken: string,
  ): Promise<ChatRoomDto> =>
    publicReportFetch<ChatRoomDto>(shareToken, `/rooms/${roomToken}`, {
      fallbackError: "Failed to load conversation",
    }),

  getRoomMessages: async (
    shareToken: string,
    roomToken: string,
    params?: { limit?: number; offset?: number },
  ): Promise<Array<ChatRoomMessageDto>> => {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.offset !== undefined) query.set("offset", String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return publicReportFetch<Array<ChatRoomMessageDto>>(
      shareToken,
      `/rooms/${roomToken}/messages${suffix}`,
      { fallbackError: "Failed to load messages" },
    );
  },

  sendRoomMessage: async (
    shareToken: string,
    roomToken: string,
    payload: {
      clientRequestId?: string;
      content: string;
      images?: Array<{ base64: string; mimeType: string }>;
    },
  ): Promise<{ success: boolean; roomToken: string; message?: ChatRoomMessageDto }> =>
    publicReportFetch(shareToken, `/rooms/${roomToken}/message`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      fallbackError: "Failed to send message",
    }),

  /** C13: the guest room's resolved reactions snapshot (load and reconnect). */
  getRoomReactions: async (
    shareToken: string,
    roomToken: string,
  ): Promise<import("@digital-twins/shared-types").ChatRoomReactionsEvent> =>
    publicReportFetch<import("@digital-twins/shared-types").ChatRoomReactionsEvent>(
      shareToken,
      `/rooms/${roomToken}/reactions`,
      { fallbackError: "Failed to load reactions" },
    ),

  getRoomImageUrl: (
    shareToken: string,
    roomToken: string,
    key: string,
  ): string =>
    `${getApiBaseUrl()}/public/reports/${shareToken}/rooms/${roomToken}/images/${key}`,

  /** Step 10c: guest read of one room material. */
  getRoomMaterialUrl: (
    shareToken: string,
    roomToken: string,
    key: string,
  ): string =>
    `${getApiBaseUrl()}/public/reports/${shareToken}/rooms/${roomToken}/materials/${encodeMaterialKey(key)}`,

  // ── Guest twin picker ──────────────────────────────────────────────
  // Mirrors `populationsApi.getPersonasForPicker` / `getColumnMetadata`, scoped
  // by share token so the guest composer shows the same twins and filters.

  getPersonasForPicker: async (
    shareToken: string,
    options?: {
      page?: number;
      limit?: number;
      search?: string;
      filters?: Record<string, Array<string>>;
      contextPersonaIds?: Array<string>;
    },
  ): Promise<{
    personas: Array<any>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> => {
    const params = new URLSearchParams();
    appendPersonasQueryParams(params, { ...options, view: "picker" });
    const qs = params.toString();
    return publicReportFetch(shareToken, `/personas${qs ? `?${qs}` : ""}`, {
      fallbackError: "Failed to load twins",
    });
  },

  getAudienceColumnMetadata: async (
    shareToken: string,
  ): Promise<{
    columns: Array<{
      name: string;
      uniqueValues: Array<string>;
      category: string;
      displayLabel: string;
      groupDisplayLabel?: string;
      canonicalId: string | null;
      isOperational?: boolean;
      type?: "multiselect";
      valueLabels?: Record<string, string>;
      queryKey?: string;
    }>;
    totalRows: number;
  }> =>
    publicReportFetch(shareToken, `/audience/column-metadata`, {
      fallbackError: "Failed to load audience filters",
    }),

  /** Response export for a shared study — same artifact the owner downloads. */
  downloadExportXlsx: async (shareToken: string): Promise<Blob> =>
    publicReportBlob(shareToken, `/export/xlsx`, {
      fallbackError: "Export not available for this study.",
    }),

  /** The scale tab book of a shared study's current analysis (SS-6). */
  downloadScaleTablesXlsx: async (shareToken: string): Promise<Blob> =>
    publicReportBlob(shareToken, `/export/scale-tables.xlsx`, {
      fallbackError: "Tables not available for this study.",
    }),

  downloadCombinedWorkbookXlsx: async (shareToken: string): Promise<Blob> =>
    publicReportBlob(shareToken, `/export/data-and-tables.xlsx`, {
      fallbackError: "Combined workbook not available for this study.",
    }),

  /** CSV of the shared study's response table — same rows as the xlsx data sheet. */
  downloadExportCsv: async (shareToken: string): Promise<Blob> =>
    publicReportBlob(shareToken, `/export/csv`, {
      fallbackError: "Export not available for this study.",
    }),
};

/** Ask-about start: the persisted guest question and the room turn it opened. */
export interface PublicAskAboutResponse {
  turnId: string;
  message: ChatRoomMessageDto;
}

// Public Rooms API — mirrors `publicApi` for shared chat rooms.
// Mandatory email-OTP gate before any room data is fetched. Uses raw `fetch`
// so its 401/403 responses cannot trigger the global axios redirect.
export const publicRoomsApi = {
  getAccessMode: async (
    shareToken: string,
  ): Promise<PublicAccessModeResponse> => {
    const response = await fetch(
      `${getApiBaseUrl()}/public/rooms/${shareToken}/access-mode`,
    );
    if (!response.ok) {
      return {
        mode: "unavailable",
        brandLogoUrl: null,
        brandLogoUrlOnDark: null,
        branding: getEmptyShareOwnerBrandBundle(),
      };
    }
    return response.json();
  },

  sendOtp: async (
    shareToken: string,
    email: string,
  ): Promise<{ sent: boolean; expiresInSeconds: number }> => {
    const response = await fetch(
      `${getApiBaseUrl()}/public/rooms/${shareToken}/send-otp`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "Failed to send verification code");
    }
    return response.json();
  },

  verifyEmail: async (
    shareToken: string,
    email: string,
    otp: string,
  ): Promise<{ token: string; expiresAt: string }> => {
    const response = await fetch(
      `${getApiBaseUrl()}/public/rooms/${shareToken}/verify-email`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "Verification failed");
    }
    const data = await response.json();
    if (typeof window !== "undefined") {
      sessionStorage.setItem(GUEST_ROOM_TOKEN_KEY, data.token);
    }
    return data;
  },

  getRoom: async (
    shareToken: string,
  ): Promise<{
    room: ChatRoomDto;
    messages: Array<ChatRoomMessageDto>;
    ownerOrg?: {
      name: string;
      logoUrl: string | null;
      logoUrlOnDark: string | null;
    };
    /** True while any turn (owner, guest, or another viewer's) is still running in the room. */
    turnInProgress: boolean;
  }> => {
    const headers: Record<string, string> = {};
    const guestToken = getGuestRoomToken();
    if (guestToken) {
      headers["Authorization"] = `Bearer ${guestToken}`;
    }
    const response = await fetch(
      `${getApiBaseUrl()}/public/rooms/${shareToken}`,
      { headers },
    );
    if (response.status === 401 || response.status === 403) {
      throw new Error("Email verification required to view this room");
    }
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Shared room not found or is no longer public");
      }
      throw new Error("Failed to load shared room");
    }
    return response.json();
  },

  /**
   * Starts an ask-about turn for one participant, keyed by the participant's
   * public `id` from the room read (never a row id). Resolves with the persisted
   * guest question and its turn id; the twin's reply lands later in `getRoom`
   * as a message whose `metadata.responseTurnId` equals `turnId`. Errors carry
   * the HTTP `status` (409 while another turn is in progress, 400 for a bad
   * request), `code` when the backend sends one (`room_not_active` on the 400
   * for a room that is not active) and `capReached: true` when the guest
   * message cap is hit.
   */
  askAbout: async (
    shareToken: string,
    participantId: string,
    chipText: string,
  ): Promise<PublicAskAboutResponse> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const guestToken = getGuestRoomToken();
    if (guestToken) {
      headers["Authorization"] = `Bearer ${guestToken}`;
    }
    const response = await fetch(
      `${getApiBaseUrl()}/public/rooms/${shareToken}/ask-about`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ participantId, chipText }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw Object.assign(new Error(data.message || "Failed to send message"), {
        status: response.status,
        code: typeof data?.code === "string" ? data.code : undefined,
        capReached: data?.capReached === true,
      });
    }
    return response.json();
  },

  /** C13: the shared room's resolved reactions snapshot, under the guest room token. */
  getReactions: async (shareToken: string): Promise<import("@digital-twins/shared-types").ChatRoomReactionsEvent> => {
    const headers: Record<string, string> = {};
    const guestToken = getGuestRoomToken();
    if (guestToken) {
      headers["Authorization"] = `Bearer ${guestToken}`;
    }
    const response = await fetch(
      `${getApiBaseUrl()}/public/rooms/${shareToken}/reactions`,
      { headers },
    );
    if (!response.ok) {
      throw new Error("Failed to load reactions");
    }
    return response.json();
  },

  clearGuestToken: () => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(GUEST_ROOM_TOKEN_KEY);
    }
  },
};

// Share Management API (requires authentication)
export const shareApi = {
  getStatus: async (experimentId: string) => {
    const response = await api.get(`/experiments/${experimentId}/share`);
    return response.data;
  },
  enable: async (experimentId: string) => {
    const response = await api.post(`/experiments/${experimentId}/share`);
    return response.data;
  },
  disable: async (experimentId: string) => {
    const response = await api.delete(`/experiments/${experimentId}/share`);
    return response.data;
  },
  regenerate: async (experimentId: string) => {
    const response = await api.post(
      `/experiments/${experimentId}/share/regenerate`,
    );
    return response.data;
  },
  ensureShareToken: async (experimentId: string) => {
    const response = await api.post(
      `/experiments/${experimentId}/ensure-share-token`,
    );
    return response.data as { shareToken: string; shareUrl: string };
  },
};

// Experiment Sharing (collaborators, guests, sharing mode)
export const experimentSharingApi = {
  getCollaborators: async (experimentId: string) => {
    const response = await api.get(
      `/experiments/${experimentId}/collaborators`,
    );
    return response.data;
  },
  addCollaborator: async (experimentId: string, userId: string) => {
    const response = await api.post(
      `/experiments/${experimentId}/collaborators`,
      { userId },
    );
    return response.data;
  },
  removeCollaborator: async (experimentId: string, collaboratorId: string) => {
    const response = await api.delete(
      `/experiments/${experimentId}/collaborators/${collaboratorId}`,
    );
    return response.data;
  },
  addGuest: async (
    experimentId: string,
    email: string,
    expiresInDays?: number,
  ): Promise<{ id: string; email: string; invitationEmailSent: boolean }> => {
    const response = await api.post(`/experiments/${experimentId}/guests`, {
      email,
      expiresInDays,
    });
    return response.data;
  },
  removeGuest: async (experimentId: string, guestId: string) => {
    const response = await api.delete(
      `/experiments/${experimentId}/guests/${guestId}`,
    );
    return response.data;
  },
  setSharingMode: async (
    experimentId: string,
    mode: "private" | "shared" | "organization",
  ) => {
    const response = await api.put(
      `/experiments/${experimentId}/sharing-mode`,
      { mode },
    );
    return response.data;
  },
  getGuestChat: async (experimentId: string) => {
    const response = await api.get(
      `/experiments/${experimentId}/sharing/guest-chat`,
    );
    return response.data as { guestChatEnabled: boolean; guestChatCap: number };
  },
  setGuestChat: async (experimentId: string, enabled: boolean, cap: number) => {
    const response = await api.patch(
      `/experiments/${experimentId}/sharing/guest-chat`,
      { enabled, cap },
    );
    return response.data;
  },
};

export interface TwinsAggregateResponse {
  totalTwins: number;
  featuredTwinsCount: number;
  completeTwinCount: number;
  promptCoverageCount: number;
  demographics: Record<string, Record<string, number>>;
  dataset: {
    columns: number;
    questions: number;
    operational: number;
    mappedQuestions: number;
    unmappedQuestions: number;
  };
  compression: {
    avgNormalizedAttributes: number;
    avgCompressedAttributes: number;
    avgDroppedAttributes: number;
    compressionRatioPct: number;
  };
  operational: {
    status: string;
    rowCountExpected: number;
    rowCountMaterialized: number;
    generatedWindow: {
      firstTwinAt: string | null;
      lastTwinAt: string | null;
    };
  };
  timeline: Array<{
    label: string;
    status: string;
    timestamp: string;
  }>;
  logs: string[];
}

function createAbortError(): Error {
  const error = new Error("Parse status polling aborted");
  error.name = "AbortError";
  return error;
}

function waitForPollInterval(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      reject(createAbortError());
    };

    signal?.addEventListener("abort", onAbort);
  });
}

/** A single IAB taxonomy label assigned to a population. */
export interface TaxonomyLabel {
  token: string;
  sourceId?: string | null;
  name: string;
  fullPath: string;
  tier: number;
}

/** Audience summary shape returned by the populations list/get endpoints. */
export interface AudienceSummary {
  id: string;
  name: string;
  /**
   * Lifecycle status:
   *  - "processing"  — initial Argo build in flight
   *  - "ready"       — data is live and queryable
   *  - "error"       — Argo build or callback failed
   *  - "relaunching" — full pipeline re-run in flight; prior data stays live
   */
  status: "processing" | "ready" | "error" | "relaunching";
  processingStage?:
    | "mapping"
    | "normalizing"
    | "backfill"
    | "compact"
    | "persisting"
    | null;
  rowCount: number | null;
  columnCount: number | null;
  /** The audience's own generated-copy language (RND-2668). Null on audiences created before RND-2668. */
  language?: string | null;
  audienceType?: "B2B" | "B2C";
  /**
   * Internal audience tier slug. New tiers (`tier1`/`tier2`/`tier3`) are stable forever;
   * legacy values (`standard`/`premium`) are kept for back-compat with un-migrated rows.
   */
  tier?: "tier1" | "tier2" | "tier3" | "standard" | "premium";
  creditsPerRespondent?: number;
  /** Effective chat rate for this audience. Absent means the tier map still applies. */
  chatCreditsPerMessage?: number;
  /** Effective focus-group rate for this audience. */
  focusGroupCreditsPerMessage?: number;
  /** Effective quick-study credits per read for this audience. */
  quickStudyCreditsPerRead?: number;
  sharingMode?:
    | "private"
    | "shared"
    | "organization"
    | "published"
    | "marketplace";
  /** Attribution identity for cross-org shared audiences. */
  sharedAs?: "member" | "brand";
  sharedAsUserId?: string | null;
  /** RND-2640: true when the viewer's org has edit access (owning org or partner org with a grant/invite), or platform admin. */
  canEdit?: boolean;
  /** True for accepted external-invite collaborators who can share this audience with their own org */
  canManageOrgSharing?: boolean;
  /** Current scope of the org grant for the viewer's org */
  orgGrantScope?: 'org' | 'specific' | null;
  /** Creator PII only when the viewer can manage (GET /populations/:id with visibility) */
  creatorEmail?: string | null;
  creatorName?: string | null;
  organizationToken?: string;
  organizationName?: string | null;
  publisherDomain?: string;
  description?: string;
  contextText?: string;
  aiDescription?: string;
  narrativeCard?: {
    header: string;
    subheader: string;
    bodyParagraph1: string;
    bodyParagraph2?: string | null;
    audienceProfile: Array<{ field: string; value: string }>;
    topLineInsights: Array<{ title: string; description: string }>;
    meta: {
      n: number;
      audienceType: string;
      model: string;
      generatedAt: string;
      findingType: string;
    };
  } | null;
  /** @deprecated Use contentLabels instead. Kept for backward compatibility. */
  aiTags?: string[];
  /** IAB Audience Taxonomy 1.1 labels classifying this audience's demographic and interest segments. */
  audienceLabels?: TaxonomyLabel[];
  /** IAB Content Taxonomy 3.1 labels classifying the research domains and content areas this audience's data covers. */
  contentLabels?: TaxonomyLabel[];
  /** Industry taxonomy label from the new dedicated classifier. */
  industryLabel?: {
    token: string;
    sourceId: string | null;
    name: string;
    fullPath: string;
    tier: number;
    confidence: number | null;
  } | null;
  industryLabels?: TaxonomyLabel[] | null;
  /** User-overridden industry token (future use). */
  primaryIndustryToken?: string | null;
  coverImageUrl?: string;
  logoUrl?: string;
  processingError?: string;
  analysisColumns?: string[];
  activeFilterColumns?: string[] | null;
  audienceTags?: Array<AudienceTag> | null;
  segmentationMappings?: SegmentationMapping[] | null;
  columnMetadataCache?: Array<{ name: string; uniqueValues?: string[] }>;
  constantContext?: Record<string, string>;
  stageMetadata?: {
    tagline?: string;
    shortDescription?: string;
    subtitle?: string;
    description?: string;
    titleFont?: string;
    selectedCoverPhotoIndex?: number;
    coverPhotoOptions?: Array<{ url: string; thumbnailUrl?: string }>;
  } | null;
  createdAt: string;
  updatedAt: string;
  userId?: string;
  isBookmarked?: boolean;
  bookmarkedAt?: string | null;
  /** Whether a study can launch on this audience (detail-for-ui only): the launch check's own outcome. */
  readiness?: AudienceReadiness;
  processingMetadata?: {
    compactContextDegraded?: boolean;
    postProcessingStatus?: {
      categorize: "pending" | "success" | "failed";
      describe: "pending" | "success" | "failed";
      coverImage: "pending" | "success" | "failed";
    } | null;
  } | null;
  [key: string]: unknown;
}

/** The launch check's outcome; a refusal is a rule-catalog code with its params. */
export type AudienceReadiness =
  | { ready: true }
  | { ready: false; code: string; params: Record<string, string | string[]> };

export interface AudienceProcessingStatus {
  id: string;
  status: "processing" | "ready" | "error" | "relaunching";
  processingStage?:
    | "mapping"
    | "normalizing"
    | "backfill"
    | "compact"
    | "persisting"
    | null;
  processingError?: string | null;
  processingMetadata?: {
    compactContextDegraded?: boolean;
    postProcessingStatus?: {
      categorize: "pending" | "success" | "failed";
      describe: "pending" | "success" | "failed";
      coverImage: "pending" | "success" | "failed";
    } | null;
  } | null;
}

// Populations API
export const audienceImportSessionsApi = {
  create: async (
    data: FormData,
    requestId?: string,
    onUploadProgress?: (percent: number) => void,
  ): Promise<AudienceCreationFlowSnapshot> => {
    const response = await api.post("/populations/import-sessions", data, {
      // Synchronous staging + preflight; allow large uploads on slow links (align with LB/ingress limits).
      timeout: 300_000,
      ...(requestId
        ? {
            headers: {
              "x-request-id": requestId,
            },
          }
        : {}),
      onUploadProgress: onUploadProgress
        ? (e) => {
            if (e.total)
              onUploadProgress(Math.round((e.loaded * 100) / e.total));
          }
        : undefined,
    });
    return response.data;
  },
  getLatest: async (
    signal?: AbortSignal,
  ): Promise<AudienceCreationFlowSnapshot | null> => {
    const response = await api.get("/populations/import-sessions/latest", {
      ...(signal ? { signal } : {}),
    });
    return response.data || null;
  },
  get: async (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<AudienceImportSession> => {
    const response = await api.get(
      `/populations/import-sessions/${sessionId}`,
      {
        ...(signal ? { signal } : {}),
      },
    );
    return response.data;
  },
  /** Lightweight progress/status for polling during active analyze/create. */
  getProgress: async (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<AudienceImportStatusProjection | null> => {
    try {
      const response = await api.get(
        `/populations/import-sessions/${sessionId}/progress`,
        { ...(signal ? { signal } : {}) },
      );
      return response.data;
    } catch (err: unknown) {
      // Same contract as getFlowSnapshot: missing/expired session is not a hard failure.
      if (
        err &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status === 404
      ) {
        return null;
      }
      throw err;
    }
  },
  getFlowSnapshot: async (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<AudienceCreationFlowSnapshot> => {
    const response = await api.get(
      `/populations/import-sessions/${sessionId}/flow`,
      { ...(signal ? { signal } : {}) },
    );
    return response.data;
  },
  getContextMaterialsProjection: async (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<AudienceImportContextMaterialsProjection> => {
    const response = await api.get(
      `/populations/import-sessions/${sessionId}/context-materials-projection`,
      { ...(signal ? { signal } : {}) },
    );
    return response.data;
  },
  getWorkbookProjection: async (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<AudienceImportWorkbookProjection> => {
    const response = await api.get(
      `/populations/import-sessions/${sessionId}/workbook-projection`,
      { ...(signal ? { signal } : {}) },
    );
    return response.data;
  },
  getPreviewProjection: async (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<AudienceImportPreviewProjection> => {
    const response = await api.get(
      `/populations/import-sessions/${sessionId}/preview-projection`,
      { ...(signal ? { signal } : {}) },
    );
    return response.data;
  },
  previewTag: async (
    sessionId: string,
    conditions: Array<AudienceTagCondition>,
    expectedVersion: number,
    signal?: AbortSignal,
  ): Promise<{ matchingCount: number; totalCount: number }> => {
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/tag-preview`,
      { conditions, expectedVersion },
      { ...(signal ? { signal } : {}) },
    );
    return response.data;
  },
  getIdentityShell: async (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<IdentityStepShell> => {
    const response = await api.get(
      `/populations/import-sessions/${sessionId}/identity`,
      { ...(signal ? { signal } : {}) },
    );
    return response.data;
  },
  getIdentitySourceOptions: async (
    sessionId: string,
    canonicalId: string,
    options?: {
      query?: string;
      offset?: number;
      limit?: number;
      signal?: AbortSignal;
    },
  ): Promise<IdentitySourceOptions> => {
    const { signal, ...params } = options ?? {};
    return retryIdentityWorkspaceHydrationRequest({
      signal,
      request: async () => {
        const response = await api.get(
          `/populations/import-sessions/${sessionId}/identity/${canonicalId}/source-options`,
          {
            params: {
              ...(params.query ? { q: params.query } : {}),
              ...(typeof params.offset === "number"
                ? { offset: params.offset }
                : {}),
              ...(typeof params.limit === "number"
                ? { limit: params.limit }
                : {}),
            },
            ...(signal ? { signal } : {}),
          },
        );
        return response.data;
      },
    });
  },
  getIdentityValueMapWorkspace: async (
    sessionId: string,
    canonicalId: string,
    signal?: AbortSignal,
  ): Promise<IdentityValueMapWorkspace> => {
    return retryIdentityWorkspaceHydrationRequest({
      signal,
      request: async () => {
        const response = await api.get(
          `/populations/import-sessions/${sessionId}/identity/${canonicalId}/value-map`,
          { ...(signal ? { signal } : {}) },
        );
        return response.data;
      },
    });
  },
  patch: async (
    sessionId: string,
    payload: ImportSessionPatch,
    requestId?: string,
  ): Promise<AudienceImportRouteAck> => {
    const response = await api.patch(
      `/populations/import-sessions/${sessionId}`,
      payload,
      {
        timeout: 60_000,
        ...(requestId
          ? {
              headers: {
                "x-request-id": requestId,
              },
            }
          : {}),
      },
    );
    return response.data;
  },
  assignSheets: async (
    sessionId: string,
    payload: ImportSessionSheetAssignmentPatch,
    expectedVersion?: number,
  ): Promise<AudienceImportRouteAck> => {
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/assign-sheets`,
      payload,
      {
        timeout: 60_000,
        ...(expectedVersion
          ? {
              headers: {
                "If-Match": `"${expectedVersion}"`,
              },
            }
          : {}),
      },
    );
    return response.data;
  },
  patchIdentityField: async (
    sessionId: string,
    canonicalId: string,
    payload: IdentityFieldPatchRequest,
    requestId?: string,
  ): Promise<IdentityFieldPatchResponse> => {
    const response = await api.patch(
      `/populations/import-sessions/${sessionId}/identity/${canonicalId}`,
      payload,
      {
        timeout: 60_000,
        ...(requestId
          ? {
              headers: {
                "x-request-id": requestId,
              },
            }
          : {}),
      },
    );
    return response.data;
  },
  createAudience: async (
    sessionId: string,
    expectedVersion?: number,
    requestId?: string,
    activeFilterColumns?: string[],
    customLabels?: Record<string, string>,
    audienceTags?: unknown[],
  ): Promise<AudienceImportRouteAck> => {
    const body: Record<string, unknown> = {};
    if (activeFilterColumns) body.activeFilterColumns = activeFilterColumns;
    if (customLabels && Object.keys(customLabels).length > 0)
      body.customLabels = customLabels;
    if (audienceTags && audienceTags.length > 0)
      body.audienceTags = audienceTags;
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/create`,
      body,
      {
        timeout: 180_000,
        ...(expectedVersion || requestId
          ? {
              headers: {
                ...(expectedVersion
                  ? {
                      "If-Match": `"${expectedVersion}"`,
                    }
                  : {}),
                ...(requestId
                  ? {
                      "x-request-id": requestId,
                    }
                  : {}),
              },
            }
          : {}),
      },
    );
    return response.data;
  },
  createAudienceArgo: async (
    sessionId: string,
    expectedVersion?: number,
    requestId?: string,
    activeFilterColumns?: string[],
    customLabels?: Record<string, string>,
    audienceTags?: unknown[],
  ): Promise<AudienceImportRouteAck & { argoWorkflowName?: string }> => {
    const body: Record<string, unknown> = {};
    if (activeFilterColumns) body.activeFilterColumns = activeFilterColumns;
    if (customLabels && Object.keys(customLabels).length > 0)
      body.customLabels = customLabels;
    if (audienceTags && audienceTags.length > 0)
      body.audienceTags = audienceTags;
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/create-argo`,
      body,
      {
        timeout: 180_000,
        ...(expectedVersion || requestId
          ? {
              headers: {
                ...(expectedVersion
                  ? {
                      "If-Match": `"${expectedVersion}"`,
                    }
                  : {}),
                ...(requestId
                  ? {
                      "x-request-id": requestId,
                    }
                  : {}),
              },
            }
          : {}),
      },
    );
    return response.data;
  },
  retryCreate: async (
    sessionId: string,
    expectedVersion?: number,
    requestId?: string,
  ): Promise<AudienceImportRouteAck> => {
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/retry`,
      {},
      {
        timeout: 180_000,
        ...(expectedVersion || requestId
          ? {
              headers: {
                ...(expectedVersion
                  ? {
                      "If-Match": `"${expectedVersion}"`,
                    }
                  : {}),
                ...(requestId
                  ? {
                      "x-request-id": requestId,
                    }
                  : {}),
              },
            }
          : {}),
      },
    );
    return response.data;
  },
  retryAnalysis: async (
    sessionId: string,
    expectedVersion?: number,
  ): Promise<AudienceImportRouteAck> => {
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/retry-analysis`,
      {},
      {
        timeout: 180_000,
        ...(expectedVersion
          ? {
              headers: {
                "If-Match": `"${expectedVersion}"`,
              },
            }
          : {}),
      },
    );
    return response.data;
  },
  completeRequiredFieldsStep: async (
    sessionId: string,
    expectedVersion?: number,
  ): Promise<AudienceImportRouteAck> => {
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/required-fields/complete`,
      {},
      expectedVersion
        ? { headers: { "If-Match": `"${expectedVersion}"` } }
        : undefined,
    );
    return response.data;
  },
  beginPreviewEnrichment: async (
    sessionId: string,
  ): Promise<{ accepted: boolean; reason?: string }> => {
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/begin-preview-enrichment`,
      {},
      {
        timeout: 180_000,
      },
    );
    return response.data;
  },
  discard: async (
    sessionId: string,
    expectedVersion?: number,
  ): Promise<void> => {
    await api.delete(`/populations/import-sessions/${sessionId}`, {
      ...(expectedVersion
        ? {
            headers: {
              "If-Match": `"${expectedVersion}"`,
            },
          }
        : {}),
    });
  },

  // Session-scoped context-material management (ASTOR-013)
  enterContextMaterials: async (
    sessionId: string,
    expectedVersion?: number,
  ): Promise<AudienceImportRouteAck> => {
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/context-materials/enter`,
      {},
      expectedVersion
        ? { headers: { "If-Match": `"${expectedVersion}"` } }
        : undefined,
    );
    return response.data;
  },
  completeOrSkipContextMaterials: async (
    sessionId: string,
    action: "complete" | "skip",
    expectedVersion?: number,
  ): Promise<AudienceImportRouteAck> => {
    const response = await api.patch(
      `/populations/import-sessions/${sessionId}/context-materials`,
      { action, expectedVersion },
      expectedVersion
        ? { headers: { "If-Match": `"${expectedVersion}"` } }
        : undefined,
    );
    return response.data;
  },
  uploadContextMaterialFile: async (
    sessionId: string,
    file: File,
    expectedVersion?: number,
  ): Promise<ContextMaterialsFilesResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/context-materials/files`,
      formData,
      expectedVersion
        ? { headers: { "If-Match": `"${expectedVersion}"` } }
        : undefined,
    );
    return response.data;
  },
  removeContextMaterialFile: async (
    sessionId: string,
    fileId: string,
    expectedVersion?: number,
  ): Promise<ContextMaterialsFilesResponse> => {
    const response = await api.delete(
      `/populations/import-sessions/${sessionId}/context-materials/files/${fileId}`,
      expectedVersion
        ? { headers: { "If-Match": `"${expectedVersion}"` } }
        : undefined,
    );
    return response.data;
  },
  retryContextMaterialFile: async (
    sessionId: string,
    fileId: string,
    expectedVersion?: number,
  ): Promise<ContextMaterialsFilesResponse> => {
    const response = await api.post(
      `/populations/import-sessions/${sessionId}/context-materials/files/${fileId}/retry`,
      {},
      expectedVersion
        ? { headers: { "If-Match": `"${expectedVersion}"` } }
        : undefined,
    );
    return response.data;
  },
  listContextMaterialFiles: async (
    sessionId: string,
  ): Promise<ContextMaterialsFilesResponse> => {
    const response = await api.get(
      `/populations/import-sessions/${sessionId}/context-materials/files`,
    );
    return response.data;
  },
  listContextMaterialFilesMinimal: async (
    sessionId: string,
  ): Promise<ContextMaterialsFilesListResponse> => {
    const response = await api.get(
      `/populations/import-sessions/${sessionId}/context-materials/files`,
      {
        params: {
          minimal: "true",
        },
      },
    );
    return response.data;
  },
  retryNarrativeCard: async (sessionId: string): Promise<void> => {
    await api.post(`/populations/import-sessions/${sessionId}/retry-narrative`);
  },
};

interface PersonasQueryOptions {
  page?: number;
  limit?: number;
  search?: string;
  filters?: Record<string, string[]>;
  view?: "picker" | "full";
  populationFilter?: { logic: string; conditions: Array<unknown> };
  /** D3: personas.token UUIDs for the batched image-only picker fetch (view=picker only). */
  personaIds?: Array<string>;
  /** Exact tokens for the existing full-context picker projection. */
  contextPersonaIds?: Array<string>;
}

function appendPersonasQueryParams(
  params: URLSearchParams,
  options?: PersonasQueryOptions,
): void {
  if (options?.page) params.append("page", String(options.page));
  if (options?.limit) params.append("limit", String(options.limit));
  if (options?.search) params.append("search", options.search);
  if (options?.filters && Object.keys(options.filters).length > 0) {
    params.append("filters", JSON.stringify(options.filters));
  }
  if (options?.populationFilter) {
    params.append("populationFilter", JSON.stringify(options.populationFilter));
  }
  if (options?.view) params.append("view", options.view);
  if (options?.contextPersonaIds && options.contextPersonaIds.length > 0) {
    params.append("contextPersonaIds", options.contextPersonaIds.join(","));
  }
  if (options?.personaIds && options.personaIds.length > 0) {
    params.append("personaIds", options.personaIds.join(","));
  }
}

export const populationsApi = {
  canCreate: async (): Promise<{
    allowed: boolean;
    current: number;
    limit: number;
  }> => {
    const response = await api.get("/populations/can-create");
    return response.data;
  },
  list: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<AudienceSummary[]> => {
    const response = await api.get("/populations", { params });
    const data = response.data;
    return Array.isArray(data) ? data : data.items;
  },
  listAdmin: async (): Promise<any[]> => {
    const response = await api.get("/populations/admin/all");
    return response.data;
  },
  listPaginated: async (params: {
    page: number;
    limit: number;
  }): Promise<{
    items: AudienceSummary[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await api.get("/populations", { params });
    const data = response.data;
    if (Array.isArray(data))
      return {
        items: data,
        total: data.length,
        page: params.page,
        limit: params.limit,
      };
    return data;
  },
  get: async (
    id: string,
    options?: { includePreview?: boolean },
  ): Promise<AudienceSummary> => {
    const params = options?.includePreview ? { includePreview: "true" } : {};
    const response = await api.get(`/populations/${id}`, { params });
    return response.data;
  },
  /** Slim audience payload for detail / edit-stage / legacy edit (no heavy artifact blobs). */
  getAudienceUiDetail: async (id: string): Promise<AudienceSummary> => {
    const response = await api.get(`/populations/${id}/detail-for-ui`);
    return response.data;
  },
  bookmark: async (id: string): Promise<void> => {
    await api.post(`/populations/${id}/bookmark`);
  },
  unbookmark: async (id: string): Promise<void> => {
    await api.delete(`/populations/${id}/bookmark`);
  },
  getProcessingStatus: async (
    id: string,
  ): Promise<AudienceProcessingStatus> => {
    const response = await api.get(`/populations/${id}/processing-status`);
    return response.data;
  },
  getTwinsAggregate: async (id: string): Promise<TwinsAggregateResponse> => {
    const response = await api.get(`/populations/${id}/twins-aggregate`);
    return response.data;
  },
  getRespondentContentSample: async (
    id: string,
  ): Promise<{
    personaName: string;
    personaPrompt: string;
    compressedAttributes: Record<string, unknown>;
    totalPersonas: number;
  }> => {
    const response = await api.get(
      `/populations/${id}/respondent-content-sample`,
    );
    return response.data;
  },
  adminRebuildCompactContext: async (
    id: string,
  ): Promise<{
    rebuilt: boolean;
    message: string;
    personasUpdated: number;
    elapsedMs: number;
  }> => {
    const response = await api.post(
      `/populations/${id}/admin/rebuild-compact-context`,
    );
    return response.data;
  },
  getAdmin: async (id: string) => {
    const response = await api.get(`/populations/admin/${id}`);
    return response.data;
  },
  downloadAdmin: (id: string, format: "csv" | "json" = "csv") => {
    window.open(
      `${getApiBaseUrl()}/populations/admin/${id}/download?format=${format}`,
      "_blank",
    );
  },
  delete: async (id: string) => {
    const response = await api.delete(`/populations/${id}`);
    return response.data;
  },
  update: async (
    id: string,
    data: {
      name?: string;
      description?: string;
      contextText?: string;
      aiDescription?: string;
      logoUrl?: string;
      audienceType?: "B2B" | "B2C";
      tier?: "tier1" | "tier2" | "tier3" | "standard" | "premium";
      creditsPerRespondent?: number;
      activeFilterColumns?: string[] | null;
      narrativeCard?: {
        header: string;
        subheader: string;
        bodyParagraph1: string;
        bodyParagraph2?: string | null;
        audienceProfile: Array<{ field: string; value: string }>;
        topLineInsights: Array<{ title: string; description: string }>;
        meta: {
          n: number;
          audienceType: string;
          model: string;
          generatedAt: string;
          findingType: string;
        };
      } | null;
      audienceTags?: Array<AudienceTag> | null;
      stageMetadata?: {
        tagline?: string;
        shortDescription?: string;
        subtitle?: string;
        description?: string;
        titleFont?: string;
        selectedCoverPhotoIndex?: number;
      };
    },
  ) => {
    const response = await api.patch(`/populations/${id}`, data);
    return response.data;
  },
  getColumnMetadata: async (
    id: string,
    options?: { includeAllColumns?: boolean; cacheBust?: boolean },
  ): Promise<{
    columns: {
      name: string;
      uniqueValues: string[];
      category: string;
      displayLabel: string;
      groupDisplayLabel?: string;
      canonicalId: string | null;
      isOperational?: boolean;
      type?: "multiselect";
      valueLabels?: Record<string, string>;
      queryKey?: string;
    }[];
    totalRows: number;
    groups?: {
      groupId: string;
      memberColumns: string[];
      groupType: "binary" | "multiselect";
      rawLabel: string | null;
      responseEncoding: string | null;
      rawGroupType: string | null;
      detectionRoute: string | null;
      source: "activeGroups" | "phase0";
    }[];
  }> => {
    const params: Record<string, string | number> = {};
    if (options?.includeAllColumns) {
      params.includeAllColumns = "1";
    }
    if (options?.cacheBust) {
      params._ts = Date.now();
    }
    const response = await api.get(`/populations/${id}/columns`, {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  },
  updateActiveFilterColumns: async (id: string, columns: string[]) => {
    const response = await api.post(`/populations/${id}/analysis-columns`, {
      columns,
    });
    return response.data;
  },
  updateSegmentationMappings: async (
    id: string,
    mappings: Array<SegmentationMapping>,
  ) => {
    const response = await api.post(
      `/populations/${id}/segmentation-mappings`,
      {
        mappings,
      },
    );
    return response.data;
  },
  previewFilter: async (
    id: string,
    filter: any,
    personaTokens?: Array<string>,
    signal?: AbortSignal,
  ): Promise<{ matchingCount: number; totalCount: number }> => {
    const response = await api.post(
      `/populations/${id}/filter-preview`,
      {
        filter,
        ...(personaTokens ? { personaTokens } : {}),
      },
      { ...(signal ? { signal } : {}) },
    );
    return response.data;
  },
  replaceLabels: async (
    id: string,
    labels: {
      audienceLabels?: string[];
      contentLabels?: string[];
      industryLabels?: string[];
    },
  ): Promise<void> => {
    await api.put(`/populations/${id}/labels`, labels);
  },

  /**
   * Infer demographic value mappings using LLM when auto-detect fails.
   * Sends unmapped values + row samples to the Python API for context-aware resolution.
   *
   * @see docs/sprint2-track-a-demographic-value-maps.md
   */
  inferDemographicValueMap: async (data: {
    canonicalId: string;
    sourceColumn: string;
    unmappedValues: string[];
    rowSamples: any[];
    allColumnNames: string[];
  }): Promise<{ suggestedMappings: Record<string, string> }> => {
    const response = await api.post(
      "/populations/infer-demographic-value-map",
      data,
    );
    return response.data;
  },

  // Population-scoped context-material file management (ASTOR-013)
  listContextMaterialFiles: async (
    populationId: string,
  ): Promise<PopulationContextMaterialsFilesResponse> => {
    const response = await api.get(
      `/populations/${populationId}/context-materials/files`,
    );
    return response.data;
  },
  uploadContextMaterialFile: async (
    populationId: string,
    file: File,
  ): Promise<PopulationContextMaterialsFilesResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `/populations/${populationId}/context-materials/files`,
      formData,
    );
    return response.data;
  },
  removeContextMaterialFile: async (
    populationId: string,
    fileId: string,
  ): Promise<PopulationContextMaterialsFilesResponse> => {
    const response = await api.delete(
      `/populations/${populationId}/context-materials/files/${fileId}`,
    );
    return response.data;
  },

  requestCoverage: async (body: {
    taxonomyPath?: string;
    countries?: string[];
    audienceType?: string;
  }): Promise<void> => {
    await api.post("/populations/coverage-request", body);
  },

  reprocess: async (id: string) => {
    const response = await api.post(`/populations/${id}/reprocess`);
    return response.data;
  },

  forceReprocess: async (id: string) => {
    const response = await api.post(`/populations/${id}/admin/force-reprocess`);
    return response.data;
  },

  toggleGlobal: async (id: string) => {
    const response = await api.post(`/populations/${id}/toggle-global`);
    return response.data;
  },

  // Cover Photos
  generateCoverPhotos: async (
    id: string,
  ): Promise<{
    coverPhotoOptions: Array<{ url: string; thumbnailUrl: string }>;
  }> => {
    const response = await api.post(`/populations/${id}/generate-cover-photos`);
    return response.data;
  },

  // Sharing
  getSharing: async (
    id: string,
  ): Promise<{
    sharingMode: string;
    guestChatEnabled?: boolean;
    guestChatCap?: number;
    collaborators: Array<{
      id: string;
      userId: string;
      role: string;
      name: string | null;
      email: string | null;
      createdAt: string;
    }>;
  }> => {
    const response = await api.get(`/populations/${id}/sharing`);
    return response.data;
  },
  updateSharing: async (
    id: string,
    data: {
      sharingMode:
        | "private"
        | "shared"
        | "organization"
        | "marketplace"
        | "published";
    },
  ) => {
    const response = await api.patch(`/populations/${id}/sharing`, data);
    return response.data;
  },
  getGuestChat: async (
    id: string,
  ): Promise<{ guestChatEnabled: boolean; guestChatCap: number }> => {
    const response = await api.get(`/populations/${id}/sharing/guest-chat`);
    return response.data;
  },
  setGuestChat: async (id: string, enabled: boolean, cap: number) => {
    const response = await api.patch(`/populations/${id}/sharing/guest-chat`, {
      enabled,
      cap,
    });
    return response.data;
  },
  updateMarketplaceStatus: async (
    id: string,
    status: "published" | "inactive",
  ) => {
    const response = await api.patch(`/populations/${id}/marketplace-status`, {
      status,
    });
    return response.data;
  },
  getMarketplaceCreditCost: async (
    id: string,
  ): Promise<{ credits: number | null }> => {
    const response = await api.get(
      `/populations/${id}/marketplace-credit-cost`,
    );
    return response.data;
  },
  addCollaborator: async (
    id: string,
    userId: string,
    role?: "collaborator" | "manager",
  ) => {
    const response = await api.post(
      `/populations/${id}/sharing/collaborators`,
      { userId, ...(role ? { role } : {}) },
    );
    return response.data;
  },
  updateCollaboratorRole: async (
    id: string,
    collaboratorId: string,
    role: "collaborator" | "manager",
  ) => {
    const response = await api.patch(
      `/populations/${id}/sharing/collaborators/${collaboratorId}/role`,
      { role },
    );
    return response.data;
  },
  setOrgGrantScope: async (
    id: string,
    scope: "org" | "specific" | "revoked",
  ) => {
    const response = await api.patch(
      `/populations/${id}/sharing/org-grant`,
      { scope },
    );
    return response.data;
  },
  listOrgGrantMembers: async (
    id: string,
  ): Promise<Array<{ id: string; name: string | null; email: string | null; avatarSeed: string | null }>> => {
    const response = await api.get(`/populations/${id}/sharing/org-grant/members`);
    return response.data;
  },
  addOrgGrantMember: async (id: string, userId: string): Promise<void> => {
    await api.post(`/populations/${id}/sharing/org-grant/members`, { userId });
  },
  removeOrgGrantMember: async (id: string, userId: string): Promise<void> => {
    await api.delete(`/populations/${id}/sharing/org-grant/members/${userId}`);
  },
  removeCollaborator: async (id: string, collaboratorId: string) => {
    const response = await api.delete(
      `/populations/${id}/sharing/collaborators/${collaboratorId}`,
    );
    return response.data;
  },

  // External invite management (published sharing mode)
  listInvites: async (
    audienceId: string,
  ): Promise<Array<AudienceExternalInvite>> => {
    const response = await api.get(
      `/populations/${audienceId}/sharing/invites`,
    );
    return response.data;
  },
  createInvite: async (
    audienceId: string,
    email: string,
  ): Promise<AudienceExternalInvite> => {
    const response = await api.post(
      `/populations/${audienceId}/sharing/invites`,
      { email },
    );
    return response.data;
  },
  revokeInvite: async (audienceId: string, inviteId: string): Promise<void> => {
    await api.delete(`/populations/${audienceId}/sharing/invites/${inviteId}`);
  },
  dismissInvite: async (audienceId: string, inviteId: string): Promise<void> => {
    await api.delete(`/populations/${audienceId}/sharing/invites/${inviteId}/dismiss`);
  },
  resendInvite: async (
    audienceId: string,
    inviteId: string,
  ): Promise<AudienceExternalInvite> => {
    const response = await api.post(
      `/populations/${audienceId}/sharing/invites/${inviteId}/resend`,
    );
    return response.data;
  },
  leaveSharedAudience: async (audienceId: string): Promise<void> => {
    await api.delete(`/populations/${audienceId}/sharing/collaborators/me`);
  },
  updateOwnership: async (
    audienceId: string,
    params: AudienceOwnershipUpdateParams,
  ) => {
    const response = await api.patch(`/populations/${audienceId}`, params);
    return response.data;
  },

  // Featured Twins
  getPersonasForPicker: async (
    id: string,
    options?: Omit<PersonasQueryOptions, "view">,
  ) => {
    const params = new URLSearchParams();
    appendPersonasQueryParams(params, { ...options, view: "picker" });
    const queryString = params.toString();
    const response = await api.get(
      `/populations/${id}/personas${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },
  getPersonas: async (
    id: string,
    options?: PersonasQueryOptions,
  ): Promise<{
    personas: Array<{
      personaId: string;
      personaName: string;
      personaPrompt: string;
      attributes: Record<string, any>;
      normalizedAttributes?: Record<string, any>;
      imageUrl?: string;
      isProfileComplete?: boolean;
      createdAt?: string;
      resolvedIdentity?: {
        display_name: string;
        age_range: string;
        country: string;
        gender: string;
        ethnicity: string;
      } | null;
      personaNarrative?: string | null;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> => {
    const params = new URLSearchParams();
    appendPersonasQueryParams(params, options);
    const queryString = params.toString();
    const response = await api.get(
      `/populations/${id}/personas${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },
  getRandomPersonaIds: async (
    id: string,
    count: number,
    filters?: Record<string, string[]>,
    exclude?: string[],
  ): Promise<{ personaIds: string[] }> => {
    const params = new URLSearchParams();
    params.append("count", String(count));
    if (filters && Object.keys(filters).length > 0) {
      params.append("filters", JSON.stringify(filters));
    }
    if (exclude && exclude.length > 0) {
      params.append("exclude", exclude.join(","));
    }
    const response = await api.get(
      `/populations/${id}/personas/random?${params.toString()}`,
    );
    return response.data;
  },
  getPersonasAdmin: async (
    id: string,
    options?: PersonasQueryOptions,
  ): Promise<{
    personas: Array<{
      personaId: string;
      personaName: string;
      personaPrompt: string;
      attributes: Record<string, any>;
      imageUrl?: string;
      isProfileComplete?: boolean;
      createdAt?: string;
      resolvedIdentity?: {
        display_name: string;
        age_range: string;
        country: string;
        gender: string;
        ethnicity: string;
      } | null;
      personaNarrative?: string | null;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> => {
    const params = new URLSearchParams();
    appendPersonasQueryParams(params, options);
    const queryString = params.toString();
    const response = await api.get(
      `/populations/admin/${id}/personas${queryString ? `?${queryString}` : ""}`,
    );
    return response.data;
  },
  getPersonaDebug: async (
    populationId: string,
    personaId: string,
  ): Promise<{
    personaId: string;
    personaName: string;
    personaPrompt: string;
    normalizedAttributes: Record<string, any>;
    runtimeAttributes: Record<string, any>;
    compressedAttributes: Record<string, any>;
    resolvedIdentity: Record<string, any> | null;
    compressedPersonaSchemaVersion: Record<string, any> | null;
    createdAt: string;
  }> => {
    const response = await api.get(
      `/populations/${populationId}/personas/${personaId}/debug`,
    );
    return response.data;
  },
  getPersonaDebugAdmin: async (
    populationId: string,
    personaId: string,
  ): Promise<{
    personaId: string;
    personaName: string;
    personaPrompt: string;
    normalizedAttributes: Record<string, any>;
    runtimeAttributes: Record<string, any>;
    compressedAttributes: Record<string, any>;
    resolvedIdentity: Record<string, any> | null;
    compressedPersonaSchemaVersion: Record<string, any> | null;
    createdAt: string;
  }> => {
    const response = await api.get(
      `/populations/admin/${populationId}/personas/${personaId}/debug`,
    );
    return response.data;
  },
  getQaPreflightAdmin: async (
    id: string,
  ): Promise<{
    qaPreflightResults: {
      status: string;
      labelRenames: Record<string, string>;
      keyBlocklist: string[];
      gridRestructureLabels: string[];
      correctionsCount: number;
      llmLatencySeconds: number;
      sampleCount: number;
      error: string | null;
    } | null;
  }> => {
    const response = await api.get(`/populations/admin/${id}/qa-preflight`);
    return response.data;
  },
  getFeaturedTwins: async (
    id: string,
  ): Promise<Array<FeaturedTwinListItem>> => {
    const response = await api.get(`/populations/${id}/featured-twins`);
    return response.data;
  },
  getFeaturedTwinsAdmin: async (
    id: string,
  ): Promise<Array<FeaturedTwinListItem>> => {
    const response = await api.get(`/populations/admin/${id}/featured-twins`);
    return response.data;
  },
  getAllFeaturedTwins: async (): Promise<
    Array<{
      personaId: string;
      personaName: string;
      imageId: string;
      imageUrl: string;
      demographics: Record<string, any>;
      personaAttributes: Record<string, any>;
      compressedAttributes?: Record<string, any>;
      columnCategories?: Record<string, string> | null;
      aiTags?: string[] | null;
      populationId: string;
      populationName: string;
    }>
  > => {
    const response = await api.get("/populations/featured-twins/all");
    return response.data;
  },
  getUserFeaturedTwins: async (): Promise<FeaturedTwin[]> => {
    const response = await api.get("/populations/user-featured-twins");
    return response.data;
  },
  addFeaturedTwins: async (
    populationId: string,
    personaIds: string[],
  ): Promise<FeaturedTwin[]> => {
    const response = await api.post(
      "/populations/add-featured-twins",
      { populationId, personaIds },
      { timeout: 180000 }, // 3 minutes for bulk operations
    );
    return response.data;
  },
  addFeaturedTwinSingle: async (
    populationId: string,
    personaId: string,
  ): Promise<FeaturedTwin> => {
    const response = await api.post(
      "/populations/add-featured-twins",
      { populationId, personaIds: [personaId] },
      { timeout: 90000 }, // 90 seconds for single twin
    );
    return response.data[0];
  },
  autoSelectFeaturedTwins: async (data: {
    targetingType: "B2B" | "B2C";
    targetingPreferences: {
      roleFamilies?: string[];
      lifestyleSegments?: string[];
      race?: string[];
      ageRange?: { min: number; max: number };
      annualIncomeRange?: { min: number; max: number };
    };
  }): Promise<
    Array<{
      personaId: string;
      personaName: string;
      imageId: string;
      imageUrl: string;
      demographics?: Record<string, any>;
      personaAttributes?: Record<string, any>;
      personaSummary?: string;
      personaPrompt?: string;
      enrichedProfile?: Record<string, string>;
      voiceProfile?: Record<string, string>;
      populationId: string;
      populationName: string;
    }>
  > => {
    const response = await api.post(
      "/populations/auto-select-featured-twins",
      data,
    );
    return response.data;
  },
  getRandomPortraitUrls: async (count = 16): Promise<string[]> => {
    const response = await api.get("/populations/random-portrait-urls", {
      params: { count },
    });
    return response.data.urls ?? [];
  },
};

// ============================================================================
// Featured Twin Chat API
// ============================================================================

/** Profile card for the twin sidebar — behavioral portrait + conversation starters.
 *  Generated by LLM at save time. Display-only — does NOT affect chat behavior. */
export interface ProfileCard {
  bio?: string;
  highlights?: Array<string>;
  tagline?: string;
  conversation_starters?: Array<string>;
}

/** Slim population GET list item (`GET /populations/:id/featured-twins`). */
export interface FeaturedTwinListItem {
  personaId: string;
  personaName: string;
  imageId?: string;
  imageUrl?: string;
  personaSummary?: string;
  personaTagline?: string | null;
  demographics?: Record<string, string>;
  audienceTagNames?: Array<string>;
  profileCard?: ProfileCard | null;
}

/** Slim population GET detail (`GET /populations/:id/featured-twins/:personaId`). */
export interface FeaturedTwinDetail extends FeaturedTwinListItem {
  populationId?: string;
  populationName?: string;
  profileCard?: ProfileCard | null;
}

export interface FeaturedTwin {
  personaId: string;
  personaName: string;
  imageId: string;
  imageUrl: string;
  demographics?: Record<string, any>;
  personaAttributes?: Record<string, any>;
  compressedAttributes?: Record<string, any>;
  personaSummary?: string;
  personaTagline?: string; // Short attribution (e.g., "HR Director at Acme")
  personaPrompt?: string;
  populationId?: string;
  populationName?: string;
  populationStatus?: string;
  enrichedProfile?: Record<string, string>; // Slot-based enrichment items (30-50 items)
  voiceProfile?: {
    // Voice style characteristics
    formality: string;
    verbosity: string;
    typo_rate: string;
    slang_level: string;
    certainty_style: string;
    punctuation: string;
  };
  profileCard?: ProfileCard; // Behavioral portrait + starters (per-twin, display-only)
  dataCoverage?: string[]; // Research categories covered by this audience's data (population-level)
  columnCategories?: Record<string, string> | null; // Attribute coverage map for topic knowledge
  /** @deprecated Use contentLabels instead. Kept for backward compatibility. */
  aiTags?: string[] | null; // Audience-level tags for topic knowledge
  /** IAB Audience Taxonomy 1.1 labels classifying this twin's audience demographic and interest segments (population-level). */
  audienceLabels?: TaxonomyLabel[] | null;
  /** IAB Content Taxonomy 3.1 labels classifying the research domains and content areas this twin's data covers (population-level). */
  contentLabels?: TaxonomyLabel[] | null;
  titleFont?: string | null; // Display font chosen during audience wizard (population-level)
  pinned?: boolean; // User-bookmarked twin (persists position vs auto-rotated)
  audienceTagNames?: Array<string>;
}

/** Encodes each path segment of a room material key, keeping the slashes the route joins. */
export function encodeMaterialKey(key: string): string {
  return key.split("/").map((segment) => encodeURIComponent(segment)).join("/");
}

export interface ChatImage {
  base64: string;
  mimeType: string;
  /**
   * Step 10c: the original PDF this tile was rendered from (room composer only).
   * Sent as the message's `documents` entry; never part of the images payload.
   */
  sourceDocument?: { base64: string; name: string; mimeType: 'application/pdf' };
  /** Hosted public URL after upload. Required before send/bind for thread stimuli. */
  url?: string;
  /** Storage object key from the hosting upload. */
  objectKey?: string;
  /** `files.token` from POST /files/upload/questionnaire-concept-image. */
  fileId?: string;
  /** Optional user-facing name for the creative. */
  label?: string;
  /** Optional per-image note (creative context for the study). */
  note?: string;
  /** Hosting upload failed or timed out. Tile stays for dismiss; send gates must not treat it as in-flight. */
  hostFailed?: boolean;
  /** Thread-asset register POST failed after retries. Tile stays for dismiss. */
  registerFailed?: boolean;
  /**
   * Room composer only: this image is a landing-page capture, so the backend
   * slices it as studies slice a page. Absent for every other image.
   */
  presentation?: 'landing_page';
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  images?: Array<ChatImage>;
}

/**
 * Tracked knowledge level for a topic discussed in conversation.
 * Persisted across chat messages to maintain consistent persona knowledge.
 */
export interface TopicKnowledge {
  kind?: string; // Assessed entity kind; echoed back as received
  topic: string; // The topic being discussed (e.g., "PSG", "machine learning")
  level: number; // Prior familiarity: 0, 0.25, 0.5, 0.75, or 1.0
  presented?: boolean; // The conversation put it in front of the twin; echoed back as received
  reviewed_in_study?: boolean; // This person reviewed it in the study; echoed back as received
}

export interface TwinChatResponse {
  response: string;
  topicKnowledge: TopicKnowledge[]; // Updated knowledge levels to persist in frontend state
  error?: string;
}

export const twinsApi = {
  /**
   * Get a specific featured twin by population and persona ID
   */
  getFeaturedTwin: async (
    populationId: string,
    personaId: string,
    signal?: AbortSignal,
  ): Promise<FeaturedTwinDetail> => {
    const response = await api.get(
      `/populations/${populationId}/featured-twins/${personaId}`,
      signal ? { signal } : undefined,
    );
    return response.data;
  },

  /**
   * Chat with a featured twin.
   * Uses two-call knowledge assessment to make responses realistic.
   *
   * @param topicKnowledge - Previously assessed topic knowledge (pass back from previous response)
   */
  chat: async (
    populationId: string,
    personaId: string,
    message: string,
    conversationHistory: ChatMessage[] = [],
    images?: Array<ChatImage>,
    topicKnowledge: TopicKnowledge[] = [],
    signal?: AbortSignal,
  ): Promise<TwinChatResponse> => {
    const response = await api.post(
      `/populations/${populationId}/featured-twins/${personaId}/chat`,
      { message, conversationHistory, images, topicKnowledge },
      signal ? { signal } : undefined,
    );
    return response.data;
  },
};

// ============================================================================
// Company Profiles API (Audience Marketplace)
// ============================================================================

export interface CompanyProfile {
  domain: string;
  name: string;
  logoUrl: string | null;
  description: string | null;
  badge: "verified" | "partner" | null;
  brandColors: { primary: string | null; secondary: string | null } | null;
  fetchedAt: string;
  audienceCount: number;
}

// ============================================================================
// Taxonomy API (Content & Audience taxonomy nodes)
// ============================================================================

export interface TaxonomyNodeDto {
  token: string;
  sourceId: string | null;
  tier: number;
  name: string;
  fullPath: string;
  isCustom: boolean;
  hasChildren: boolean;
  /** Audience segment filter: 'b2b', 'b2c', 'both', or null (applies to all) */
  segment: string | null;
}

export const taxonomyApi = {
  getNodes: async (
    type: "audience" | "content" | "industry",
    params?: { tier?: number; parentToken?: string },
  ): Promise<{ nodes: Array<TaxonomyNodeDto> }> => {
    const response = await api.get(`/taxonomy/${type}/nodes`, { params });
    return response.data;
  },
  searchNodes: async (
    type: "audience" | "content" | "industry",
    q: string,
    limit?: number,
  ): Promise<{ nodes: Array<TaxonomyNodeDto> }> => {
    const response = await api.get(`/taxonomy/${type}/nodes/search`, {
      params: { q, limit },
    });
    return response.data;
  },
  resolveNodes: async (
    type: "audience" | "content" | "industry",
    tokens: Array<string>,
  ): Promise<{ nodes: Array<TaxonomyNodeDto> }> => {
    const response = await api.post(`/taxonomy/${type}/nodes/resolve`, {
      tokens,
    });
    return response.data;
  },
  resolveCountries: async (
    query: string,
  ): Promise<Array<{ code: string; name: string }>> => {
    const response = await api.post("/taxonomy/audience/resolve-countries", {
      query,
    });
    return response.data.countries;
  },
  resolveFilters: async (
    query: string,
  ): Promise<{
    filters: {
      categoryNames: Array<string>;
      countryCodes: Array<string>;
      audienceType: "B2B" | "B2C" | null;
    } | null;
    rejected: boolean;
    rejectionReason?: string;
  }> => {
    const response = await api.post("/taxonomy/audience/resolve-filters", {
      query,
    });
    return response.data;
  },
};

export const companyProfilesApi = {
  list: async (): Promise<CompanyProfile[]> => {
    const response = await api.get("/company-profiles");
    return response.data;
  },
};

// ============================================================================
// Deck API (Executive Deck Generation)
// ============================================================================

import type { Deck } from "@digital-twins/deck-ui/slides/contracts";

export const deckApi = {
  /**
   * Get deck for an experiment
   */
  getDeck: async (experimentId: string): Promise<Deck> => {
    const response = await api.get(`/experiments/${experimentId}/deck`);
    return response.data;
  },

  /**
   * Persist user edits to deck JSON (saved into experiment.deckJson).
   */
  updateDeck: async (
    experimentId: string,
    deckJson: Deck,
  ): Promise<{ success: boolean }> => {
    const response = await api.put(`/experiments/${experimentId}/deck`, {
      deck_json: deckJson,
    });
    return response.data;
  },

  revertDeck: async (
    experimentId: string,
  ): Promise<{ success: boolean; deck: Deck }> => {
    const response = await api.post(`/experiments/${experimentId}/deck/revert`);
    return response.data;
  },

  getChartSegmentMeta: async (
    experimentId: string,
    signal?: AbortSignal,
  ) => {
    const response = await api.get(
      `/experiments/${experimentId}/deck/chart-segment-meta`,
      { signal },
    );
    return response.data;
  },

  getChartSegmentView: async (
    experimentId: string,
    slideId: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
  ) => {
    const response = await api.post(
      `/experiments/${experimentId}/deck/slides/${slideId}/chart-segment-view`,
      body,
      { signal },
    );
    return response.data;
  },
};

// ============================================================================
// Chat Rooms API
// ============================================================================

import {
  ChatRoomDto,
  ChatRoomListItemDto,
  ChatRoomMessageDto,
  ChatRoomSettings,
  CreateChatRoomDto,
  SendChatRoomMessageDto,
  ConversationHistoryEntry,
  RecentConversationsPage,
  RecordRecentOpenRequest,
  SetRecentPinRequest,
} from "@digital-twins/shared-types";

// ============================================================================
// Audience Invites API (cross-org audience sharing)
// ============================================================================

export type {
  AudienceInviteStatus,
  AudienceSharedAs,
  AudienceInviteInfo,
} from "@/types/components/audiences/audience-sharing";
import type { AudienceInviteInfo } from "@/types/components/audiences/audience-sharing";

export const audienceInvitesApi = {
  getInfo: async (token: string): Promise<AudienceInviteInfo> => {
    const response = await api.get(`/populations/invites/${token}`);
    return response.data;
  },

  accept: async (
    token: string,
  ): Promise<{ populationId: string; name: string }> => {
    const response = await api.post(`/populations/invites/${token}/accept`);
    return response.data;
  },
};

/** A questionnaire asset file the backend stored and issued (its `files` row). */
export interface IssuedAssetFile {
  token: string;
  publicUrl: string;
  filePath: string;
  spacesUri: string;
  mimeType: string;
  originalFilename: string;
  fileSize: number;
  /** A PDF's page 1, rendered by the backend and issued as an image file. */
  preview?: IssuedAssetFile;
}

export interface UrlCaptureResult {
  ok: boolean;
  failureReason?: string | null;
  detail?: string | null;
  publicUrl?: string | null;
  bucket?: string | null;
  objectKey?: string | null;
  gcsUri?: string | null;
  screenshotBase64?: string | null;
  pageTitle?: string | null;
  extractedText?: string | null;
  width?: number;
  height?: number;
  /** The builder's capture, stored as an asset file (`issueAssetFile`). */
  file?: IssuedAssetFile;
  /**
   * Set when the dialog attached a video instead of a page screenshot:
   * "youtube" for a watch URL, "video" for a direct video file URL.
   */
  source?: "youtube" | "page" | "video";
}

export const mediaApi = {
  /** Capture a landing-page URL server-side and return a previewable screenshot. */
  captureUrl: async (
    url: string,
    opts?: { scale?: number; fullPage?: boolean },
  ): Promise<UrlCaptureResult> => {
    const response = await api.post('/media/capture-url', {
      url,
      ...(opts?.scale != null && { scale: opts.scale }),
      ...(opts?.fullPage != null && { fullPage: opts.fullPage }),
    });
    return response.data;
  },
};

export const chatRoomsApi = {
  create: async (dto: CreateChatRoomDto): Promise<ChatRoomDto> => {
    const response = await api.post("/chat-rooms", dto);
    return response.data;
  },

  list: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<Array<ChatRoomListItemDto>> => {
    const response = await api.get("/chat-rooms", { params });
    return response.data;
  },

  getConversationHistory: async (params?: {
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<{
    items: Array<ConversationHistoryEntry>;
    total: number;
  }> => {
    const response = await api.get("/chat-rooms/history", { params });
    return response.data;
  },

  get: async (token: string): Promise<ChatRoomDto> => {
    const response = await api.get(`/chat-rooms/${token}`);
    return response.data;
  },

  getMessages: async (
    token: string,
    params?: { limit?: number; offset?: number },
  ): Promise<Array<ChatRoomMessageDto>> => {
    const response = await api.get(`/chat-rooms/${token}/messages`, { params });
    return response.data;
  },

  sendMessage: async (
    token: string,
    dto: SendChatRoomMessageDto,
  ): Promise<{ success: boolean; message?: ChatRoomMessageDto }> => {
    const response = await api.post(`/chat-rooms/${token}/message`, dto);
    return response.data;
  },

  /** Fire-and-forget: the twins start watching a video the user just hosted in this room. */
  prewatchVideo: async (token: string, videoUrl: string): Promise<void> => {
    await api.post(`/chat-rooms/${token}/videos/prewatch`, { videoUrl });
  },

  /** C13: the room's resolved reactions snapshot (same shape as the socket event). */
  getReactions: async (token: string): Promise<import("@digital-twins/shared-types").ChatRoomReactionsEvent> => {
    const response = await api.get(`/chat-rooms/${token}/reactions`);
    return response.data;
  },

  close: async (token: string): Promise<{ success: boolean }> => {
    const response = await api.post(`/chat-rooms/${token}/close`);
    return response.data;
  },

  delete: async (token: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/chat-rooms/${token}`);
    return response.data;
  },

  getImageUrl(token: string, key: string): string {
    return `/api/chat-rooms/${token}/images/${key}`;
  },

  /** Step 10c: one room material (`key` is a storage key or `stimulus:<n>`). */
  getMaterialUrl(token: string, key: string): string {
    return `/api/chat-rooms/${token}/materials/${encodeMaterialKey(key)}`;
  },

  getTranscriptUrl(token: string): string {
    return `/api/chat-rooms/${token}/transcript`;
  },

  updateSettings: async (
    token: string,
    settings: Partial<ChatRoomSettings>,
  ): Promise<ChatRoomDto> => {
    const response = await api.patch(`/chat-rooms/${token}/settings`, {
      settings,
    });
    return response.data;
  },

  // RND-2580: manual rename. Persists the title and locks it against auto-gen.
  updateTitle: async (
    token: string,
    title: string,
  ): Promise<{ title: string }> => {
    const response = await api.patch(`/chat-rooms/${token}/title`, { title });
    return response.data;
  },

  getShareStatus: async (
    token: string,
  ): Promise<{
    isShared: boolean;
    shareToken: string | null;
    shareUrl: string | null;
    shareCreatedAt: string | null;
  }> => {
    const response = await api.get(`/chat-rooms/${token}/share`);
    return response.data;
  },

  enableSharing: async (
    token: string,
  ): Promise<{ shareToken: string; shareUrl: string }> => {
    const response = await api.post(`/chat-rooms/${token}/share`);
    return response.data;
  },

  disableSharing: async (token: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/chat-rooms/${token}/share`);
    return response.data;
  },

  regenerateShareToken: async (
    token: string,
  ): Promise<{ shareToken: string; shareUrl: string }> => {
    const response = await api.post(`/chat-rooms/${token}/share/regenerate`);
    return response.data;
  },

  ensureShareToken: async (
    token: string,
  ): Promise<{ shareToken: string; shareUrl: string }> => {
    const response = await api.post(`/chat-rooms/${token}/ensure-share-token`);
    return response.data;
  },

  setSharingMode: async (
    token: string,
    mode: "private" | "shared" | "organization",
  ): Promise<{ success: boolean }> => {
    const response = await api.put(`/chat-rooms/${token}/sharing-mode`, {
      mode,
    });
    return response.data;
  },

  getCollaborators: async (
    token: string,
  ): Promise<{
    sharingMode: string;
    collaborators: Array<any>;
    guests: Array<any>;
  }> => {
    const response = await api.get(`/chat-rooms/${token}/collaborators`);
    return response.data;
  },

  addCollaborator: async (token: string, userId: string): Promise<any> => {
    const response = await api.post(`/chat-rooms/${token}/collaborators`, {
      userId,
    });
    return response.data;
  },

  removeCollaborator: async (
    token: string,
    collaboratorId: string,
  ): Promise<any> => {
    const response = await api.delete(
      `/chat-rooms/${token}/collaborators/${collaboratorId}`,
    );
    return response.data;
  },

  addGuest: async (token: string, email: string): Promise<any> => {
    const response = await api.post(`/chat-rooms/${token}/guests`, { email });
    return response.data;
  },

  removeGuest: async (token: string, guestId: string): Promise<any> => {
    const response = await api.delete(`/chat-rooms/${token}/guests/${guestId}`);
    return response.data;
  },
};

/**
 * Sidebar Recent rail read model: knowledge threads and started chat rooms in
 * one page, merged and ordered server side (pinned rows first, then
 * recency). `cursor` is opaque; pass back the previous page's `nextCursor`.
 */
export const recentConversationsApi = {
  listPage: async (params: {
    limit: number;
    cursor?: string | null;
  }): Promise<RecentConversationsPage> => {
    const response = await api.get("/recent-conversations", {
      params: {
        limit: params.limit,
        ...(params.cursor ? { cursor: params.cursor } : {}),
      },
    });
    return response.data;
  },
  /** Records that the caller opened an item: 204, or 404 when it is not visible. */
  recordOpen: async (body: RecordRecentOpenRequest): Promise<void> => {
    await api.post("/recent-conversations/open", body);
  },
  /** Pins or unpins a room, experiment or draft: 204, or 404 when it is not visible. */
  setPinned: async (body: SetRecentPinRequest): Promise<void> => {
    await api.post("/recent-conversations/pin", body);
  },
};

/**
 * Skill catalog + optional org / personal instruction overlays
 * (docs/plans/skills-org-personal-overrides.md).
 *
 * Reads: `GET /skills` is `SkillSummary` (no body); `GET /skills/:token`
 * returns the merged `instructions` (personal → org → seed) plus optional
 * overlay fields when `skillsCustomization` is on.
 *
 * Writes (flag-gated on the server): instructions only. Name, slug, and
 * description stay seed-owned. Empty overlay inherits the next layer.
 * Reset DELETE may return 204 or the refreshed Skill.
 */
export const skillsApi = {
  list: async (): Promise<Array<SkillSummary>> => {
    const response = await api.get("/skills");
    return response.data;
  },

  get: async (token: string): Promise<Skill> => {
    const response = await api.get(`/skills/${token}`);
    return response.data;
  },

  putOrg: async (
    token: string,
    dto: { instructions: string },
  ): Promise<Skill> => {
    const response = await api.put(`/skills/${token}`, dto);
    return response.data;
  },

  putMe: async (
    token: string,
    dto: { instructions: string },
  ): Promise<Skill> => {
    const response = await api.put(`/skills/${token}/me`, dto);
    return response.data;
  },

  resetOrg: async (token: string): Promise<Skill | void> => {
    const response = await api.delete(`/skills/${token}`);
    return skillFromDeleteResponse(response);
  },

  resetMe: async (token: string): Promise<Skill | void> => {
    const response = await api.delete(`/skills/${token}/me`);
    return skillFromDeleteResponse(response);
  },
};

function skillFromDeleteResponse(response: {
  status: number;
  data: unknown;
}): Skill | void {
  if (response.status === 204) {
    return;
  }
  if (response.data && typeof response.data === "object") {
    return response.data as Skill;
  }
}

/**
 * Org default + current-user overlay for the insights beat
 * (brand / product / category / region / notes). Missing rows return empty
 * strings. PUT accepts a full form; the server also accepts partials.
 */
export const insightScopeApi = {
  getOrg: async (): Promise<InsightScope> => {
    const response = await api.get("/insight-scope");
    return response.data;
  },

  putOrg: async (dto: InsightScope): Promise<InsightScope> => {
    const response = await api.put("/insight-scope", dto);
    return response.data;
  },

  getMe: async (): Promise<InsightScope> => {
    const response = await api.get("/insight-scope/me");
    return response.data;
  },

  putMe: async (dto: InsightScope): Promise<InsightScope> => {
    const response = await api.put("/insight-scope/me", dto);
    return response.data;
  },
};

import type {
  WorkflowSchedule,
  WorkflowScheduleCadence,
} from "@digital-twins/shared-types";

/**
 * Recurring workflow starts (Assistant Platform v1 Step 6.4), backing the
 * workflow detail page's schedule card. Reads are organization-wide; writes
 * 403 for anyone but the schedule's creator, so the card renders read-only
 * off the wire's `viewerOwnsSchedule` rather than guessing from a role.
 *
 * No `inputs` in either payload (Step 6 of
 * docs/plans/workflow-input-settings-plan.md): a scheduled run resolves the
 * org's saved inputs at fire time, through `workflowTemplateSettingsApi`. The
 * server refuses an `inputs` field; its vestigial column is written empty.
 */
export const workflowSchedulesApi = {
  list: async (templateToken?: string): Promise<Array<WorkflowSchedule>> => {
    const response = await api.get("/workflow-schedules", {
      params: templateToken ? { templateToken } : undefined,
    });
    return response.data;
  },

  create: async (dto: {
    templateToken: string;
    cadence: WorkflowScheduleCadence;
    hourUtc: number;
    enabled?: boolean;
    autoApprove?: boolean;
  }): Promise<WorkflowSchedule> => {
    const response = await api.post("/workflow-schedules", dto);
    return response.data;
  },

  get: async (token: string): Promise<WorkflowSchedule> => {
    const response = await api.get(`/workflow-schedules/${token}`);
    return response.data;
  },

  update: async (
    token: string,
    dto: {
      cadence?: WorkflowScheduleCadence;
      hourUtc?: number;
      enabled?: boolean;
      autoApprove?: boolean;
    },
  ): Promise<WorkflowSchedule> => {
    const response = await api.patch(`/workflow-schedules/${token}`, dto);
    return response.data;
  },

  delete: async (token: string): Promise<void> => {
    await api.delete(`/workflow-schedules/${token}`);
  },
};

// ============================================================================
// Workflow templates + runs (Assistant Platform v1 Phase 2, Steps A / B / G)
// ============================================================================

import {
  AssistantCitation,
  AssistantContextUsage,
  AssistantEffort,
  AssistantPreparedRun,
  PopulationFilter,
  WorkflowGateDecision,
  WorkflowInputValues,
  WorkflowRun,
  WorkflowRunTrigger,
  WorkflowStepOutput,
  UpdateWorkflowStepInstructionsPayload,
  WorkflowTemplateSettings,
  WorkflowTemplate,
  WorkflowTemplateSummary,
} from "@digital-twins/shared-types";

/**
 * Read-only workflow template registry (`WorkflowsController`), behind
 * `JwtAuthGuard` and the `ASSISTANT_PLATFORM` flag.
 *
 * `getTemplate` takes the template TOKEN, never the slug: the route param is
 * `@IsUUID()` (`WorkflowTemplateTokenParamDto`), so a slug is a 400. The
 * slug-keyed surface resolves through `listTemplates` first and then calls
 * this with `summary.token`.
 */
export const workflowsApi = {
  /**
   * `includeInactive` crosses the wire as the literal string `"true"`, never a
   * boolean: `ListWorkflowTemplatesQueryDto` is `@IsIn(['true','false'])`
   * precisely because the global ValidationPipe's implicit conversion turns the
   * string `"false"` into boolean `true`. Omitted entirely when not asked for.
   */
  listTemplates: async (
    includeInactive?: boolean,
    /**
     * Reveals the in-thread ACTION templates the lobby hides by slug prefix
     * (`thread-action-*`). Only the thread chip row asks for them: it resolves
     * its three templates off this list and has no other way to learn their
     * tokens. Same literal-string treatment as `includeInactive`, and omitted
     * entirely when not asked for, so every existing call site is byte-identical
     * on the wire.
     */
    includeThreadActions?: boolean,
  ): Promise<Array<WorkflowTemplateSummary>> => {
    const params = {
      ...(includeInactive ? { includeInactive: "true" } : {}),
      ...(includeThreadActions ? { includeThreadActions: "true" } : {}),
    };
    const response = await api.get("/workflows", {
      params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
  },

  getTemplate: async (token: string): Promise<WorkflowTemplate> => {
    const response = await api.get(`/workflows/${token}`);
    return response.data;
  },

  /**
   * Plan Start / registry resolve: slug → summary (includes `token`).
   * Capability registry JSON never carries tokens; this recovers them only at
   * confirm/start time. 404 when inactive or invisible to the org.
   */
  resolveTemplateBySlug: async (
    slug: string,
  ): Promise<WorkflowTemplateSummary> => {
    const response = await api.get(
      `/workflows/by-slug/${encodeURIComponent(slug)}`,
    );
    return response.data;
  },

  /**
   * Org overlay write for a single step's instruction body (D8/D10). Keyed by
   * template TOKEN, same as `getTemplate`. Full-body replacement; the fence is
   * not writable here. Returns the projected detail record so the editor can
   * seat it without a follow-up GET.
   */
  putStepInstructions: async (
    token: string,
    stepKey: string,
    payload: UpdateWorkflowStepInstructionsPayload,
  ): Promise<WorkflowTemplate> => {
    const response = await api.put(
      `/workflows/${token}/steps/${encodeURIComponent(stepKey)}/instructions`,
      payload,
    );
    if (response.data == null || response.data === "") {
      throw new Error(
        "PUT /workflows/:token/steps/:stepKey/instructions returned an empty body",
      );
    }
    return response.data;
  },

  /**
   * Clears the org overlay for one step's instructions, reverting to the seed
   * text. Same TOKEN + stepKey addressing as `putStepInstructions`. The
   * controller returns the full projected template, not `204`, so callers can
   * seat the response directly; an empty axios body is treated as a contract
   * violation rather than silently handing `undefined` to the editor.
   */
  resetStepInstructions: async (
    token: string,
    stepKey: string,
  ): Promise<WorkflowTemplate> => {
    const response = await api.delete(
      `/workflows/${token}/steps/${encodeURIComponent(stepKey)}/instructions`,
    );
    if (response.data == null || response.data === "") {
      throw new Error(
        "DELETE /workflows/:token/steps/:stepKey/instructions returned an empty body",
      );
    }
    return response.data;
  },
};

/**
 * Per-organization saved defaults for a workflow template's run inputs
 * (`WorkflowTemplateSettingsController`, workflow-input-settings plan Step 5).
 * Keyed by template TOKEN, same convention as `workflowsApi.getTemplate`: the
 * controller resolves token to slug server-side before touching the table.
 */
export const workflowTemplateSettingsApi = {
  /**
   * `null` when this org has never saved settings for the template.
   *
   * `||` rather than `??`, deliberately. The controller returns `null`, which
   * Nest sends as an EMPTY BODY, and axios hands an empty body back as `''`
   * rather than as null. `?? null` would therefore return that empty string,
   * and the signature would be quietly lying to every caller.
   */
  get: async (token: string): Promise<WorkflowTemplateSettings | null> => {
    const response = await api.get(`/workflows/${token}/settings`);
    return response.data || null;
  },

  save: async (
    token: string,
    inputs: WorkflowInputValues,
  ): Promise<WorkflowTemplateSettings> => {
    const response = await api.put(`/workflows/${token}/settings`, {
      inputs,
    });
    return response.data;
  },
};

/**
 * The org's Slack webhook, as PRESENCE only
 * (`WorkflowDeliverySettingsController`). `maskedUrl` is never the real URL and
 * the endpoint refuses to read one back unmasked, so this is safe to call from
 * any surface that only needs to know whether a Slack delivery could land.
 *
 * Behind `JwtAuthGuard` and the `ASSISTANT_PLATFORM` flag, readable by any org
 * member (only the write is admin-gated).
 */
export interface OrganizationSlackWebhookSettings {
  configured: boolean;
  /** Never the real URL. `null` when not configured. */
  maskedUrl: string | null;
}

export const workflowDeliverySettingsApi = {
  getSlackWebhook: async (): Promise<OrganizationSlackWebhookSettings> => {
    const response = await api.get("/workflow-delivery/slack-webhook");
    return response.data;
  },

  /**
   * `PATCH /workflow-delivery/slack-webhook`. A string sets the org webhook,
   * `null` clears it. Owner or admin only; the server validates the URL
   * (https, host exactly hooks.slack.com) and answers 400 with the reason.
   */
  updateSlackWebhook: async (
    webhookUrl: string | null,
  ): Promise<OrganizationSlackWebhookSettings> => {
    const response = await api.patch("/workflow-delivery/slack-webhook", {
      webhookUrl,
    });
    return response.data;
  },
};

/** `GET /slack-twins/install/status`: Slack Twins on the org settings page. */
export interface SlackTwinsInstallStatus {
  /** The Slack app is configured on this server. Hide the card when false. */
  available: boolean;
  /** The caller's organization has a Slack workspace connected. */
  connected: boolean;
  /** Owner or admin: may add Twins to Slack. */
  canManage: boolean;
}

/** `GET /slack-twins/links/preview`: who and where a connect link is for. */
export interface SlackTwinsLinkPreview {
  workspaceName: string | null;
  slackUserName: string | null;
}

/** A Slack channel Twins and the user are both in (Slack's own id only). */
export interface SlackTwinsChannel {
  channelId: string;
  name: string;
  isPrivate: boolean;
}

/** `GET /slack-twins/channels`: the channels to start in, or where to connect first. */
export type SlackTwinsChannelsResponse =
  | { linked: false; connectUrl: string }
  | { linked: true; channels: Array<SlackTwinsChannel> };

/** Body of `POST /slack-twins/rooms` (`StartSlackRoomDto`). */
export interface StartSlackRoomPayload {
  populationToken: string;
  participantPersonaIds: Array<string>;
  name?: string;
  target: { kind: "dm" } | { kind: "channel"; channelId: string };
  /** One per Start intent; a retry with the same id gives the same room. */
  clientRequestId: string;
}

/** `starting`: an earlier Start with this id is still creating the room. */
export type StartSlackRoomResponse =
  | { status: "started"; roomToken: string; permalink: string | null }
  | { status: "starting"; roomToken: null; permalink: null };

export const slackTwinsApi = {
  getInstallStatus: async (): Promise<SlackTwinsInstallStatus> => {
    const response = await api.get("/slack-twins/install/status");
    return response.data;
  },

  /**
   * `POST /slack-twins/install/start`. Owner or admin only. Returns Slack's
   * authorize URL carrying a signed one-time state; the caller navigates to it.
   */
  startInstall: async (): Promise<{ url: string }> => {
    const response = await api.post("/slack-twins/install/start");
    return response.data;
  },

  /**
   * `POST /slack-twins/links`: links the Slack user the token was minted for
   * to the logged-in account. 410 when the link expired or the workspace is
   * gone, 403 when the account is in another organization, 409 when the Slack
   * account is already connected to another Fairgen account.
   */
  /** Read-only; does not use the link up. */
  previewLink: async (token: string): Promise<SlackTwinsLinkPreview> => {
    const response = await api.get("/slack-twins/links/preview", { params: { token } });
    return response.data;
  },

  /**
   * Connect. `sign_in`: the browser goes to `url` (Sign in with Slack); the
   * link is written only if the signed-in Slack user is the one the link was
   * made for, and the callback lands back on the connect page with
   * `?slackLink=<result>`. `connected`: already linked. 409
   * `account_linked_elsewhere` when this account has another Slack user.
   */
  confirmLink: async (
    token: string,
  ): Promise<{ status: "connected" } | { status: "sign_in"; url: string }> => {
    const response = await api.post("/slack-twins/links", { token });
    return response.data;
  },

  /** `DELETE /slack-twins/link`: removes the logged-in user's Slack links. */
  disconnectSlack: async (): Promise<{ disconnected: boolean }> => {
    const response = await api.delete("/slack-twins/link");
    return response.data;
  },

  /** `GET /slack-twins/channels`. 404 `slack_not_installed` when the org has no workspace. */
  listChannels: async (): Promise<SlackTwinsChannelsResponse> => {
    const response = await api.get("/slack-twins/channels");
    return response.data;
  },

  /**
   * `POST /slack-twins/rooms`. 403 `slack_not_linked` or
   * `channel_unavailable` (Twins or the user left the channel).
   */
  startRoom: async (payload: StartSlackRoomPayload): Promise<StartSlackRoomResponse> => {
    const response = await api.post("/slack-twins/rooms", payload);
    return response.data;
  },
};

/** Body of `POST /workflow-runs` (`CreateWorkflowRunDto`). */
export interface CreateWorkflowRunPayload {
  /** `workflow_templates.token`. Row ids never cross the wire. */
  templateToken: string;
  /** Thread the run reports into. Absent for a run started outside one. */
  threadToken?: string;
  /** Values keyed by `WorkflowInputSpec.key`. */
  inputs?: Record<string, string | null>;
  autoApprove?: boolean;
  trigger?: WorkflowRunTrigger;
  /**
   * Caller-supplied dedupe key. A repeat POST with the same key returns the run
   * that already exists rather than starting a second one.
   */
  idempotencyKey?: string;
  /**
   * Structured twin-test segment. Sibling of `inputs`, never a `rest` string.
   * Absent = today's full-audience draw.
   */
  populationFilter?: PopulationFilter;
  segmentLabel?: string;
  /**
   * Today's note (Step 9). This run, this person. Typed at Start run and
   * never saved as a setting. Appended to `conversationSnapshot` as a user
   * turn. Absent means no note. Scheduled runs carry none.
   */
  note?: string;
  /**
   * Typed sentence that started this run. Persisted as the user turn and
   * appended to the briefing snapshot. Omitted when empty/whitespace.
   * Intake-settled starts do not send this. Max 4000 on the server.
   */
  askTurn?: string;
  /**
   * Answer language for a thread the server mints when `threadToken` is
   * absent. Ignored when a thread is already in scope, since that thread
   * carries its own. Absent leaves the minted thread unpicked, which is the
   * honest answer for a scheduled fire.
   */
  language?: string;
}

/** Body of `POST /workflow-runs/:token/gate` (`SubmitWorkflowGateDto`). */
export interface SubmitWorkflowGatePayload {
  decision: WorkflowGateDecision;
  /**
   * Gate edits keyed by the PRODUCING STEP KEY, each value a full replacement
   * `WorkflowStepOutput` for that step. Server rules that bite: keys must be a
   * subset of the gate's `artifacts` AND only when `config.editable === true`
   * (so omit the key entirely for a non-editable gate, where `editableKeys` is
   * `[]` and any key is a 400); each value's `kind` must equal the target
   * step's already-stored output kind; artifacts must carry `{slug, title,
   * body}` all strings, and an artifact `index` of `null` fails validation
   * where `undefined` passes — build each override by SPREADING the stored
   * artifact objects rather than reconstructing them.
   */
  overrides?: Record<string, WorkflowStepOutput>;
  note?: string;
}

/** Body of `PATCH /workflow-runs/:token/segment` (`UpdateWorkflowRunSegmentDto`). */
export interface UpdateWorkflowRunSegmentPayload {
  populationFilter: PopulationFilter | null;
  segmentLabel: string | null;
}

/** Body of `POST /workflow-runs/:token/revise` (`ReviseWorkflowStepDto`). */
export interface ReviseWorkflowStepPayload {
  /**
   * The reader's own words, verbatim, forwarded into the step's REVISION
   * prompt block. Max 4000 on the server.
   */
  instruction: string;
  /**
   * The document as the reader has it RIGHT NOW: one full replacement
   * `WorkflowStepOutput` for the step under review (the step in front of the
   * open gate), never a map keyed by step.
   *
   * Same validation as a gate override, and the same trap: SPREAD the stored
   * artifact objects rather than rebuilding them, since an artifact `index` of
   * `null` fails validation where an absent key passes. Omit the key entirely
   * when nothing was edited, so the rewrite starts from the stored output.
   */
  override?: WorkflowStepOutput;
}

/** Body of `POST /workflow-runs/:token/retry` (`RetryWorkflowStepDto`). */
export interface RetryWorkflowStepPayload {
  /**
   * Step to retry from, as its 0-based `stepIndex` — NEVER a `stepId`. A UI
   * holding a step uuid must map it through
   * `run.steps.find((s) => s.stepId === id)?.stepIndex` first. Absent means the
   * run's current step, which is the failed one in every normal case.
   */
  stepIndex?: number;
}

/**
 * Workflow run lifecycle (`WorkflowRunsController`), behind
 * `JwtAuthGuard + PaidFirstPaymentGuard` plus the `ASSISTANT_PLATFORM` flag —
 * so an org that can browse templates through `workflowsApi` may still be
 * refused a run.
 *
 * There is deliberately no `reject` method because there is no reject
 * endpoint: `cancel` on a run parked at a gate routes internally to
 * `submitGate({ decision: 'reject' })` server-side
 * (`workflow-run.service.ts`), and that is the whole reject path.
 *
 * Every mutating method answers with the full projected `WorkflowRun`, so a
 * caller can seed its cache from the response and never needs a follow-up
 * `get`.
 */
export const workflowRunsApi = {
  create: async (dto: CreateWorkflowRunPayload): Promise<WorkflowRun> => {
    const response = await api.post("/workflow-runs", dto);
    return response.data;
  },

  /**
   * `GET /workflow-runs?threadToken=…`: the lookup the run pane calls on load
   * and after a reload. A thread with no run is NORMAL and answers `null`,
   * never a 404.
   *
   * That `null` reaches the browser as an EMPTY BODY, not as the four bytes
   * `null`: Nest's express adapter short-circuits a nil handler result to
   * `response.send()` with no payload, so axios parses it as `""`. Normalizing
   * here is what makes "no run yet" a value the caller can branch on instead
   * of an empty string that is truthy-adjacent and typed as a run.
   */
  getForThread: async (threadToken: string): Promise<WorkflowRun | null> => {
    const response = await api.get("/workflow-runs", {
      params: { threadToken },
    });
    const data = response.data;
    if (!data || typeof data !== "object") {
      return null;
    }
    return data as WorkflowRun;
  },

  get: async (token: string): Promise<WorkflowRun> => {
    const response = await api.get(`/workflow-runs/${token}`);
    return response.data;
  },

  submitGate: async (
    token: string,
    dto: SubmitWorkflowGatePayload,
  ): Promise<WorkflowRun> => {
    const response = await api.post(`/workflow-runs/${token}/gate`, dto);
    return response.data;
  },

  retry: async (
    token: string,
    dto?: RetryWorkflowStepPayload,
  ): Promise<WorkflowRun> => {
    const response = await api.post(`/workflow-runs/${token}/retry`, dto ?? {});
    return response.data;
  },

  /**
   * `POST /workflow-runs/:token/revise`: write the document under review
   * again, with one instruction from the reader.
   *
   * There is no `stepIndex`: the server pins the target to the step in front
   * of the open gate, so this cannot name a different one. A run that is not
   * parked at a revisable gate answers 409, and so does one whose revision is
   * already in flight.
   */
  revise: async (
    token: string,
    dto: ReviseWorkflowStepPayload,
  ): Promise<WorkflowRun> => {
    const response = await api.post(`/workflow-runs/${token}/revise`, dto);
    return response.data;
  },

  cancel: async (token: string): Promise<WorkflowRun> => {
    const response = await api.post(`/workflow-runs/${token}/cancel`);
    return response.data;
  },

  /**
   * `PATCH /workflow-runs/:token/segment`: replace who a quick-study run asks
   * while its gate is open. The body is the whole segment; a null filter
   * clears it. 409 once the run left `awaiting_approval`.
   */
  updateSegment: async (
    token: string,
    dto: UpdateWorkflowRunSegmentPayload,
  ): Promise<WorkflowRun> => {
    const response = await api.patch(`/workflow-runs/${token}/segment`, dto);
    return response.data;
  },
};

// ============================================================================
// Artifact tips (THE VAULT — post-run study MD edits, owner-only)
// ============================================================================

/**
 * Wire shape for a tip. No DB row ids — `publicId` is the table `token`.
 * Mirrors Nest `ArtifactTipView`.
 */
export interface ArtifactTipView {
  publicId: string;
  runToken: string;
  stepId: string;
  artifactIdentity: string;
  sourceBody: string;
  editedBody: string;
  version: number;
  /** Monotonic revision within identity; latest = max(revision). */
  revision: number;
  updatedAt: string;
  derivedFrom: string | null;
  opsApplied: unknown | null;
  original: boolean;
}

/** Body of `PUT /workflow-runs/:runToken/artifact-tips`. */
export interface UpsertArtifactTipPayload {
  stepId: string;
  artifactIdentity: string;
  editedBody: string;
  /** Frozen on insert; omit on later upserts. Defaults to `editedBody` server-side. */
  sourceBody?: string;
  stepIndex?: number;
  stepKey?: string;
  /** Required when a tip already exists for this identity. */
  version?: number;
}

/** Body of `PATCH /artifact-tips/:publicId` (autosave + version guard). */
export interface PatchArtifactTipPayload {
  editedBody: string;
  version: number;
  updatedAt?: string;
}

/**
 * Tip store HTTP surface (THE VAULT). Owner-only; non-owners get 403 on the
 * run-scoped routes. PATCH answers 409 on stale `version`.
 */
export const artifactTipsApi = {
  list: async (runToken: string): Promise<Array<ArtifactTipView>> => {
    const response = await api.get(
      `/workflow-runs/${runToken}/artifact-tips`,
    );
    return response.data;
  },

  upsert: async (
    runToken: string,
    dto: UpsertArtifactTipPayload,
  ): Promise<ArtifactTipView> => {
    const response = await api.put(
      `/workflow-runs/${runToken}/artifact-tips`,
      dto,
    );
    return response.data;
  },

  patch: async (
    publicId: string,
    dto: PatchArtifactTipPayload,
  ): Promise<ArtifactTipView> => {
    const response = await api.patch(`/artifact-tips/${publicId}`, dto);
    return response.data;
  },

  /** THE LIVING — new findings revision from allowlisted ops. */
  extend: async (
    runToken: string,
    publicId: string,
    dto: {
      ops: Array<{
        opId: string;
        params?: Record<string, unknown>;
        questionId?: string;
      }>;
      findingsMd?: string;
      responses?: Array<Record<string, unknown>>;
      generation?: Record<string, unknown>;
    },
  ): Promise<ArtifactTipView> => {
    const response = await api.post(
      `/workflow-runs/${runToken}/artifact-tips/${publicId}/extend`,
      dto,
    );
    return response.data;
  },

  /** THE LIVING — new StudyDoc tip citing a findings revision. */
  rewrite: async (
    runToken: string,
    publicId: string,
    dto: {
      findingsPublicId: string;
      confirmReplaceEdited?: boolean;
      studyDoc?: string;
    },
  ): Promise<ArtifactTipView> => {
    const response = await api.post(
      `/workflow-runs/${runToken}/artifact-tips/${publicId}/rewrite`,
      dto,
    );
    return response.data;
  },
};

// ============================================================================
// Assistant turn stream (Assistant Platform v1 Phase 2, Step G — transport)
// ============================================================================

function normalizeAssistantPlanStepInputs(
  value: unknown,
): AssistantPlanStepInputs | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  const audience =
    typeof raw.audience === "string" ? raw.audience.trim() : "";
  if (!audience) {
    return undefined;
  }
  return { audience };
}

function normalizeAssistantPlanTail(
  value: unknown,
): AssistantTurnPlanTail | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const raw = value as {
    steps?: unknown;
    cardEligible?: unknown;
  };
  if (!Array.isArray(raw.steps) || raw.steps.length === 0) {
    return undefined;
  }
  const steps: Array<AssistantPlanStep> = [];
  for (const item of raw.steps) {
    if (!item || typeof item !== "object") {
      return undefined;
    }
    const step = item as Record<string, unknown>;
    const kind = typeof step.kind === "string" ? step.kind : "";
    const slug = typeof step.slug === "string" ? step.slug.trim() : "";
    const displayName =
      typeof step.displayName === "string" ? step.displayName.trim() : "";
    if (
      (kind !== "skill" && kind !== "workflow") ||
      !slug ||
      !displayName
    ) {
      return undefined;
    }
    const normalized: AssistantPlanStep = {
      kind,
      slug,
      displayName,
    };
    if (typeof step.why === "string" && step.why.trim()) {
      normalized.why = step.why.trim();
    }
    if (typeof step.title === "string" && step.title.trim()) {
      normalized.title = step.title.trim();
    }
    const inputs = normalizeAssistantPlanStepInputs(step.inputs);
    if (inputs) {
      normalized.inputs = inputs;
    }
    if (Array.isArray(step.flags)) {
      const flags = step.flags as Array<AssistantPlanStepFlag>;
      if (flags.length > 0) {
        normalized.flags = flags;
      }
    }
    steps.push(normalized);
  }
  if (steps.length === 0) {
    return undefined;
  }
  return {
    steps,
    cardEligible: Boolean(raw.cardEligible) && steps.length >= 2,
  };
}

/**
 * Admit a done-frame creative draft set. Requires an array of
 * `{ key, letter, title, body }`. `kind` defaults to `creative`.
 * Malformed entries drop the whole tail (same degrade as plan).
 */
function normalizeAssistantCreativeDrafts(
  value: unknown,
): Array<AssistantCreativeDraft> | undefined {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }
  const drafts: Array<AssistantCreativeDraft> = [];
  for (const item of value) {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      return undefined;
    }
    const raw = item as Record<string, unknown>;
    const key = typeof raw.key === "string" ? raw.key.trim() : "";
    const letter = typeof raw.letter === "string" ? raw.letter.trim() : "";
    if (
      !key ||
      !letter ||
      typeof raw.title !== "string" ||
      typeof raw.body !== "string"
    ) {
      return undefined;
    }
    const kind =
      typeof raw.kind === "string" && raw.kind.trim()
        ? raw.kind.trim()
        : "creative";
    const draft: AssistantCreativeDraft = {
      key,
      letter,
      title: raw.title,
      body: raw.body,
      kind,
    };
    if (typeof raw.updatedAt === "string" && raw.updatedAt) {
      draft.updatedAt = raw.updatedAt;
    }
    if (typeof raw.confirmedAt === "string") {
      draft.confirmedAt = raw.confirmedAt;
    } else if (raw.confirmedAt === null) {
      draft.confirmedAt = null;
    }
    if (typeof raw.publicId === "string") {
      draft.publicId = raw.publicId;
    } else if (raw.publicId === null) {
      draft.publicId = null;
    }
    drafts.push(draft);
  }
  return drafts;
}

function normalizeAssistantPreparedRun(
  value: unknown,
): AssistantPreparedRun | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  if (raw.status !== "prepared") {
    return undefined;
  }
  const seedRaw = raw.commandSeed;
  if (!seedRaw || typeof seedRaw !== "object" || Array.isArray(seedRaw)) {
    return undefined;
  }
  const seed = seedRaw as Record<string, unknown>;
  const slug = typeof seed.slug === "string" ? seed.slug.trim() : "";
  if (!slug) {
    return undefined;
  }
  const rest = typeof seed.rest === "string" ? seed.rest : "";
  const nonce =
    typeof seed.nonce === "number" && Number.isFinite(seed.nonce)
      ? seed.nonce
      : 0;
  const commandSeed: AssistantPreparedRun["commandSeed"] = {
    slug,
    rest,
    nonce,
    forceConfirm: true,
  };
  const audience = typeof seed.audience === "string" ? seed.audience.trim() : "";
  if (audience) {
    commandSeed.audience = audience;
  }
  return { status: "prepared", commandSeed };
}

function normalizeAssistantPreparedStudy(
  value: unknown,
): AssistantPreparedStudy | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  const draftToken = typeof raw.draftToken === "string" ? raw.draftToken.trim() : "";
  const title = typeof raw.title === "string" ? raw.title.trim() : "";
  if (!draftToken || !title) {
    return undefined;
  }
  if (raw.presentation === 'quick') {
    const origin = raw.threadOrigin as Record<string, unknown> | undefined;
    if (!origin || typeof origin.sourceThreadToken !== 'string' || !origin.sourceThreadToken.trim() ||
      typeof origin.originMessageToken !== 'string' || !origin.originMessageToken.trim() ||
      origin.completionDelivery !== 'origin_card' ||
      typeof raw.authoringQuestionCeiling !== 'number' || !Number.isInteger(raw.authoringQuestionCeiling) || raw.authoringQuestionCeiling < 1) return undefined;
    return { draftToken, title, presentation: 'quick', authoringQuestionCeiling: raw.authoringQuestionCeiling,
      threadOrigin: { sourceThreadToken: origin.sourceThreadToken, originMessageToken: origin.originMessageToken, completionDelivery: 'origin_card' } };
  }
  if (raw.presentation !== undefined && raw.presentation !== 'full') return undefined;
  return { draftToken, title };
}

// --- end Agent C ---

/**
 * The `done` frame as it actually reaches the browser.
 *
 * Normalized from the wire so a consumer never has to defend itself: `text`,
 * `citations`, `gaps`, `refused` and `truncated` are always present here even
 * though the shared `AssistantDoneEvent` declares them optional, because the
 * generator below fills each default before yielding.
 *
 * `contextUsage` is the one field that stays nullable. Python always sends it
 * (`AssistantDoneFrame.contextUsage`, generated), but Nest's relay type reads
 * every key as optional (`AssistantTurnDoneEvent`) and the forwarder writes it
 * through by reference, so an absent value is a real possibility on the wire
 * and `null` is the honest way to say so. The field names are `truncated` and
 * `contextUsage` verbatim — `contextTruncated` does not exist anywhere — and
 * `truncated` means HISTORY truncation only.
 */
export interface AssistantTurnDoneFrame extends AssistantDoneEvent {
  askId: string;
  text: string;
  citations: Array<AssistantCitation>;
  refused: boolean;
  gaps: Array<string>;
  /** A12: Nest stamps the saved artifact's `publicId` on the report tail. */
  report?: AssistantDoneReport & { publicId?: string };
}

/** Durable worker failures are owned by Nest's snapshot transport, independently
 * of the provider SSE error-code vocabulary. Preserve the server reason so the
 * reader and diagnostics can distinguish a lost job from a provider failure. */
export interface AssistantDurableTurnErrorFrame {
  type: "error";
  code: NonNullable<AssistantTurnSnapshot["error"]>["code"];
  message: string;
}

/**
 * Every frame `assistantTurnStream` can yield, from the generated vocabulary.
 * V2 evidence and outcome frames pass through here; rendering owns their use.
 */
export type AssistantTurnStreamFrame =
  | Exclude<AssistantStreamEvent, AssistantDoneEvent>
  | AssistantTurnDoneFrame
  | AssistantImageProgressEvent
  | AssistantDurableTurnErrorFrame;

/**
 * Compile-time pins (capability contracts D9). Each fails `tsc` when its two
 * sides drift: the frames this stream yields are exactly python's generated
 * stream vocabulary, and the `done` tails Nest relays without reshaping
 * (`report`, `contextUsage`) still accept what python's `AssistantDoneFrame`
 * declares, and `switch` is the same union on both sides. Tails Nest narrows
 * (`plan`, `intake`, `tool_calls`, ...)
 * are owned by its `project*` functions, not pinned here.
 */
type IsSameUnion<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
const assistantStreamFrameTypesPin: IsSameUnion<AssistantTurnStreamFrame["type"], AssistantStreamEventType> = true;
const assistantDoneReportPin: [AssistantDoneReport] extends [AssistantReportTail] ? true : false = true;
const assistantDoneContextUsagePin: [AssistantDoneContextUsage] extends [AssistantContextUsage] ? true : false = true;
void assistantStreamFrameTypesPin;
void assistantDoneReportPin;
void assistantDoneContextUsagePin;
// `switch` carries the results outcome OR the report hand-off on one key: the
// browser type must accept both sides python declares, and nothing else.
const assistantDoneSwitchPin: IsSameUnion<
  NonNullable<AssistantDoneEvent["switch"]>,
  NonNullable<AssistantDoneFrame["switch"]>
> = true;
void assistantDoneSwitchPin;

const IMAGE_PROGRESS_STAGES: ReadonlyArray<AssistantImageProgressEvent["stage"]> = [
  "queued", "rendering", "partial", "done", "failed", "stopped",
];
const IMAGE_PROGRESS_SIZES: ReadonlyArray<NonNullable<AssistantImageProgressEvent["size"]>> =
  CREATIVE_FORMATS.map((format) => format.renderSize);

/**
 * An `image_progress` side-channel frame (image plan Step 4b), or null when it
 * is malformed: no `callId`, an unknown `stage`, or `done` without an asset
 * token. Optional fields of the wrong type are dropped, never trusted.
 */
export function normalizeImageProgressFrame(raw: object): AssistantImageProgressEvent | null {
  const record = raw as Record<string, unknown>;
  const callId = typeof record.callId === "string" ? record.callId.trim() : "";
  const stage = IMAGE_PROGRESS_STAGES.find((value) => value === record.stage);
  if (!callId || !stage) {
    return null;
  }
  const size = IMAGE_PROGRESS_SIZES.find((value) => value === record.size);
  const asset = record.asset as ThreadAssetView | null | undefined;
  const hasAsset = Boolean(
    asset && typeof asset === "object" && typeof asset.token === "string" && asset.token &&
      asset.ref && typeof asset.ref === "object",
  );
  if (stage === "done" && !hasAsset) {
    return null;
  }
  const previewDataUrl =
    typeof record.previewDataUrl === "string" && record.previewDataUrl.startsWith("data:image/")
      ? record.previewDataUrl
      : undefined;
  const reason = typeof record.reason === "string" && record.reason ? record.reason : undefined;
  return {
    type: "image_progress",
    callId,
    stage,
    ...(size ? { size } : {}),
    ...(previewDataUrl ? { previewDataUrl } : {}),
    ...(hasAsset && asset ? { asset } : {}),
    ...(reason ? { reason } : {}),
  };
}

/** Transport defaults are normalized here; vocabularies belong to the server. */
type AssistantWireFrame = {
  [Kind in AssistantStreamEvent["type"]]: { type: Kind } &
    Partial<Extract<AssistantStreamEvent, { type: Kind }>> &
    (Kind extends "done" ? { creative_drafts?: unknown } : object)
}[AssistantStreamEvent["type"]];

/**
 * The provider call id a `tool_start` / `tool_end` carries, when the producer
 * sends one (docs/plans/thread-activity-steps.md, D3). Passed through so the
 * thread can pair each end with its own start; a missing or non-string value
 * is dropped and the thread falls back to pairing by tool name.
 */
function readToolCallId(event: object): string | undefined {
  const callId = "callId" in event ? event.callId : undefined;
  return typeof callId === "string" && callId.trim() ? callId : undefined;
}

function assertNeverAssistantFrame(frame: never): never {
  throw new Error(`Unhandled assistant frame: ${JSON.stringify(frame)}`);
}

/**
 * One server-owned ASSISTANT turn: accept once at `/assistant/turns`, then
 * observe that execution through versioned readback. The existing frame
 * vocabulary remains the UI contract; aborting this generator only detaches
 * its observer. Explicit Stop uses `assistantTurnsApi.cancel`.
 *
 * All modes share this acceptance and execution lifecycle, behind
 * `ASSISTANT_PLATFORM`.
 *
 * Base skills are resolved server-side. A plan skill step also sends
 * `stepSkillSlug` so Nest can append that step's skill last.
 *
 * `mode: 'intake'` turns the same endpoint into the twin-test intake
 * conversation: a fast route with no retrieval legs, whose terminal frame
 * carries the `intake` tail above. An empty `message` is legal ONLY under that
 * mode (the reader picks `/test` having typed nothing, and intake opens the
 * conversation itself). The backend FORCES the retrieval and effort fields for
 * an intake turn and never trusts the client for them, so `effort` passed
 * alongside `mode` is simply ignored rather than being an error to defend
 * against here.
 *
 * `mode: 'report'` is the second fork: the same turn, writing the thread up
 * (`docs/plans/2026-09-03-assistant-report-mode.md`, D1). It streams tool
 * frames but no `delta`s — the draft is buffered so the grounding gate sees
 * the whole of it — and its `done` frame carries `report` when one was
 * written. Typed admission errors preserve their code and response details
 * for the caller; provider failure prose is never shown as an admission error.
 */
export async function* assistantTurnStream(
  token: string,
  message: string,
  options?: {
    signal?: AbortSignal;
    clientTurnId?: string;
    imageApprovalMessageToken?: string;
    imageApprovalRequestId?: string;
    imageQuality?: 'medium' | 'high';
    /** A format id only: the server owns size and price. */
    imageFormat?: CreativeFormatId;
    /** The card's whole selection, stored on the turn. */
    imageFormats?: Array<CreativeFormatId>;
    /** Which image of the card this approval starts. The server refuses the rest without it. */
    imageApprovalStep?: 'first' | 'try' | 'rest';
    imageCreativeSelection?: CreativeContextSelection;
    /** On a try, its number. On the rest, the tries the card showed. */
    imageTry?: number;
    creativeContextRevision?: number;
    onAccepted?: (snapshot: AssistantTurnSnapshot) => void;
    onSnapshot?: (snapshot: AssistantTurnSnapshot) => void;
    planCursor?: AssistantPlanCursorBinding;
    scope?: Partial<KnowledgeThreadScope> | null;
    effort?: AssistantEffort;
    /**
     * Assistant profile id (plan 2026-10-21, D1): `low`, `medium`, `high`, or
     * a staff profile for verified Fairgen staff. The profile decides the
     * effort, so a caller sends one or the other. Absent keeps today's request.
     */
    profile?: string;
    /**
     * Absent = an ordinary turn, byte-compatible with the request sent today.
     * `'report'` is the forked writing mode: no retrieval legs, no `delta`
     * frames, and a `done` frame that may carry `report`.
     */
    mode?: AssistantRequestMode;
    intake?: AssistantIntakeRequest;
    quickStudy?: QuickStudyCommand;
    /** Thread-asset registry tokens (POST /assets), not files.token. */
    attachmentTokens?: Array<string>;
    /** Current plan step skill slug (kebab). Nest resolves the body. */
    stepSkillSlug?: string;
    /** Replay after intake handoff: user row already stored. */
    skipUserMessage?: boolean;
  },
): AsyncGenerator<AssistantTurnStreamFrame> {
  const { signal, onAccepted, onSnapshot, clientTurnId = crypto.randomUUID(), ...request } = options ?? {};
  const snapshot = await assistantTurnsApi.accept(token, { ...request, message, clientTurnId }, signal);
  onAccepted?.(snapshot);
  yield* observeAssistantTurn(token, snapshot, signal, onSnapshot);
}

/** Normalize the same vocabulary for live observation and restored snapshots. */
export function* normalizeAssistantTurnFrame(raw: unknown): Generator<AssistantTurnStreamFrame> {
  if (!raw || typeof raw !== "object" || !("type" in raw) ||
      !ASSISTANT_STREAM_EVENT_TYPES.some((type) => type === raw.type)) {
    return;
  }
  if (raw.type === "image_progress") {
    const imageFrame = normalizeImageProgressFrame(raw);
    if (imageFrame) {
      yield imageFrame;
    }
    return;
  }
  const event = raw as AssistantWireFrame;
  switch (event.type) {
  case "delta": {
    if (typeof event.text === "string") {
      yield { type: "delta", text: event.text };
    }
    break;
  }
  case "tool_start": {
    if (!event.tool) break;
    const query =
      typeof event.query === "string" ? event.query.trim() : "";
    const callId = readToolCallId(event);
    yield {
      type: "tool_start",
      ...projectAssistantActivity(event),
      tool: event.tool,
      label: event.label || "",
      ...(query ? { query } : {}),
      ...(callId ? { callId } : {}),
    };
    break;
  }
  case "tool_end": {
    if (!event.tool) break;
    const callId = readToolCallId(event);
    yield {
      type: "tool_end",
      ...projectAssistantActivity(event),
      tool: event.tool,
      ok: Boolean(event.ok),
      itemCount: event.itemCount ?? 0,
      ...(callId ? { callId } : {}),
      ...(event.errorCode ? { errorCode: event.errorCode } : {}),
    };
    break;
  }
  case "citations": {
    yield { type: "citations", items: event.items || [] };
    break;
  }
  case "citations_v2": {
    yield { type: "citations_v2", evidenceVersion: 2, items: event.items ?? [] };
    break;
  }
  case "library_outcome": {
    if (event.outcome && event.callId) {
      yield { type: "library_outcome", callId: event.callId, outcome: event.outcome, gaps: event.gaps ?? [] };
    }
    break;
  }
  case "web_outcome": {
    if (event.outcome && event.callId) {
      yield { type: "web_outcome", callId: event.callId, outcome: event.outcome, gaps: event.gaps ?? [] };
    }
    break;
  }
  case "judgment": {
    yield { type: "judgment", note: event.note || "" };
    break;
  }
  case "done": {
    const intake = event.intake;
    const plan = normalizeAssistantPlanTail(event.plan);
    const preparedRun = normalizeAssistantPreparedRun(event.preparedRun);
    const preparedStudy = normalizeAssistantPreparedStudy(event.preparedStudy);
    const creativeDrafts = normalizeAssistantCreativeDrafts(
      event.creativeDrafts ?? event.creative_drafts,
    );
    // --- end Agent C ---
    yield {
      type: "done",
      askId: event.askId || "",
      text: event.text || "",
      citations: event.citations || [],
      refused: Boolean(event.refused),
      gaps: event.gaps || [],
      truncated: Boolean(event.truncated),
      contextUsage: event.contextUsage ?? null,
      costEventId: event.costEventId,
      ...(event.userMessageToken
        ? { userMessageToken: event.userMessageToken }
        : {}),
      ...(event.assistantMessageToken
        ? { assistantMessageToken: event.assistantMessageToken }
        : {}),
      ...(intake ? { intake } : {}),
      ...(plan ? { plan } : {}),
      ...(preparedRun ? { preparedRun } : {}),
      ...(preparedStudy ? { preparedStudy } : {}),
      ...(event.preparedQuickStudy ? {preparedQuickStudy: event.preparedQuickStudy} : {}),
      ...(event.socialTrackerProposal
        ? { socialTrackerProposal: event.socialTrackerProposal }
        : {}),
      ...(event.preparedConversation
        ? { preparedConversation: event.preparedConversation }
        : {}),
      ...(creativeDrafts ? { creativeDrafts } : {}),
      ...(event.report && typeof event.report.markdown === "string"
        ? { report: event.report }
        : {}),
      ...(event.switch ? { switch: event.switch } : {}),
      ...(event.living ? { living: event.living } : {}),
      ...(event.charts ? { charts: event.charts } : {}),
      ...(event.tables ? { tables: event.tables } : {}),
      ...(event.tool_calls ? { tool_calls: event.tool_calls } : {}),
      ...(event.evidenceVersion === 2 ? { evidenceVersion: 2 as const } : {}),
      ...(event.citations_resolved ? { citations_resolved: event.citations_resolved } : {}),
      ...(event.answer_spans ? { answer_spans: event.answer_spans } : {}),
      ...(event.calculations ? { calculations: event.calculations } : {}),
      ...(event.imageRequests?.length ? { imageRequests: event.imageRequests } : {}),
      ...(event.further_reading ? { further_reading: event.further_reading } : {}),
      ...(event.usage_by_route ? { usage_by_route: event.usage_by_route } : {}),
    };
    break;
  }
  case "error": {
    yield {
      type: "error",
      code: event.code || "internal",
      message: event.message || "Assistant turn failed",
    };
    break;
  }
  case "suggestions": {
    yield { type: "suggestions", suggestions: event.suggestions ?? [] };
    break;
  }
  default:
    assertNeverAssistantFrame(event);
  }
}

async function assistantTurnRequest<T>(path: string, init?: RequestInit, nullableEmpty = false): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}/knowledge/threads/${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let data: { code?: string; message?: string } = {};
    try { data = JSON.parse(text); } catch { /* Preserve non-JSON upstream errors. */ }
    throw Object.assign(new Error(data.message || text || `Assistant request failed (${response.status})`), {
      code: data.code,
      response: { status: response.status, data: response.status < 500 ? data : { ...data, message: undefined } },
    });
  }
  if (nullableEmpty) {
    // Nest sends a successful `null` controller return as an empty body.
    // Only nullable discovery routes accept that; acceptance/readback still
    // require a real execution snapshot.
    const text = await response.text();
    return (text.trim() ? JSON.parse(text) : null) as T;
  }
  return response.json() as Promise<T>;
}

export const assistantTurnsApi = {
  accept: (threadToken: string, body: object, signal?: AbortSignal): Promise<AssistantTurnSnapshot> =>
    assistantTurnRequest(`${encodeURIComponent(threadToken)}/assistant/turns`, {
      method: "POST", body: JSON.stringify(body), signal,
    }),
  latest: (threadToken: string, signal?: AbortSignal): Promise<AssistantTurnSnapshot | null> =>
    assistantTurnRequest(`${encodeURIComponent(threadToken)}/assistant/turns/latest`, { signal }, true),
  active: (threadToken: string, signal?: AbortSignal): Promise<AssistantTurnSnapshot | null> =>
    assistantTurnRequest(`${encodeURIComponent(threadToken)}/assistant/turns/active`, { signal }, true),
  get: (threadToken: string, turnToken: string, signal?: AbortSignal): Promise<AssistantTurnSnapshot> =>
    assistantTurnRequest(`${encodeURIComponent(threadToken)}/assistant/turns/${encodeURIComponent(turnToken)}`, { signal }),
  cancel: (threadToken: string, turnToken: string): Promise<AssistantTurnSnapshot> =>
    assistantTurnRequest(`${encodeURIComponent(threadToken)}/assistant/turns/${encodeURIComponent(turnToken)}/cancel`, { method: "POST" }),
  /** Each metered reply's cost and the conversation's running total (millicredits). */
  credits: (threadToken: string, signal?: AbortSignal): Promise<AssistantThreadCredits> =>
    assistantTurnRequest(`${encodeURIComponent(threadToken)}/assistant/turns/credits`, { signal }),
};

export function isAssistantTurnActive(snapshot: AssistantTurnSnapshot): boolean {
  return snapshot.status === "queued" || snapshot.status === "running";
}

function waitForAssistantPoll(signal?: AbortSignal, delayMs = 750): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new DOMException("Observation detached", "AbortError")); return; }
    const finish = () => { signal?.removeEventListener("abort", abort); resolve(); };
    const timer = setTimeout(finish, delayMs);
    const abort = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      reject(new DOMException("Observation detached", "AbortError"));
    };
    signal?.addEventListener("abort", abort, { once: true });
  });
}

/** A viewer can leave at any point. Detaching never sends a cancellation. */
export async function* observeAssistantTurnSnapshots(
  threadToken: string,
  initial: AssistantTurnSnapshot,
  signal?: AbortSignal,
): AsyncGenerator<AssistantTurnSnapshot> {
  let snapshot = initial;
  let revision = -1;
  let readFailures = 0;
  while (!signal?.aborted) {
    if (snapshot.revision !== revision) {
      revision = snapshot.revision;
      yield snapshot;
    }
    if (!isAssistantTurnActive(snapshot) && snapshot.continuation?.status !== "pending") return;
    await waitForAssistantPoll(signal, 750 * 2 ** readFailures);
    try {
      snapshot = await assistantTurnsApi.get(threadToken, initial.token, signal);
      readFailures = 0;
    } catch (error) {
      if (signal?.aborted) throw error;
      // Retry readback only. Never repeat the paid submission on a transport error.
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status && status < 500) throw error;
      // A lost connection is not a failed execution. Stay attached logically,
      // with bounded readback backoff, until the server reports a terminal state.
      readFailures = Math.min(readFailures + 1, 4);
    }
  }
  throw new DOMException("Observation detached", "AbortError");
}

/** Compact snapshots replace state; adapt them to the existing frame consumers. */
export async function* observeAssistantTurn(
  threadToken: string,
  initial: AssistantTurnSnapshot,
  signal?: AbortSignal,
  onSnapshot?: (snapshot: AssistantTurnSnapshot) => void,
): AsyncGenerator<AssistantTurnStreamFrame> {
  let answer = "";
  const seen = new Set<string>();
  let sawDone = false;
  let sawError = false;
  for await (const snapshot of observeAssistantTurnSnapshots(threadToken, initial, signal)) {
    onSnapshot?.(snapshot);
    for (const raw of snapshot.events) {
      for (const frame of normalizeAssistantTurnFrame(raw)) {
        if (frame.type === "delta") {
          if (!frame.text.startsWith(answer)) {
            throw new Error("Assistant progress changed unexpectedly; reopen the thread to reconnect.");
          }
          const added = frame.text.slice(answer.length);
          answer = frame.text;
          if (added) yield { type: "delta", text: added };
          continue;
        }
        // A done frame becomes observable only after all persistence/handoffs settle.
        if (frame.type === "done" && snapshot.status !== "completed") continue;
        const key = frame.type === "judgment" && typeof raw.eventId === "string"
          ? `${raw.eventId}:${JSON.stringify(frame)}`
          : JSON.stringify(frame);
        if (seen.has(key)) continue;
        seen.add(key);
        if (frame.type === "done") sawDone = true;
        if (frame.type === "error") sawError = true;
        yield frame;
      }
    }
    if (snapshot.status === "cancelled") throw new DOMException("Assistant turn stopped", "AbortError");
    if (snapshot.status === "failed" && !sawError) {
      yield { type: "error", code: snapshot.error?.code ?? "internal", message: snapshot.error?.message ?? "Assistant turn failed" };
    }
    if (snapshot.status === "completed" && !sawDone) {
      throw new Error("Completed assistant turn has no saved answer; reopen the thread.");
    }
  }
}

export interface ConnectedSourceConnector {
  slug: string
  name: string
  authenticated: boolean
  available: boolean
  description: string | null
  logoUrl: string | null
  sourceUrl: string | null
  categories: Array<string>
  capabilities: Array<{ name: string; description: string }>
}

export const connectedSourcesApi = {
  request: async (connector: string): Promise<{ requested: true }> => {
    const response = await api.post("/connected-sources/request", { connector })
    return response.data
  },
  list: async (): Promise<{ connectors: Array<ConnectedSourceConnector> }> => {
    const response = await api.get("/connected-sources")
    return response.data
  },
  link: async (connector: string): Promise<{ url: string }> => {
    const response = await api.post("/connected-sources/link", { connector })
    return response.data
  },
  disconnect: async (connector: string): Promise<{ disconnected: true }> => {
    const response = await api.post("/connected-sources/disconnect", { connector })
    return response.data
  },
}

export type AssistantLinkedRoom = Omit<ChatRoomListItemDto, "id">;

/** What the server's pick is relative to the request. `reason` says the same in the user's language. */
export type TwinPickOutcome = "answers" | "mentions" | "unmatched" | "filter" | "filter_dropped" | "empty";

export interface TwinPickResult {
  personaTokens: Array<string>;
  reason: string;
  outcome: TwinPickOutcome;
  matchedOn?: Array<{ question: string; answer: string; chip: string }>;
  appliedFilter?: PopulationFilter;
  notFound?: Array<string>;
}

export interface AssistantLinkedStudy {
  token: string;
  title: string;
  status: string;
  reportStatus: ReportStatus | null;
}

/** Linked assets are derived from durable server links, never panel storage. */
export const assistantSessionsApi = {
  runs: async (threadToken: string): Promise<Array<WorkflowRun>> => {
    const response = await api.get('/workflow-runs/history', { params: { threadToken } });
    return response.data;
  },
  studies: async (threadToken: string): Promise<Array<AssistantLinkedStudy>> => {
    const response = await api.get(`/experiments/by-thread/${encodeURIComponent(threadToken)}`);
    return response.data;
  },
  rooms: async (threadToken: string): Promise<Array<AssistantLinkedRoom>> => {
    const response = await api.get('/chat-rooms', { params: { sourceThreadToken: threadToken } });
    return response.data;
  },
  pickTwins: async (audienceToken: string, request: {
    populationFilter?: PopulationFilter | null;
    keywords?: Array<string> | null;
    count: number;
    language?: string | null;
  }): Promise<TwinPickResult> => {
    const response = await api.post(`/populations/${encodeURIComponent(audienceToken)}/twin-picks`, request, {
      headers: { "Accept-Language": resolveUiLanguage(i18n.language) },
    });
    return response.data;
  },
};
